/**
 * ElecBid — Crew Performance Dashboard (Accurate Metrics)
 * Browning Electric
 *
 * TWO core metrics done RIGHT:
 *   1. Same-Day Timesheet Rate — start_at date vs created_at date
 *   2. Same-Day Notes Rate — completed visit matched to note on same job_number by same tech on same day
 *
 * Data sources:
 *   - jb_jobs — completed jobs, revenue, profit, margin
 *   - jobber_timesheets — hours worked, submission timing
 *   - jobber_visits — visit completion, assigned techs
 *   - jobber_job_notes — note completion rate
 */

import { useState, useEffect, useCallback, useMemo } from "react";
import { supabase } from "../lib/supabase";
import { apiFetch } from "../lib/apiFetch";
import { computeBonusRows, NOT_LEAD_FIRST, buildPaperworkHoursIndex, owesPaperwork } from "../lib/profitBonus";
import BonusTracker from "./BonusTracker";
import MasterCrewReport from "./MasterCrewReport";
import ReferralLeaderboard from "./ReferralLeaderboard";
import { useSession } from "../App";
import { usePermissions } from "../hooks/usePermissions";

// ─── Brand colours ───────────────────────────────────────────────────────────
const C = {
  bg: "#0d0f12", surface: "#161a20", card: "#1c2028", border: "#2a3140",
  accent: "#a5ce3a", accentDim: "rgba(165,206,58,0.12)",
  green: "#22c55e", greenDim: "rgba(34,197,94,0.10)",
  red: "#ef4444", redDim: "rgba(239,68,68,0.10)",
  yellow: "#eab308", yellowDim: "rgba(234,179,8,0.10)",
  blue: "#3b82f6", blueDim: "rgba(59,130,246,0.10)",
  purple: "#a78bfa", purpleDim: "rgba(167,139,250,0.10)",
  orange: "#f97316", orangeDim: "rgba(249,115,22,0.10)",
  cyan: "#06b6d4", text: "#f0f4f8", muted: "#8892a4", dim: "#4a5568",
};

// ─── Excluded techs (part-time / no longer with company) ────────────────────
const EXCLUDED_TECHS = ["Stacy", "Liam", "Ayden"];

// ─── Non-crew staff: NEVER auto-credited for reviews ────────────────────────
// Office / marketing people whose first names appear in review comments for
// non-field reasons: Taylor Priegnitz is admin/dispatch, and "Taylor &
// Mallory" (taylor@nabocreative.com) is the social-media agency's shared
// Jobber login — not a person on the crew. Reviews thanking "Taylor" are
// about scheduling/CSR work, so the name-mention scan and review-star
// auto-attribution skip these names entirely. Manual assignment in the UI
// still works. Mirrors api/_knownTechs.js NON_CREW_FIRST (CRA can't import
// from api/) and BonusTracker.jsx.
const NON_CREW_FIRST = ["taylor", "mallory"];
function isNonCrew(name) {
  return NON_CREW_FIRST.includes(firstName(name));
}
// Techs who are responsible for closing visits and jobs (journeymen only)
const JOURNEYMEN_ONLY = ["kyle", "tommy", "justin", "colton"];

// ─── Profit-bonus thresholds (defaults — overridden by app_settings) ────────
// Two pools per qualifying job:
//   excess     = profit_amount − revenue × (margin_threshold / 100)
//   lead_pool  = excess × lead_pct          → goes to the lead tech
//   crew_pool  = excess × crew_pct          → split among everyone else who
//                                            logged hours + the admin (Taylor)
const PROFIT_BONUS_START   = new Date(2026, 0, 1);   // floor — no jobs older than this
const DEFAULT_LEAD_PCT     = 10;
const DEFAULT_CREW_PCT     = 5;
const DEFAULT_REV_MIN      = 5500;
const DEFAULT_MARGIN_MIN   = 50;

// ─── Review-bonus defaults (overridable via app_settings) ───────────────────
// Per 5★ review: every tech who worked the job earns the base. Every tech
// the customer named in the comment earns the mention bonus on top. Monthly
// cap prevents one tech from dominating if they happened to be on every
// job that month. Default values calibrated for ~7 reviews/month volume.
const DEFAULT_REVIEW_BONUS_BASE        = 25;
const DEFAULT_REVIEW_BONUS_MENTION     = 25;
const DEFAULT_REVIEW_BONUS_MONTHLY_CAP = 300;

// Hardcoded carve-outs: never receive bonuses regardless of role
const BONUS_EXCLUDED_FIRST = ["dylan", "colton", "cindy"];
// Roles eligible to be a job's lead — used both for explicit lead assignment
// (jb_jobs.lead_tech custom field) and for the top-hours auto-fallback when
// lead_tech is empty. Journeymen are included on both sides: they're the
// people who actually run jobs solo, so when no lead is named the journeyman
// with the most hours wins. (Previously the fallback was Ops-Lead-only, which
// silently dropped journeymen-led jobs off the profitability leaderboard.)
const LEAD_ROLES = ["journeyman", "ops lead tech", "ops lead"];

function isBonusExcluded(first) {
  return BONUS_EXCLUDED_FIRST.includes((first || "").toLowerCase());
}
function calcExcess(rev, profitAmt, marginMin) {
  const r  = Number(rev) || 0;
  const pa = Number(profitAmt) || 0;
  return pa - r * (Number(marginMin) / 100 || 0.5);
}
function jobQualifies(rev, profitPct, revMin, marginMin) {
  return Number(rev) > Number(revMin) && Number(profitPct) > Number(marginMin);
}

// ─── Helpers ────────────────────────────────────────────────────────────────
const dollar = (n) => n != null ? `$${Number(n).toLocaleString("en-CA", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}` : "--";
const pct = (n) => n != null ? `${Number(n).toFixed(1)}%` : "--";
const hrs = (n) => n != null ? `${Number(n).toFixed(1)}h` : "--";

const MONTH_INDEX = {
  jan: 0, feb: 1, mar: 2, apr: 3, may: 4, jun: 5,
  jul: 6, aug: 7, sep: 8, sept: 8, oct: 9, nov: 10, dec: 11,
};

function parseDate(str) {
  if (str == null) return null;
  if (str instanceof Date) return isNaN(str.getTime()) ? null : str;
  const s = String(str).trim();
  if (!s || s === "-") return null;
  // Date-only strings (YYYY-MM-DD) must be treated as local midnight, not UTC midnight.
  // new Date("2025-04-21") returns UTC midnight, which is before local midnight in
  // North American timezones — causing "This Week"/"Last Week" filters to exclude
  // valid records from closed_date and created_at date columns.
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, mo, d] = s.split("-").map(Number);
    const dt = new Date(y, mo - 1, d);
    return isNaN(dt.getTime()) ? null : dt;
  }
  // Jobber's date columns (closed_date, created_date) are stored as "Mon DD, YYYY"
  // — e.g. "Apr 02, 2026". Parse explicitly so we don't depend on the engine's
  // Date(str) heuristics, which have historically returned Invalid Date in some
  // browsers/locales for this exact format.
  const m = s.match(/^([A-Za-z]{3,4})\s+(\d{1,2}),\s*(\d{4})$/);
  if (m) {
    const mo = MONTH_INDEX[m[1].toLowerCase()];
    if (mo != null) {
      const dt = new Date(Number(m[3]), mo, Number(m[2]));
      return isNaN(dt.getTime()) ? null : dt;
    }
  }
  const d = new Date(s);
  return isNaN(d.getTime()) ? null : d;
}

/** Compare two dates — same calendar day (local timezone) */
function sameCalendarDay(d1, d2) {
  if (!d1 || !d2) return false;
  return d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth() &&
    d1.getDate() === d2.getDate();
}

/** Normalise a name for comparison: lowercase, trim, collapse whitespace */
function normName(name) {
  if (!name) return "";
  return name.trim().toLowerCase().replace(/\s+/g, " ");
}

/** Get first name from a full name */
function firstName(name) {
  if (!name) return "";
  return name.trim().split(/\s+/)[0].toLowerCase();
}

/** Returns true if the tech's first name matches any excluded tech */
function isExcluded(name) {
  return EXCLUDED_TECHS.some(ex => firstName(name) === ex.toLowerCase());
}

function getDateRange(preset) {
  const now = new Date();
  const y = now.getFullYear(), m = now.getMonth(), dow = now.getDay();
  switch (preset) {
    case "this_week": {
      const mon = new Date(y, m, now.getDate() - ((dow + 6) % 7));
      mon.setHours(0, 0, 0, 0);
      return { from: mon, to: now };
    }
    case "last_week": {
      const mon = new Date(y, m, now.getDate() - ((dow + 6) % 7) - 7);
      mon.setHours(0, 0, 0, 0);
      const sun = new Date(mon); sun.setDate(sun.getDate() + 6); sun.setHours(23, 59, 59, 999);
      return { from: mon, to: sun };
    }
    case "this_month": return { from: new Date(y, m, 1), to: now };
    case "last_month": return { from: new Date(y, m - 1, 1), to: new Date(y, m, 0, 23, 59, 59) };
    case "this_quarter": return { from: new Date(y, Math.floor(m / 3) * 3, 1), to: now };
    case "this_year": return { from: new Date(y, 0, 1), to: now };
    case "this_fiscal": {
      const fy = m >= 5 ? y : y - 1;
      return { from: new Date(fy, 5, 1), to: now };
    }
    case "all": default: return { from: null, to: null };
  }
}

// ─── Colour for rate ────────────────────────────────────────────────────────
function rateColor(rate) {
  if (rate == null) return C.muted;
  if (rate >= 80) return C.green;
  if (rate >= 60) return C.yellow;
  return C.red;
}

// ─── UI Components ──────────────────────────────────────────────────────────
function KPICard({ label, value, sub, color, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: C.surface, border: `1px solid ${onClick ? C.accent + "60" : C.border}`,
        borderRadius: 12, padding: "16px 18px", flex: "1 1 150px", minWidth: 150, minHeight: 100, boxSizing: "border-box",
        cursor: onClick ? "pointer" : "default",
        transition: "border-color 0.15s, background 0.15s",
      }}
      onMouseEnter={onClick ? (e) => { e.currentTarget.style.background = C.accentDim; } : undefined}
      onMouseLeave={onClick ? (e) => { e.currentTarget.style.background = C.surface; } : undefined}
    >
      <div style={{ fontSize: 11, color: C.muted, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
        {label}{onClick && <span style={{ marginLeft: 6, color: C.accent, fontSize: 10 }}>↗</span>}
      </div>
      <div style={{ fontSize: 24, fontWeight: 700, color: color || C.text, lineHeight: 1.1 }}>
        {value ?? "--"}
      </div>
      {sub && <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function ScoreBar({ value, max = 100, color }) {
  const w = Math.min(100, Math.max(0, (value / max) * 100));
  return (
    <div style={{ height: 6, borderRadius: 3, background: `${C.border}40`, width: "100%" }}>
      <div style={{ height: 6, borderRadius: 3, background: color || C.accent, width: `${w}%`, transition: "width 0.3s" }} />
    </div>
  );
}

// ─── Leaderboard ─────────────────────────────────────────────────────────────
const MEDAL_COLORS = ["#FFD700", "#C0C0C0", "#CD7F32"];
const RANK_LABELS  = ["1st", "2nd", "3rd", "4th", "5th"];

function TechLeaderboard({ title, entries, countLabel, onEntryClick, rateColorFn, renderValue, renderSubline, emptyMessage }) {
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? entries : entries.slice(0, 5);
  const hasMore = entries.length > 5;
  const _rateColorFn = rateColorFn || ((e) => rateColor(e.rate));
  const _renderValue = renderValue || ((e) => pct(e.rate));
  const _renderSubline = renderSubline || ((e) => `${e.count} ${countLabel}`);
  return (
    <div style={{ marginBottom: 4 }}>
      <div style={{ fontSize: 12, fontWeight: 600, color: C.muted, textTransform: "uppercase", letterSpacing: "0.07em", marginBottom: 10 }}>
        {title}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        {visible.map((entry, i) => {
          const isTop3 = i < 3;
          const isBeyond5 = i >= 5;
          const medalColor = MEDAL_COLORS[i];
          const rowBg = isBeyond5 ? "transparent" : C.surface;
          const rowBgHover = isBeyond5 ? `${C.border}15` : C.card;
          return (
            <div
              key={entry.tech}
              onClick={() => onEntryClick && onEntryClick(entry)}
              style={{
                background: rowBg,
                border: `1px solid ${isBeyond5 ? "transparent" : isTop3 ? medalColor + "30" : C.border}`,
                borderRadius: 10,
                padding: "9px 12px",
                display: "flex", alignItems: "center", gap: 10,
                cursor: onEntryClick ? "pointer" : "default",
                transition: "background 0.15s",
                opacity: isBeyond5 ? 0.7 : 1,
              }}
              onMouseEnter={onEntryClick ? (e) => { e.currentTarget.style.background = rowBgHover; } : undefined}
              onMouseLeave={onEntryClick ? (e) => { e.currentTarget.style.background = rowBg; } : undefined}
            >
              {/* Rank */}
              <div style={{
                fontSize: 11, fontWeight: 700,
                color: isBeyond5 ? C.dim : isTop3 ? medalColor : C.dim,
                minWidth: 20, textAlign: "center",
                fontVariantNumeric: "tabular-nums",
              }}>
                {i + 1}
              </div>
              {/* Name + subline */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: isBeyond5 ? 400 : 600, color: isBeyond5 ? C.muted : C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {entry.tech}
                </div>
                <div style={{ fontSize: 11, color: C.dim, marginTop: 1 }}>
                  {_renderSubline(entry)}
                </div>
              </div>
              {/* Value — right-aligned */}
              <div style={{
                fontSize: 14, fontWeight: 700,
                color: isBeyond5 ? C.dim : _rateColorFn(entry),
                textAlign: "right", flexShrink: 0, minWidth: 44,
              }}>
                {_renderValue(entry)}
              </div>
            </div>
          );
        })}
        {entries.length === 0 && (
          <div style={{ textAlign: "center", padding: "20px 0", color: C.muted, fontSize: 12 }}>
            {emptyMessage || "No data for selected filters"}
          </div>
        )}
        {hasMore && (
          <button
            onClick={() => setExpanded(e => !e)}
            style={{
              marginTop: 4, background: "none", border: "none",
              color: C.dim, fontSize: 11, cursor: "pointer",
              fontWeight: 500, padding: "2px 0", textAlign: "left",
            }}
            onMouseEnter={e => { e.currentTarget.style.color = C.accent; }}
            onMouseLeave={e => { e.currentTarget.style.color = C.dim; }}
          >
            {expanded ? "Show less" : `View all ${entries.length}`}
          </button>
        )}
      </div>
    </div>
  );
}

function MetricMini({ label, value, color, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: `${C.border}15`, borderRadius: 8, padding: "8px 10px",
        cursor: onClick ? "pointer" : "default",
        transition: onClick ? "background 0.15s" : undefined,
      }}
      onMouseEnter={onClick ? e => { e.currentTarget.style.background = `${C.border}30`; } : undefined}
      onMouseLeave={onClick ? e => { e.currentTarget.style.background = `${C.border}15`; } : undefined}
    >
      <div style={{ fontSize: 10, color: C.muted, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 700, color: color || C.text, marginTop: 2 }}>{value}</div>
    </div>
  );
}

function Section({ title, children, style }) {
  return (
    <div style={{ marginBottom: 28, ...style }}>
      <h3 style={{ fontSize: 14, fontWeight: 700, color: C.accent, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 14 }}>
        {title}
      </h3>
      {children}
    </div>
  );
}

function highlightMention(text, name) {
  if (!name || !text) return text;
  const idx = text.toLowerCase().indexOf(name.toLowerCase());
  if (idx === -1) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ background: "rgba(234,179,8,0.25)", color: C.yellow, borderRadius: 2, padding: "0 1px" }}>
        {text.slice(idx, idx + name.length)}
      </mark>
      {text.slice(idx + name.length)}
    </>
  );
}

// Inline "Closed by" picker for visit records. Stores the canonical full
// name in jobber_visits.closed_by, or null to fall back to the assigned-lead
// attribution. Used to correct the same-day-completion metric when an admin
// closes a visit on a tech's behalf (Jobber doesn't expose completedBy in
// GraphQL, so this is the only way to attribute the close honestly).
function ClosedByEditor({ visitId, currentCloser, leadName, allActiveTechs, onSave }) {
  const [saving, setSaving] = useState(false);
  const [value, setValue] = useState(currentCloser || "");
  useEffect(() => { setValue(currentCloser || ""); }, [currentCloser]);

  const handleChange = async (e) => {
    const next = e.target.value || null;
    if ((next || null) === (currentCloser || null)) return;
    setValue(e.target.value);
    setSaving(true);
    try {
      await onSave(visitId, next);
    } catch {
      setValue(currentCloser || "");
    } finally {
      setSaving(false);
    }
  };

  const autoLabel = leadName ? `Auto — assigned to ${leadName}` : "Auto — assigned tech";
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 6, marginTop: 8, fontSize: 11 }}>
      <span style={{ color: C.muted, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.06em", fontSize: 10 }}>
        Closed by
      </span>
      <select
        value={value}
        onChange={handleChange}
        disabled={saving}
        style={{
          fontSize: 11, background: C.surface, color: C.text,
          border: `1px solid ${C.border}`, borderRadius: 6,
          padding: "3px 6px", outline: "none", cursor: saving ? "default" : "pointer",
        }}
      >
        <option value="">{autoLabel}</option>
        {allActiveTechs.map(t => (
          <option key={t} value={t}>{t}</option>
        ))}
      </select>
      {saving && <span style={{ color: C.muted }}>saving…</span>}
      {!saving && currentCloser && (
        <span style={{ color: C.yellow, background: C.yellowDim, borderRadius: 3, padding: "1px 5px", fontSize: 9, fontWeight: 700 }}>
          manual
        </span>
      )}
    </div>
  );
}

function ReviewAssignmentEditor({ record, allActiveTechs, onUpdateAssignment, jobs, timesheets }) {
  const isManual = record.assignedTechs != null;
  const effectiveTechs = isManual ? record.assignedTechs : (record.autoTechs || []);
  const [localChecked, setLocalChecked] = useState(() => new Set(effectiveTechs));
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState(false);
  const [jobSearch, setJobSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [linkedJob, setLinkedJob] = useState(() => {
    if (!record.assignedJob) return null;
    const found = (jobs || []).find(j => String(j.job_num) === String(record.assignedJob));
    return found || { job_num: record.assignedJob, client_name: null };
  });

  useEffect(() => {
    const ef = record.assignedTechs != null ? record.assignedTechs : (record.autoTechs || []);
    setLocalChecked(new Set(ef));
  }, [record.assignedTechs, record.autoTechs]);

  useEffect(() => {
    if (!record.assignedJob) { setLinkedJob(null); return; }
    const found = (jobs || []).find(j => String(j.job_num) === String(record.assignedJob));
    setLinkedJob(found || { job_num: record.assignedJob, client_name: null });
  }, [record.assignedJob, jobs]);

  const handleToggle = (tech) => {
    setSavedMsg(false);
    setLocalChecked(prev => {
      const next = new Set(prev);
      if (next.has(tech)) next.delete(tech); else next.add(tech);
      return next;
    });
  };

  const isDirty = (() => {
    const saved = new Set(record.assignedTechs != null ? record.assignedTechs : (record.autoTechs || []));
    if (localChecked.size !== saved.size) return true;
    for (const t of localChecked) if (!saved.has(t)) return true;
    if ((linkedJob?.job_num || null) !== (record.assignedJob || null)) return true;
    return false;
  })();

  const handleSave = async () => {
    setSaving(true);
    await onUpdateAssignment(record.id, [...localChecked], linkedJob?.job_num ?? null);
    setSaving(false);
    setSavedMsg(true);
    setTimeout(() => setSavedMsg(false), 2000);
  };

  const handleReset = async () => {
    setSaving(true);
    setLinkedJob(null);
    await onUpdateAssignment(record.id, null, null);
    setSaving(false);
  };

  const jobResults = jobSearch.trim().length > 0
    ? (jobs || [])
        .filter(j => j.client_name && j.client_name.toLowerCase().includes(jobSearch.trim().toLowerCase()))
        .sort((a, b) => (b._closedDate || 0) - (a._closedDate || 0))
        .slice(0, 10)
    : [];

  const handleSelectJob = (j) => {
    setLinkedJob(j);
    setJobSearch("");
    setShowDropdown(false);
    setSavedMsg(false);
    const jobNum = String(j.job_num);
    const techsOnJob = new Set(
      (timesheets || [])
        .filter(ts => String(ts.job_number) === jobNum && ts.user_name && !isExcluded(ts.user_name))
        .map(ts => allActiveTechs.find(t => firstName(t) === firstName(ts.user_name)))
        .filter(Boolean)
    );
    if (techsOnJob.size > 0) setLocalChecked(techsOnJob);
  };

  const handleClearLink = () => {
    setLinkedJob(null);
    setJobSearch("");
    setSavedMsg(false);
  };

  return (
    <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.border}40` }}>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ color: C.muted }}>
          Credit Assignment{" "}
          {isManual
            ? <span style={{ color: C.yellow, background: C.yellowDim, borderRadius: 3, padding: "1px 5px", fontSize: 9 }}>manual</span>
            : <span style={{ color: C.accent, background: C.accentDim, borderRadius: 3, padding: "1px 5px", fontSize: 9 }}>auto</span>
          }
        </span>
        {isManual && (
          <button
            onClick={handleReset}
            disabled={saving}
            style={{ fontSize: 10, background: "none", border: `1px solid ${C.border}`, color: C.muted, borderRadius: 4, padding: "2px 8px", cursor: "pointer" }}
          >
            Reset to auto
          </button>
        )}
      </div>

      {/* Job link section */}
      <div style={{ marginBottom: 8, position: "relative" }}>
        {linkedJob ? (
          <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, background: C.accentDim, border: `1px solid ${C.accent}40`, borderRadius: 6, padding: "5px 8px" }}>
            <span style={{ color: C.accent, fontWeight: 600 }}>
              Linked to Job #{linkedJob.job_num}{linkedJob.client_name ? ` — ${linkedJob.client_name}` : ""}
            </span>
            <button
              onClick={handleClearLink}
              style={{ marginLeft: "auto", fontSize: 10, background: "none", border: `1px solid ${C.border}`, color: C.muted, borderRadius: 4, padding: "1px 7px", cursor: "pointer" }}
            >
              Clear link
            </button>
          </div>
        ) : (
          <div>
            <input
              type="text"
              placeholder="Link to Job — search client name…"
              value={jobSearch}
              onChange={e => { setJobSearch(e.target.value); setShowDropdown(true); }}
              onFocus={() => setShowDropdown(true)}
              onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
              style={{
                width: "100%", boxSizing: "border-box",
                fontSize: 11, background: C.surface, color: C.text,
                border: `1px solid ${C.border}`, borderRadius: 6,
                padding: "5px 8px", outline: "none",
              }}
            />
            {showDropdown && jobResults.length > 0 && (
              <div style={{
                position: "absolute", top: "100%", left: 0, right: 0, zIndex: 99,
                background: C.card, border: `1px solid ${C.border}`, borderRadius: 6,
                marginTop: 2, overflow: "hidden", boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
              }}>
                {jobResults.map(j => (
                  <div
                    key={j.job_num}
                    onMouseDown={() => handleSelectJob(j)}
                    style={{
                      padding: "6px 10px", cursor: "pointer", fontSize: 11,
                      borderBottom: `1px solid ${C.border}40`,
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = C.surface}
                    onMouseLeave={e => e.currentTarget.style.background = ""}
                  >
                    <span style={{ color: C.text, fontWeight: 500 }}>{j.client_name}</span>
                    <span style={{ color: C.muted, fontSize: 10, marginLeft: 8 }}>
                      #{j.job_num}{j._closedDate ? ` · ${j._closedDate.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" })}` : ""}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 12px", marginBottom: 8 }}>
        {allActiveTechs.map(tech => {
          const checked = localChecked.has(tech);
          const isAutoDetected = (record.autoTechs || []).includes(tech);
          return (
            <label key={tech} style={{ display: "flex", alignItems: "center", gap: 4, cursor: "pointer", fontSize: 11, color: checked ? C.text : C.dim, userSelect: "none" }}>
              <input type="checkbox" checked={checked} onChange={() => handleToggle(tech)} style={{ accentColor: C.accent, cursor: "pointer" }} />
              {tech}
              {isAutoDetected && (
                <span style={{ fontSize: 9, color: C.accent, background: C.accentDim, borderRadius: 3, padding: "1px 4px" }}>auto</span>
              )}
              {isManual && checked && !isAutoDetected && (
                <span style={{ fontSize: 9, color: C.yellow, background: C.yellowDim, borderRadius: 3, padding: "1px 4px" }}>manual</span>
              )}
            </label>
          );
        })}
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {isDirty && (
          <button
            onClick={handleSave}
            disabled={saving}
            style={{ fontSize: 11, fontWeight: 700, background: C.accent, color: C.bg, border: "none", borderRadius: 6, padding: "5px 14px", cursor: saving ? "default" : "pointer" }}
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>
        )}
        {savedMsg && !isDirty && (
          <span style={{ fontSize: 11, color: C.green }}>✓ Saved</span>
        )}
      </div>
    </div>
  );
}

// ── Manual name-mention editor ───────────────────────────────────────────────
// The mention boards scan the review text for tech first names — nicknames,
// typos, or "the technician" slip through. This lets an admin credit a mention
// the scan missed (stored as canonical full names in manual_mentions). Auto-
// detected names show as fixed chips; office/social staff (NON_CREW) and
// excluded techs never appear — mentions of them don't pay by standing rule.
function MentionEditor({ record, allActiveTechs, onUpdateMentions }) {
  // Same word-boundary matcher the boards use.
  const autoFirsts = useMemo(() => {
    const lower = (record.reviewText || "").toLowerCase();
    const found = new Set();
    if (!lower) return found;
    for (const techFull of allActiveTechs || []) {
      if (isNonCrew(techFull) || isExcluded(techFull)) continue;
      const fn = firstName(techFull);
      if (!fn) continue;
      let from = 0;
      while (true) {
        const idx = lower.indexOf(fn, from);
        if (idx === -1) break;
        const before = idx > 0 ? lower[idx - 1] : " ";
        const after = idx + fn.length < lower.length ? lower[idx + fn.length] : " ";
        if (!/[a-z]/.test(before) && !/[a-z]/.test(after)) { found.add(fn); break; }
        from = idx + fn.length;
      }
    }
    return found;
  }, [record.reviewText, allActiveTechs]);

  const savedFirsts = useMemo(
    () => new Set((record.manualMentions || []).map(n => firstName(n))),
    [record.manualMentions]
  );
  const [checked, setChecked] = useState(() => new Set(savedFirsts));
  const [saving, setSaving] = useState(false);
  const [savedMsg, setSavedMsg] = useState(false);

  useEffect(() => { setChecked(new Set(savedFirsts)); }, [record.id, savedFirsts]);

  // Office staff (Taylor P) ARE offered as manual chips — a deliberate human
  // credit pays, same rule as manual assigned_techs. Only the AUTO scan never
  // credits them (see autoFirsts above). Part-time/departed techs stay hidden.
  const candidates = (allActiveTechs || []).filter(t => !isExcluded(t));
  const dirty = checked.size !== savedFirsts.size || [...checked].some(fn => !savedFirsts.has(fn));

  const toggle = (fn) => {
    setSavedMsg(false);
    setChecked(prev => {
      const next = new Set(prev);
      if (next.has(fn)) next.delete(fn); else next.add(fn);
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const fullNames = candidates.filter(t => checked.has(firstName(t)));
      await onUpdateMentions(record.id, fullNames);
      setSavedMsg(true);
      setTimeout(() => setSavedMsg(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.border}40` }}>
      <div style={{ fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: C.muted, marginBottom: 6 }}>
        Name Mentions <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>— pays the mention bonus; check a guy the auto-scan missed</span>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {candidates.map(t => {
          const fn = firstName(t);
          const isAuto = autoFirsts.has(fn);
          const isOn = isAuto || checked.has(fn);
          return (
            <button
              key={t}
              onClick={isAuto ? undefined : () => toggle(fn)}
              disabled={isAuto}
              title={isAuto ? `${t} is named in the review text — credited automatically` : (checked.has(fn) ? "Remove manual mention credit" : `Credit ${t} with a mention the scan missed`)}
              style={{
                fontSize: 11, borderRadius: 20, padding: "3px 10px",
                cursor: isAuto ? "default" : "pointer",
                border: `1px solid ${isOn ? C.cyan : C.border}`,
                background: isOn ? `${C.cyan}22` : "none",
                color: isOn ? C.cyan : C.muted,
              }}
            >
              {t.split(" ")[0]}{isAuto ? " · auto" : checked.has(fn) ? " ✓" : ""}
            </button>
          );
        })}
      </div>
      {(dirty || savedMsg) && (
        <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 8 }}>
          {dirty && (
            <button
              onClick={handleSave}
              disabled={saving}
              style={{ fontSize: 11, background: C.accent, border: "none", color: "#0d0f12", fontWeight: 700, borderRadius: 5, padding: "4px 12px", cursor: "pointer", opacity: saving ? 0.6 : 1 }}
            >
              {saving ? "Saving…" : "Save mentions"}
            </button>
          )}
          {savedMsg && <span style={{ fontSize: 11, color: C.green }}>✓ Saved</span>}
        </div>
      )}
    </div>
  );
}

function ReviewDrawerRecord({ r, rType, allActiveTechs, canEditAssignments, onUpdateAssignment, onIgnoreReview, onUpdateMentions, jobs, timesheets }) {
  const [expanded, setExpanded] = useState(false);
  const text = r.reviewText || "";
  const snippet = text.length > 100 ? text.slice(0, 100) + "…" : text;
  const displayText = expanded ? text : snippet;
  const filledStars = r.rating || 0;
  const starStr = "★".repeat(filledStars) + "☆".repeat(Math.max(0, 5 - filledStars));
  const starColor = filledStars >= 4 ? C.yellow : filledStars >= 3 ? C.orange : C.red;
  return (
    <div style={{
      background: C.surface, border: `1px solid ${C.border}`,
      borderRadius: 10, padding: "12px 14px", marginBottom: 8,
      borderLeftWidth: 3, borderLeftColor: starColor,
    }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <div style={{ fontSize: 16, color: starColor, letterSpacing: -1, flexShrink: 0, lineHeight: 1.3 }}>
          {starStr}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
            {r.clientName || r.reviewerName || "Customer"}
            {r.jobNumber && <span style={{ color: C.dim }}> — Job #{r.jobNumber}</span>}
          </div>
          {r.date && (
            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
              {r.date.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" })}
            </div>
          )}
          {text && (
            <div
              style={{
                marginTop: 6, fontSize: 11, color: C.muted,
                background: `${C.border}40`, borderRadius: 6,
                padding: "6px 8px", lineHeight: 1.5,
                cursor: text.length > 100 ? "pointer" : "default",
              }}
              onClick={text.length > 100 ? () => setExpanded(e => !e) : undefined}
            >
              {rType === "name_mentions" && r.mentionedName
                ? highlightMention(displayText, r.mentionedName)
                : displayText}
              {text.length > 100 && (
                <span style={{ color: C.accent, marginLeft: 4 }}>{expanded ? "▲ less" : "▼ more"}</span>
              )}
            </div>
          )}
          {rType === "review_stars" && r.aiConfidence && (
            <div style={{ marginTop: 5, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
              <span style={{
                fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em",
                borderRadius: 3, padding: "1px 5px",
                color:      r.aiConfidence === "high"   ? C.green   : r.aiConfidence === "medium" ? C.yellow   : C.muted,
                background: r.aiConfidence === "high"   ? `${C.green}22`  : r.aiConfidence === "medium" ? `${C.yellow}22` : `${C.border}40`,
              }}>
                AI {r.aiConfidence}
              </span>
              {r.aiReasoning && (
                <span style={{ fontSize: 10, color: C.muted, fontStyle: "italic" }}>{r.aiReasoning}</span>
              )}
            </div>
          )}
          {rType === "review_stars" && canEditAssignments && onUpdateAssignment && !r.ignored && (
            <ReviewAssignmentEditor
              record={r}
              allActiveTechs={allActiveTechs || []}
              onUpdateAssignment={onUpdateAssignment}
              jobs={jobs}
              timesheets={timesheets}
            />
          )}
          {rType === "review_stars" && canEditAssignments && onUpdateMentions && !r.ignored && (
            <MentionEditor
              record={r}
              allActiveTechs={allActiveTechs || []}
              onUpdateMentions={onUpdateMentions}
            />
          )}
          {rType === "review_stars" && canEditAssignments && onIgnoreReview && (
            r.ignored ? (
              <div style={{ marginTop: 8, display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{
                  fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em",
                  color: C.muted, background: `${C.border}40`, borderRadius: 3, padding: "1px 5px",
                }}>
                  Ignored
                </span>
                <button
                  onClick={() => onIgnoreReview(r.id, false)}
                  title="Put this review back in the unassigned queue and credit math"
                  style={{
                    fontSize: 10, background: "none", border: `1px solid ${C.border}`,
                    color: C.muted, borderRadius: 4, padding: "2px 8px", cursor: "pointer",
                  }}
                >
                  Restore
                </button>
              </div>
            ) : !r.tech ? (
              <div style={{ marginTop: 8 }}>
                <button
                  onClick={() => onIgnoreReview(r.id, true)}
                  title="No tech was involved (office praise, generic review, not our client) — drops it from the unassigned queue and all review credit. Restorable from the Ignored Reviews list."
                  style={{
                    fontSize: 10, background: "none", border: `1px solid ${C.border}`,
                    color: C.muted, borderRadius: 4, padding: "3px 10px", cursor: "pointer",
                  }}
                >
                  Ignore — nobody involved
                </button>
              </div>
            ) : null
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Manual metric overrides (excuse / add miss) ─────────────────────────────
// Local calendar day string — the shared key format between the metric
// builders, the override rows, and BonusTracker's deduction calc.
function localDayISO(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Badge + excuse/restore controls on a discipline-metric drawer record.
// r._ov = { metric, techFirst, targetKey, day } — the exact key the metric
// used for this record, so the override always lands on the right one.
function MetricOverrideControls({ r, isMiss, canEdit, onMetricOverride }) {
  if (!r._ov || !onMetricOverride) return null;
  const btn = {
    fontSize: 10, background: "none", border: `1px solid ${C.border}`,
    color: C.muted, borderRadius: 4, padding: "1px 7px", cursor: "pointer",
  };
  if (r.excused) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3 }}>
        <span
          title={r.excuseNote ? `Excused: ${r.excuseNote}` : "Excused by admin — not counted"}
          style={{ fontSize: 10, fontWeight: 700, color: C.muted, background: `${C.border}60`, padding: "2px 6px", borderRadius: 4 }}
        >
          Excused
        </span>
        {canEdit && <button onClick={() => onMetricOverride("unexcuse", r._ov)} style={btn}>Restore</button>}
      </div>
    );
  }
  if (r.manualMiss) {
    return (
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 3 }}>
        <span style={{ fontSize: 10, fontWeight: 700, color: C.purple, background: `${C.purple}22`, padding: "2px 6px", borderRadius: 4 }}>
          Manual
        </span>
        {canEdit && <button onClick={() => onMetricOverride("remove_add_miss", r._ov)} style={btn}>Remove</button>}
      </div>
    );
  }
  if (isMiss && canEdit) {
    return (
      <button
        onClick={() => onMetricOverride("excuse", r._ov)}
        title="Excuse this miss — drops it from the board and any bonus deduction. You'll be asked for a reason."
        style={{ ...btn, padding: "2px 8px" }}
      >
        Excuse
      </button>
    );
  }
  return null;
}

// "+ Add missed entry" form shown in a tech's drill-down drawer — records a
// miss the automation didn't catch (counts as late/missing on the board and
// in the bonus deductions).
function AddMissForm({ metric, techName, onAddMiss }) {
  const [open, setOpen] = useState(false);
  const [day, setDay] = useState("");
  const [target, setTarget] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const input = {
    padding: "5px 8px", borderRadius: 6, border: `1px solid ${C.border}`,
    background: C.surface, color: C.text, fontSize: 12,
  };
  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        style={{ fontSize: 11, background: "none", border: `1px solid ${C.border}`, color: C.muted, borderRadius: 6, padding: "4px 10px", cursor: "pointer" }}
      >
        + Add missed entry
      </button>
    );
  }
  return (
    <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
      <input type="date" value={day} onChange={e => { setDay(e.target.value); setMsg(null); }} style={input} />
      <input placeholder="Job # (optional)" value={target} onChange={e => setTarget(e.target.value)} style={{ ...input, width: 110 }} />
      <input placeholder="Reason" value={note} onChange={e => setNote(e.target.value)} style={{ ...input, width: 150 }} />
      <button
        disabled={!day || saving}
        onClick={async () => {
          setSaving(true);
          try {
            await onAddMiss(metric, techName, { day, targetKey: target.trim() || null, note: note.trim() || null });
            setMsg("✓ Added — reopen this drawer to see it listed");
            setDay(""); setTarget(""); setNote("");
          } catch (_) {
            setMsg("✗ Failed to add");
          } finally {
            setSaving(false);
          }
        }}
        style={{ fontSize: 11, background: C.accent, border: "none", color: "#0d0f12", fontWeight: 700, borderRadius: 6, padding: "5px 12px", cursor: (!day || saving) ? "not-allowed" : "pointer", opacity: (!day || saving) ? 0.5 : 1 }}
      >
        {saving ? "…" : "Add miss"}
      </button>
      <button onClick={() => { setOpen(false); setMsg(null); }} style={{ fontSize: 11, background: "none", border: `1px solid ${C.border}`, color: C.muted, borderRadius: 6, padding: "4px 10px", cursor: "pointer" }}>
        Cancel
      </button>
      {msg && <span style={{ fontSize: 11, color: msg.startsWith("✓") ? C.green : C.red }}>{msg}</span>}
    </div>
  );
}

// ─── Detail Drawer ───────────────────────────────────────────────────────────
function DetailDrawer({ data, onClose, allActiveTechs, canEditAssignments, onUpdateReview, onIgnoreReview, onUpdateMentions, onUpdateVisitCloser, canEditVisitCloser, canEditMetricOverrides, onMetricOverride, onAddMiss, jobs, timesheets }) {
  const [filter, setFilter] = useState("all");
  const [expandedTech, setExpandedTech] = useState(null);

  if (!data) return null;

  const { type, techName, title, records = [], subMetrics, sections, boards, highlightedTech, rawMetrics } = data;
  const isSections = Array.isArray(sections);
  const isLeaderboards = type === "composite_leaderboards";

  // Determine hit/miss field by type
  const isHit = (r) => {
    if (type === "notes") return r.hasNote;
    if (type === "total_notes") return true;
    if (type === "review_stars" || type === "name_mentions") return true;
    if (type === "job_financial") return true;
    if (type === "bonus_rows") return r.qualifies;
    return r.onTime;
  };

  const filtered = isSections ? [] : (
    filter === "all" ? records
      : filter === "hit" ? records.filter(isHit)
        : records.filter(r => !isHit(r))
  );

  const hitCount  = isSections ? 0 : records.filter(isHit).length;
  const missCount = isSections ? 0 : records.length - hitCount;

  const hitLabel  = type === "notes" ? "with notes"
    : type === "total_notes" ? "notes"
    : type === "job_close" ? "same-day closed"
    : type === "review_stars" ? "reviews"
    : type === "name_mentions" ? "mentions"
    : type === "job_financial" ? "jobs"
    : type === "bonus_rows" ? "qualifying"
    : "on time";
  const missLabel = type === "notes" ? "missing"
    : type === "total_notes" ? ""
    : type === "job_close" ? "not same-day"
    : type === "review_stars" || type === "name_mentions" ? ""
    : type === "job_financial" ? ""
    : type === "bonus_rows" ? "below threshold"
    : "late";
  const hitColor  = C.green;
  const missColor = C.red;

  const fmtDate = (d) => d ? d.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" }) : "--";
  const fmtScheduled = (d) => {
    if (!d) return "--";
    const date = d.toLocaleDateString("en-CA", { weekday: "short", month: "short", day: "numeric" });
    const time = d.toLocaleTimeString("en-CA", { hour: "numeric", minute: "2-digit", hour12: true });
    return `${date}, ${time}`;
  };
  const fmtTime = (d) => d ? d.toLocaleTimeString("en-CA", { hour: "numeric", minute: "2-digit", hour12: true }) : null;
  // Variants that include seconds — used when two timestamps fall in the same
  // minute so they don't render identically (e.g. real-time clock-in where
  // start_at and created_at differ by a few seconds — without seconds, the
  // display would look like the app is just echoing one value twice).
  const fmtTimeWithSec = (d) => d ? d.toLocaleTimeString("en-CA", { hour: "numeric", minute: "2-digit", second: "2-digit", hour12: true }) : null;
  // Show "Mon Jan 26, 8:00 a.m." — includes the day so you can tell same-day vs next-day
  const fmtDateTime = (d) => d ? d.toLocaleDateString("en-CA", { weekday: "short", month: "short", day: "numeric" })
    + ", " + d.toLocaleTimeString("en-CA", { hour: "numeric", minute: "2-digit", hour12: true }) : null;
  const fmtDateTimeWithSec = (d) => d ? d.toLocaleDateString("en-CA", { weekday: "short", month: "short", day: "numeric" })
    + ", " + d.toLocaleTimeString("en-CA", { hour: "numeric", minute: "2-digit", second: "2-digit", hour12: true }) : null;
  // True iff two Date objects fall in the same wall-clock minute.
  const sameMinute = (a, b) => a && b && Math.floor(a.getTime() / 60000) === Math.floor(b.getTime() / 60000);
  const isMidnight = (d) => d && d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0;

  function renderRecord(rType, r, idx) {
    // Per-record override: a mixed drawer (e.g. Review Bonus = worked reviews +
    // mention-only reviews) tags individual records with their own render type.
    if (r && r._rType) rType = r._rType;
    if (rType === "timesheet") {
      const periods = r.periods || [];
      const hasMultiplePeriods = periods.length > 1;
      const dayTotalHours = r.totalDayHours != null ? r.totalDayHours : (r.hours || 0);
      return (
        <div key={idx} style={{
          background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: 10, padding: "12px 14px", marginBottom: 8,
          borderLeftWidth: 3, borderLeftColor: r.onTime ? hitColor : missColor,
        }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <span style={{ fontSize: 18, lineHeight: 1, marginTop: 1 }}>{r.onTime ? "✓" : "✗"}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                {!techName && <span style={{ color: C.accent, marginRight: 6 }}>{r.tech}</span>}
                {r.clientName
                  ? <><span style={{ color: C.text }}>{r.clientName}</span><span style={{ color: C.dim }}>{r.jobNumber ? ` — Job #${r.jobNumber}` : ""}</span></>
                  : (r.jobTitle || r.jobNumber ? (r.jobTitle || `Job #${r.jobNumber}`) : "No job title")
                }
              </div>
              {r.scheduledTime && (
                <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>
                  Scheduled: <span style={{ color: C.text }}>{fmtScheduled(r.scheduledTime)}</span>
                </div>
              )}
              {r.missedSignIn && (
                <div style={{ fontSize: 11, color: C.red, marginTop: 3 }}>
                  Assigned to this assessment (completed in Jobber) — no request time clocked that day
                </div>
              )}
              {r.clockInTime && (() => {
                // If clock-in and entered fall in the same minute, render with
                // seconds so the two lines aren't visually identical (which
                // looks like the app is just echoing the clock-in twice).
                const collide = sameMinute(r.clockInTime, r.latestEntered);
                return (
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                    Clocked in: <span style={{ color: r.onTime ? C.green : C.yellow }}>
                      {collide ? fmtDateTimeWithSec(r.clockInTime) : fmtDateTime(r.clockInTime)}
                    </span>
                  </div>
                );
              })()}
              {/* "Entered" = Jobber's immutable createdAt. If this is on a later
                  calendar day than the work, the entry was backdated. We always
                  surface it (even when same-day on time) so you can see how
                  promptly the row was logged. */}
              {r.latestEntered && r.clockInTime && (() => {
                const sameDay = sameCalendarDay(r.latestEntered, r.clockInTime);
                const collide = sameMinute(r.clockInTime, r.latestEntered);
                const daysLate = sameDay
                  ? 0
                  : Math.floor((r.latestEntered.getTime() - r.clockInTime.getTime()) / 86400000);
                return (
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                    Entered: <span style={{ color: sameDay ? C.green : C.red }}>
                      {collide ? fmtDateTimeWithSec(r.latestEntered) : fmtDateTime(r.latestEntered)}
                      {!sameDay && daysLate > 0 ? ` (${daysLate}d late)` : ""}
                    </span>
                  </div>
                );
              })()}
              {hasMultiplePeriods ? (
                <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
                  <span style={{ color: C.text }}>{periods.length}</span> clock-in periods · <span style={{ color: C.text }}>{dayTotalHours.toFixed(1)}h</span> total
                  <ul style={{ margin: "3px 0 0 0", paddingLeft: 14, lineHeight: 1.5, listStyle: "none" }}>
                    {periods.map((p, i) => {
                      const periodLate = p.created && p.start && !sameCalendarDay(p.created, p.start);
                      const collide = sameMinute(p.start, p.created);
                      // Always show the period's entered time (not just for
                      // backdated rows). Red when the calendar day differs,
                      // muted otherwise. Format with seconds when entered lands
                      // in the same minute as the period's start so you can see
                      // the actual creation moment (proves real Jobber data).
                      const enteredStr = !p.created
                        ? null
                        : periodLate
                          ? fmtDateTime(p.created)
                          : (collide ? fmtTimeWithSec(p.created) : fmtTime(p.created));
                      return (
                        <li key={i} style={{ fontSize: 11, color: C.dim }}>
                          <span style={{ color: C.muted }}>•</span>{" "}
                          <span style={{ color: C.text }}>{fmtTime(p.start)}{p.end ? `–${fmtTime(p.end)}` : ""}</span>
                          {" · "}<span style={{ color: C.text }}>{(p.hours || 0).toFixed(1)}h</span>
                          {p.label ? <span style={{ color: p.isTravel ? C.muted : C.dim }}> · {p.label}</span> : ""}
                          {enteredStr && (
                            <span style={{ color: periodLate ? C.red : C.muted }}> · entered {enteredStr}</span>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ) : (
                <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                  {dayTotalHours != null ? dayTotalHours.toFixed(1) + "h" : ""}
                  {!r.scheduledTime && r.date && ` · ${fmtDate(r.date)}`}
                  {!r.scheduledTime && r.jobNumber && ` · #${r.jobNumber}`}
                </div>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
              {!r.excused && (
                <div style={{
                  fontSize: 11, fontWeight: 700,
                  color: r.onTime ? hitColor : missColor,
                  background: r.onTime ? C.greenDim : C.redDim,
                  padding: "3px 8px", borderRadius: 4
                }}>
                  {r.onTime ? "On Time" : r.missedSignIn ? "No Sign-In" : "Late"}
                </div>
              )}
              <MetricOverrideControls r={r} isMiss={!r.onTime} canEdit={canEditMetricOverrides} onMetricOverride={onMetricOverride} />
            </div>
          </div>
        </div>
      );
    }

    if (rType === "clock_in") {
      const deltaColor = r.isOnTime ? C.green : r.deltaMin > 30 ? C.red : C.yellow;
      const deltaLabel = r.deltaMin > 0 ? `+${r.deltaMin}m late` : r.deltaMin < 0 ? `${Math.abs(r.deltaMin)}m early` : "exact";
      return (
        <div key={idx} style={{
          background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: 10, padding: "12px 14px", marginBottom: 8,
          borderLeftWidth: 3, borderLeftColor: r.isOnTime ? hitColor : missColor,
        }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <span style={{ fontSize: 18, lineHeight: 1, marginTop: 1 }}>{r.isOnTime ? "✓" : "✗"}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                {r.clientName
                  ? <><span>{r.clientName}</span><span style={{ color: C.dim }}>{r.jobNumber ? ` — Job #${r.jobNumber}` : ""}</span></>
                  : (r.jobNumber ? `Job #${r.jobNumber}` : "No job info")
                }
              </div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>
                Scheduled: <span style={{ color: C.text }}>{fmtTime(r.scheduledTime)}</span>
                {" · "}Timer start: <span style={{ color: deltaColor }}>{fmtTime(r.timerStartTime || r.clockInTime)}</span>
              </div>
              {r.wasEdited && (
                <div style={{ fontSize: 10, color: C.yellow, marginTop: 2 }}>
                  ⚠ Declared start {fmtTime(r.clockInTime)} ({r.declaredVsTimerMin > 0 ? `edited ${r.declaredVsTimerMin}m earlier` : `edited ${Math.abs(r.declaredVsTimerMin)}m later`})
                </div>
              )}
              <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                {fmtDate(r.date)}{r.jobNumber && ` · #${r.jobNumber}`}{r.hours != null ? ` · ${r.hours.toFixed(1)}h` : ""}
                {r.jobNumber && (
                  <>
                    {" · "}
                    <a
                      href={`https://secure.getjobber.com/jobs?search=${encodeURIComponent(r.jobNumber)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: C.accent, textDecoration: "none" }}
                    >
                      View in Jobber →
                    </a>
                  </>
                )}
              </div>
            </div>
            <div style={{
              fontSize: 11, fontWeight: 700, flexShrink: 0,
              color: deltaColor,
              background: r.isOnTime ? C.greenDim : r.deltaMin > 30 ? C.redDim : C.yellowDim,
              padding: "3px 8px", borderRadius: 4
            }}>
              {deltaLabel}
            </div>
          </div>
        </div>
      );
    }

    if (rType === "forgot_signout") {
      return (
        <div key={idx} style={{
          background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: 10, padding: "12px 14px", marginBottom: 8,
          borderLeftWidth: 3, borderLeftColor: C.orange,
        }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <span style={{ fontSize: 18, lineHeight: 1, marginTop: 1 }}>⚠</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                {r.clientName
                  ? <><span>{r.clientName}</span><span style={{ color: C.dim }}>{r.jobNumber ? ` — Job #${r.jobNumber}` : ""}</span></>
                  : (r.jobNumber ? `Job #${r.jobNumber}` : "No job info")
                }
              </div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                {fmtDate(r.date)}{r.endDate ? ` → ${fmtDate(r.endDate)}` : ""}
                {r.jobNumber && ` · #${r.jobNumber}`}
              </div>
            </div>
            <div style={{
              fontSize: 11, fontWeight: 700, flexShrink: 0,
              color: C.orange, background: C.orangeDim,
              padding: "3px 8px", borderRadius: 4
            }}>
              {r.reason}
            </div>
          </div>
        </div>
      );
    }

    if (rType === "notes") {
      return (
        <div key={idx} style={{
          background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: 10, padding: "12px 14px", marginBottom: 8,
          borderLeftWidth: 3, borderLeftColor: r.hasNote ? hitColor : missColor,
        }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <span style={{ fontSize: 18, lineHeight: 1, marginTop: 1 }}>{r.hasNote ? "✓" : "✗"}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                {!techName && <span style={{ color: C.accent, marginRight: 6 }}>{r.tech}</span>}
                {r.clientName
                  ? <><span style={{ color: C.text }}>{r.clientName}</span><span style={{ color: C.dim }}>{r.jobNumber ? ` — Job #${r.jobNumber}` : ""}</span></>
                  : (r.jobTitle || r.jobNumber ? (r.jobTitle || `Job #${r.jobNumber}`) : "No job info")
                }
              </div>
              {r.scheduledTime && (
                <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>
                  Scheduled: <span style={{ color: C.text }}>{fmtScheduled(r.scheduledTime)}</span>
                </div>
              )}
              {r.clockInTime && (
                <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                  Clocked in: <span style={{ color: C.green }}>{fmtTime(r.clockInTime)}</span>
                </div>
              )}
              {!r.scheduledTime && (
                <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                  {fmtDate(r.date)}{r.jobNumber && ` · #${r.jobNumber}`}
                </div>
              )}
              {r.noteText && (
                <div style={{
                  marginTop: 6, fontSize: 11, color: C.muted,
                  background: `${C.border}40`, borderRadius: 6,
                  padding: "6px 8px", lineHeight: 1.4,
                  maxHeight: 80, overflowY: "auto"
                }}>
                  {r.noteText.length > 200 ? r.noteText.slice(0, 200) + "…" : r.noteText}
                </div>
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
              {!r.excused && (
                <div style={{
                  fontSize: 11, fontWeight: 700,
                  color: r.hasNote ? hitColor : missColor,
                  background: r.hasNote ? C.greenDim : C.redDim,
                  padding: "3px 8px", borderRadius: 4
                }}>
                  {r.hasNote ? "Note" : "Missing"}
                </div>
              )}
              {r.hasNote && r.noteFromTimesheet && (
                <div style={{
                  fontSize: 10, fontWeight: 600, color: C.cyan,
                  background: "rgba(6,182,212,0.10)", padding: "2px 7px", borderRadius: 4
                }}>
                  ⏱ from timesheet
                </div>
              )}
              <MetricOverrideControls r={r} isMiss={!r.hasNote} canEdit={canEditMetricOverrides} onMetricOverride={onMetricOverride} />
            </div>
          </div>
        </div>
      );
    }

    if (rType === "visits") {
      return (
        <div key={idx} style={{
          background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: 10, padding: "12px 14px", marginBottom: 8,
          borderLeftWidth: 3,
          borderLeftColor: r.excludedFromLeaderboard ? C.muted : (r.onTime ? hitColor : missColor),
          opacity: r.excludedFromLeaderboard ? 0.85 : 1,
        }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <span style={{ fontSize: 18, lineHeight: 1, marginTop: 1 }}>{r.onTime ? "✓" : "✗"}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                {!techName && r.tech && <span style={{ color: C.accent, marginRight: 6 }}>{r.tech}</span>}
                {r.clientName
                  ? <><span style={{ color: C.text }}>{r.clientName}</span><span style={{ color: C.dim }}>{r.jobNumber ? ` — Job #${r.jobNumber}` : ""}</span></>
                  : (r.jobTitle || r.jobNumber ? (r.jobTitle || `Job #${r.jobNumber}`) : "No job info")
                }
              </div>
              {r.scheduledTime && (
                <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>
                  Scheduled: <span style={{ color: C.text }}>{fmtScheduled(r.scheduledTime)}</span>
                </div>
              )}
              {r.clockInTime && (
                <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                  Clocked in: <span style={{ color: C.green }}>{fmtTime(r.clockInTime)}</span>
                </div>
              )}
              <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                {!r.scheduledTime && r.scheduledDate && `Scheduled ${fmtDate(r.scheduledDate)} · `}
                Completed {fmtDate(r.completedDate)}
                {!r.scheduledTime && r.jobNumber && ` · #${r.jobNumber}`}
              </div>
              {canEditVisitCloser && onUpdateVisitCloser && r.id && (
                <ClosedByEditor
                  visitId={r.id}
                  currentCloser={r.closedBy}
                  leadName={r.leadName}
                  allActiveTechs={allActiveTechs || []}
                  onSave={onUpdateVisitCloser}
                />
              )}
            </div>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
              {!r.excused && (
                <div style={{
                  fontSize: 11, fontWeight: 700,
                  color: r.onTime ? hitColor : missColor,
                  background: r.onTime ? C.greenDim : C.redDim,
                  padding: "3px 8px", borderRadius: 4
                }}>
                  {r.onTime ? "Same Day" : "Late"}
                </div>
              )}
              <MetricOverrideControls r={r} isMiss={!r.onTime} canEdit={canEditMetricOverrides} onMetricOverride={onMetricOverride} />
              {r.excludedFromLeaderboard && (
                <div style={{
                  fontSize: 10, fontWeight: 700,
                  color: C.muted, background: `${C.border}60`,
                  padding: "2px 6px", borderRadius: 4, whiteSpace: "nowrap",
                }} title="Admin closed this visit on the tech's behalf — not counted on anyone's leaderboard">
                  Admin close
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (rType === "late_notes") {
      return (
        <div key={idx} style={{
          background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: 10, padding: "12px 14px", marginBottom: 8,
          borderLeftWidth: 3, borderLeftColor: r.onTime ? hitColor : missColor,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 18, lineHeight: 1 }}>{r.onTime ? "✓" : "✗"}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                {!techName && <span style={{ color: C.accent, marginRight: 6 }}>{r.tech}</span>}
                {r.jobTitle || r.jobNumber ? (r.jobTitle || `Job #${r.jobNumber}`) : "No job info"}
              </div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                Visit {fmtDate(r.date)}
                {" · "}Note filed {fmtDate(r.noteCreatedAt)}
                {r.jobNumber && ` · #${r.jobNumber}`}
              </div>
            </div>
            <div style={{
              fontSize: 11, fontWeight: 700,
              color: r.onTime ? hitColor : missColor,
              background: r.onTime ? C.greenDim : C.redDim,
              padding: "3px 8px", borderRadius: 4
            }}>
              {r.onTime ? "On Time" : "Late"}
            </div>
          </div>
        </div>
      );
    }

    if (rType === "total_notes") {
      return (
        <div key={idx} style={{
          background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: 10, padding: "12px 14px", marginBottom: 8,
          borderLeftWidth: 3, borderLeftColor: C.green,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
              {!techName && <span style={{ color: C.accent, marginRight: 6 }}>{r.tech}</span>}
              {r.jobNumber ? `Job #${r.jobNumber}` : "No job info"}
            </div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
              {fmtDate(r.date)}
              {r.jobNumber && ` · #${r.jobNumber}`}
            </div>
            {r.noteText && (
              <div style={{
                marginTop: 6, fontSize: 11, color: C.muted,
                background: `${C.border}40`, borderRadius: 6,
                padding: "6px 8px", lineHeight: 1.4,
                maxHeight: 80, overflowY: "auto"
              }}>
                {r.noteText.length > 200 ? r.noteText.slice(0, 200) + "…" : r.noteText}
              </div>
            )}
          </div>
        </div>
      );
    }

    if (rType === "job_close") {
      return (
        <div key={idx} style={{
          background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: 10, padding: "12px 14px", marginBottom: 8,
          borderLeftWidth: 3, borderLeftColor: r.onTime ? hitColor : missColor,
        }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{ fontSize: 18, lineHeight: 1 }}>{r.onTime ? "✓" : "✗"}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                {!techName && <span style={{ color: C.accent, marginRight: 6 }}>{r.tech}</span>}
                {r.jobTitle || r.jobNumber ? (r.jobTitle || `Job #${r.jobNumber}`) : "No job info"}
              </div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                Visit {fmtDate(r.date)}
                {r.closeDate && ` · Closed ${fmtDate(r.closeDate)}`}
                {r.jobNumber && ` · #${r.jobNumber}`}
              </div>
            </div>
            <div style={{
              fontSize: 11, fontWeight: 700,
              color: r.onTime ? hitColor : missColor,
              background: r.onTime ? C.greenDim : C.redDim,
              padding: "3px 8px", borderRadius: 4
            }}>
              {r.onTime ? "Same Day" : "Later"}
            </div>
          </div>
        </div>
      );
    }

    if (rType === "review_stars" || rType === "name_mentions") {
      return (
        <ReviewDrawerRecord
          key={idx}
          r={r}
          rType={rType}
          allActiveTechs={allActiveTechs}
          canEditAssignments={canEditAssignments}
          onUpdateAssignment={onUpdateReview}
          onIgnoreReview={onIgnoreReview}
          onUpdateMentions={onUpdateMentions}
          jobs={jobs}
          timesheets={timesheets}
        />
      );
    }

    if (rType === "bonus_rows") {
      const jobNum = r.jobNum;
      const closedStr = r.closedDate
        ? r.closedDate.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" })
        : "--";
      // "Updated" = jb_jobs.updated_at, which Jobber bumps every time the job is
      // touched (expense added, sub bill posted, etc.). If this is recent, the
      // profit/bonus numbers reflect the latest data; if it's old, late expenses
      // may not be captured (the daily cron only re-syncs the last 60 days).
      const updatedDate = r.jobUpdatedAt;
      const updatedStr = updatedDate
        ? updatedDate.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" })
        : null;
      const daysSinceUpdate = updatedDate
        ? Math.floor((Date.now() - updatedDate.getTime()) / 86400000)
        : null;
      const updatedColor = daysSinceUpdate == null
        ? C.muted
        : daysSinceUpdate <= 60 ? C.green
        : daysSinceUpdate <= 90 ? C.yellow
        : C.muted;
      // Status pill
      let statusLabel, statusColor, statusBg;
      switch (r.status) {
        case "completed":
          statusLabel = "Work completed"; statusColor = C.muted; statusBg = `${C.border}40`; break;
        case "invoiced":
          statusLabel = `Invoiced ${dollar(r.invoiced)} — awaiting payment`; statusColor = C.yellow; statusBg = C.yellowDim; break;
        case "partial":
          statusLabel = `Paid ${dollar(r.paidOnInvoice)} / ${dollar(r.invoiced)}`; statusColor = C.yellow; statusBg = C.yellowDim; break;
        case "paid":
          statusLabel = `Paid ${dollar(r.invoiced)}`; statusColor = C.green; statusBg = C.greenDim; break;
        default:
          statusLabel = "—"; statusColor = C.muted; statusBg = `${C.border}40`;
      }
      const roleColor = r.role === "lead" ? C.accent : C.blue;
      const roleLabel = r.role === "lead" ? "Lead" : "Crew";
      const dim = !r.qualifies; // non-qualifying = visually muted
      return (
        <div key={idx} style={{
          background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: 10, padding: "12px 14px", marginBottom: 8,
          borderLeftWidth: 3, borderLeftColor: dim ? C.border : roleColor,
          opacity: dim ? 0.7 : 1,
        }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                <span style={{
                  fontSize: 10, fontWeight: 700, color: roleColor,
                  background: `${roleColor}22`, padding: "2px 7px", borderRadius: 4,
                  textTransform: "uppercase", letterSpacing: "0.05em",
                }}>{roleLabel}</span>
                {!r.qualifies && (
                  <span style={{
                    fontSize: 9, fontWeight: 600, color: C.muted,
                    background: `${C.border}60`, padding: "2px 6px", borderRadius: 4,
                    textTransform: "uppercase", letterSpacing: "0.05em",
                  }}>Below threshold</span>
                )}
                <span style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                  {r.clientName || (jobNum ? `Job #${jobNum}` : "Unknown job")}
                </span>
                {jobNum && <span style={{ fontSize: 11, color: C.dim }}>#{jobNum}</span>}
              </div>
              {r.jobTitle && r.jobTitle !== r.clientName && (
                <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{r.jobTitle}</div>
              )}
              <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                Closed {closedStr} · {dollar(r.revenue)} rev · {pct(r.profitPct)} margin
              </div>
              {updatedStr && (
                <div style={{ fontSize: 10, color: C.dim, marginTop: 2 }}>
                  Updated <span style={{ color: updatedColor, fontWeight: 600 }}>{updatedStr}</span>
                  {daysSinceUpdate != null && (
                    <span style={{ color: C.dim }}>
                      {" "}({daysSinceUpdate === 0 ? "today" : daysSinceUpdate === 1 ? "1 day ago" : `${daysSinceUpdate} days ago`})
                    </span>
                  )}
                </div>
              )}
              <div style={{
                marginTop: 6, display: "inline-block",
                fontSize: 10, fontWeight: 600,
                color: statusColor, background: statusBg,
                padding: "3px 8px", borderRadius: 4,
              }}>
                {statusLabel}
              </div>
            </div>
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              {r.qualifies && r._hideBonus ? (
                <div style={{ fontSize: 11, color: C.dim, fontStyle: "italic" }}>qualifies</div>
              ) : r.qualifies ? (
                <>
                  <div style={{ fontSize: 14, fontWeight: 700, color: r.paid ? C.muted : roleColor, textDecoration: r.paid ? "line-through" : "none" }}>
                    {dollar(r.bonus)}
                  </div>
                  <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>
                    {r.role === "lead"
                      ? `${Number(r.bonus_pct).toFixed(1)}%${r.overridden ? "*" : ""}`
                      : `${Number(r.bonus_pct).toFixed(1)}% pool · ${((r.weight || 0) * 100).toFixed(0)}%${r.weightOverridden ? "*" : ""}`}
                  </div>
                  {r.paid && (
                    <div style={{ fontSize: 10, color: C.green, marginTop: 2, fontWeight: 600 }}>PAID</div>
                  )}
                </>
              ) : (
                <div style={{ fontSize: 11, color: C.dim, fontStyle: "italic" }}>
                  no bonus
                </div>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (rType === "job_financial") {
      const jobNum = r.job_num || r.job_number || null;
      const closedStr = r._closedDate
        ? r._closedDate.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" })
        : "--";
      const invDates = r._invoiceDates || [];
      const invDatesStr = invDates.length > 0
        ? invDates.map(d => d.toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "2-digit" })).join(" · ")
        : null;
      return (
        <div key={idx} style={{
          background: C.surface, border: `1px solid ${C.border}`,
          borderRadius: 10, padding: "12px 14px", marginBottom: 8,
          borderLeftWidth: 3, borderLeftColor: r._qualifies ? C.accent : C.green,
        }}>
          <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>
                {r.client_name || r.title || (jobNum ? `Job #${jobNum}` : "Unknown job")}
                {jobNum && <span style={{ color: C.dim }}>{` — #${jobNum}`}</span>}
              </div>
              {r.client_name && r.title && r.title !== r.client_name && (
                <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{r.title}</div>
              )}
              <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Closed: {closedStr}</div>
              {invDatesStr && (
                <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                  Invoiced: <span style={{ color: C.text }}>{invDatesStr}</span>
                </div>
              )}
            </div>
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              {r.total_revenue != null && <div style={{ fontSize: 13, fontWeight: 700, color: C.green }}>{dollar(r.total_revenue)}</div>}
              {r.profit != null && <div style={{ fontSize: 11, color: (r.profit || 0) >= 0 ? C.blue : C.red }}>{dollar(r.profit)} profit</div>}
              {r.profit_pct != null && <div style={{ fontSize: 11, color: r.profit_pct >= 35 ? C.green : r.profit_pct >= 25 ? C.yellow : C.red }}>{pct(r.profit_pct)} margin</div>}
              {r._qualifies && (
                <div style={{
                  marginTop: 6, fontSize: 11, fontWeight: 700,
                  color: C.accent, background: C.accentDim,
                  borderRadius: 4, padding: "3px 6px",
                }}>
                  Bonus {dollar(r._bonus)}
                  <span style={{ color: C.muted, fontWeight: 400, marginLeft: 4 }}>
                    @ {Number(r._bonusPct).toFixed(0)}%{r._bonusOverridden ? "*" : ""}
                  </span>
                </div>
              )}
              {r._bonusPaid && (
                <div style={{ marginTop: 4, fontSize: 10, color: C.muted }}>Paid</div>
              )}
            </div>
          </div>
        </div>
      );
    }

    return null;
  }

  // ── Leaderboards mode (composite sub-board rankings) ─────────────────────
  if (isLeaderboards) {
    return (
      <>
        <div
          onClick={onClose}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
            zIndex: 1000, backdropFilter: "blur(2px)"
          }}
        />
        <div style={{
          position: "fixed", top: 0, right: 0, bottom: 0,
          width: "min(520px, 100vw)",
          background: C.bg, borderLeft: `1px solid ${C.border}`,
          zIndex: 1001, display: "flex", flexDirection: "column",
          boxShadow: "-8px 0 32px rgba(0,0,0,0.5)"
        }}>
          <div style={{
            padding: "16px 20px", borderBottom: `1px solid ${C.border}`,
            display: "flex", alignItems: "flex-start", gap: 12, flexShrink: 0
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>{title}</div>
              <div style={{ fontSize: 12, color: C.accent, marginTop: 2 }}>
                Sub-board rankings
                {highlightedTech && <span style={{ color: C.muted }}> — {highlightedTech} highlighted</span>}
              </div>
              {subMetrics && (
                <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                  {subMetrics.map((sm, i) => (
                    <div key={i} style={{ background: `${C.border}30`, borderRadius: 6, padding: "4px 8px" }}>
                      <span style={{ fontSize: 10, color: C.muted }}>{sm.label}: </span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: sm.color }}>{sm.value}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              style={{
                background: "none", border: "none", color: C.muted,
                cursor: "pointer", fontSize: 22, lineHeight: 1, padding: 4, flexShrink: 0
              }}
            >×</button>
          </div>
          <div style={{ flex: 1, overflowY: "auto", padding: "12px 20px" }}>
            {(boards || []).map((board, bi) => (
              <div key={bi} style={{ marginBottom: 28 }}>
                <div style={{
                  fontSize: 11, fontWeight: 700, color: C.accent,
                  textTransform: "uppercase", letterSpacing: "0.06em",
                  marginBottom: 10, paddingBottom: 8, borderBottom: `1px solid ${C.border}`
                }}>
                  {board.title}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {board.entries.map((e, i) => {
                    const isHigh = e.tech === highlightedTech;
                    const medalColor = MEDAL_COLORS[i] || null;
                    const rankLabel = i < 5 ? RANK_LABELS[i] : `${i + 1}th`;
                    const valueColor = e.isCountOnly ? C.blue : (e.rate != null ? rateColor(e.rate) : C.muted);
                    const expandKey = `${board.title}|${e.tech}`;
                    const isExpanded = expandedTech === expandKey;
                    const techRecords = rawMetrics ? (rawMetrics[board.title] || {})[e.tech] || [] : [];
                    const hasRecords = techRecords.length > 0;
                    return (
                      <div key={e.tech}>
                        <div
                          onClick={hasRecords ? () => setExpandedTech(isExpanded ? null : expandKey) : undefined}
                          style={{
                            background: isHigh ? C.accentDim : i < 3 ? `${medalColor}09` : C.surface,
                            border: `1px solid ${isHigh ? C.accent + "60" : i < 3 ? medalColor + "35" : C.border}`,
                            borderRadius: isExpanded ? "10px 10px 0 0" : 10, padding: "10px 14px",
                            display: "flex", alignItems: "center", gap: 12,
                            cursor: hasRecords ? "pointer" : "default",
                            transition: "background 0.15s",
                          }}>
                          <div style={{
                            fontSize: i < 3 ? 16 : 13, fontWeight: 700,
                            color: i < 3 ? medalColor : C.dim,
                            minWidth: 36, fontFamily: "'Bebas Neue', sans-serif",
                            letterSpacing: "0.04em", lineHeight: 1,
                          }}>
                            {rankLabel}
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              fontSize: 13, fontWeight: isHigh ? 700 : 600,
                              color: isHigh ? C.accent : C.text,
                              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                            }}>
                              {e.tech}{isHigh ? " ★" : ""}
                              {hasRecords && (
                                <span style={{ fontSize: 10, color: C.muted, marginLeft: 6 }}>
                                  {isExpanded ? "▲" : "▼"}
                                </span>
                              )}
                            </div>
                            <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>
                              {e.isCountOnly
                                ? `${e.count} ${e.countLabel}`
                                : `${e.countStr} ${e.countLabel}`}
                            </div>
                          </div>
                          <div style={{
                            fontSize: 16, fontWeight: 700, color: valueColor,
                            fontFamily: "'Bebas Neue', sans-serif",
                            letterSpacing: "0.04em", flexShrink: 0,
                          }}>
                            {e.isCountOnly ? e.count : (e.rate != null ? pct(e.rate) : "--")}
                          </div>
                        </div>
                        {/* ── Expanded records ── */}
                        {isExpanded && techRecords.length > 0 && (
                          <div style={{
                            background: `${C.surface}cc`, border: `1px solid ${C.border}`,
                            borderTop: "none", borderRadius: "0 0 10px 10px",
                            padding: "8px 10px", maxHeight: 320, overflowY: "auto",
                          }}>
                            <div style={{ fontSize: 10, color: C.accent, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>
                              {techRecords.length} record{techRecords.length !== 1 ? "s" : ""} — {e.tech}
                            </div>
                            {techRecords.map((rec, ri) => {
                              const dateStr = rec.date ? rec.date.toLocaleDateString("en-CA", { month: "short", day: "numeric", weekday: "short" }) : "--";
                              const isTimesheetRec = rec.createdAt !== undefined;
                              const isClockInRec = rec.deltaMin !== undefined;
                              const isNoteRec = rec.hasNote !== undefined;
                              const isLateNoteRec = rec.noteCreatedAt !== undefined && rec.hasNote === undefined;
                              return (
                                <div key={ri} style={{
                                  background: C.card, borderRadius: 8, padding: "8px 10px", marginBottom: 4,
                                  border: `1px solid ${C.border}40`,
                                }}>
                                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                                    <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{dateStr}</div>
                                    {isTimesheetRec && (
                                      <span style={{
                                        fontSize: 10, fontWeight: 700, borderRadius: 4, padding: "2px 6px",
                                        background: rec.onTime ? C.greenDim : C.redDim,
                                        color: rec.onTime ? C.green : C.red,
                                      }}>
                                        {rec.onTime ? "SAME-DAY" : "LATE"}
                                      </span>
                                    )}
                                    {isClockInRec && (
                                      <span style={{
                                        fontSize: 10, fontWeight: 700, borderRadius: 4, padding: "2px 6px",
                                        background: rec.isOnTime ? C.greenDim : C.redDim,
                                        color: rec.isOnTime ? C.green : C.red,
                                      }}>
                                        {rec.isOnTime ? "ON TIME" : `${rec.deltaMin > 0 ? "+" : ""}${rec.deltaMin}min`}
                                      </span>
                                    )}
                                    {isNoteRec && (
                                      <span style={{
                                        fontSize: 10, fontWeight: 700, borderRadius: 4, padding: "2px 6px",
                                        background: rec.hasNote ? C.greenDim : C.redDim,
                                        color: rec.hasNote ? C.green : C.red,
                                      }}>
                                        {rec.hasNote ? "NOTED" : "MISSING"}
                                      </span>
                                    )}
                                    {isLateNoteRec && (
                                      <span style={{
                                        fontSize: 10, fontWeight: 700, borderRadius: 4, padding: "2px 6px",
                                        background: rec.onTime ? C.greenDim : C.redDim,
                                        color: rec.onTime ? C.green : C.red,
                                      }}>
                                        {rec.onTime ? "ON TIME" : "LATE NOTE"}
                                      </span>
                                    )}
                                  </div>
                                  {(rec.clientName || rec.jobNumber) && (
                                    <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>
                                      {rec.clientName || ""}{rec.jobNumber ? `${rec.clientName ? " — " : ""}#${rec.jobNumber}` : ""}
                                    </div>
                                  )}
                                  {isTimesheetRec && (() => {
                                    // Prefer latestEntered (MAX createdAt across periods) over
                                    // the dedup-winning row's createdAt — the latter can hide
                                    // a backdated period when the day also has a real-time one.
                                    const enteredDate = rec.latestEntered || parseDate(rec.createdAt);
                                    // daysLate is measured from the earliest clock-in of the day
                                    // (rec.clockInTime = earliestStart), not from rec.date (which
                                    // is the dedup-winning row's start_at = the LATEST period).
                                    const refStart = rec.clockInTime || rec.date;
                                    const daysLate = enteredDate && refStart
                                      ? Math.floor((enteredDate.getTime() - refStart.getTime()) / 86400000)
                                      : null;
                                    const collide = sameMinute(rec.clockInTime, enteredDate);
                                    const enteredStr = enteredDate
                                      ? (collide ? fmtDateTimeWithSec(enteredDate) : fmtDateTime(enteredDate))
                                      : null;
                                    return (
                                      <div style={{ fontSize: 10, color: C.dim, marginTop: 2, lineHeight: 1.5 }}>
                                        {rec.hours > 0 ? <>{hrs(rec.hours)} logged</> : ""}
                                        {rec.clockInTime && !isMidnight(rec.clockInTime)
                                          ? <>{rec.hours > 0 ? " · " : ""}In: <span style={{ color: C.text }}>{collide ? fmtDateTimeWithSec(rec.clockInTime) : fmtDateTime(rec.clockInTime)}</span></>
                                          : ""}
                                        {rec.clockOutTime && !isMidnight(rec.clockOutTime)
                                          ? <>{" · "}Out: <span style={{ color: C.text }}>{fmtDateTime(rec.clockOutTime)}</span></>
                                          : ""}
                                        {rec.scheduledTime && !isMidnight(rec.scheduledTime)
                                          ? <><br/>Sched: <span style={{ color: C.muted }}>{fmtDateTime(rec.scheduledTime)}</span></>
                                          : ""}
                                        {enteredStr && (
                                          <><br/>Entered: <span style={{ color: rec.onTime ? C.green : C.red }}>
                                            {enteredStr}{!rec.onTime && daysLate > 0 ? ` (${daysLate}d late)` : ""}
                                          </span></>
                                        )}
                                      </div>
                                    );
                                  })()}
                                  {isClockInRec && (
                                    <div style={{ fontSize: 10, color: C.dim, marginTop: 2, lineHeight: 1.5 }}>
                                      Sched: <span style={{ color: C.muted }}>{rec.scheduledTime && !isMidnight(rec.scheduledTime) ? fmtDateTime(rec.scheduledTime) : "--"}</span>
                                      <br/>Timer start: <span style={{ color: C.text }}>{rec.timerStartTime && !isMidnight(rec.timerStartTime) ? fmtDateTime(rec.timerStartTime) : (rec.clockInTime && !isMidnight(rec.clockInTime) ? fmtDateTime(rec.clockInTime) : "--")}</span>
                                      {rec.wasEdited && (
                                        <><br/><span style={{ color: C.yellow }}>⚠ Declared {fmtDateTime(rec.clockInTime)} ({rec.declaredVsTimerMin > 0 ? `edited ${rec.declaredVsTimerMin}m earlier` : `edited ${Math.abs(rec.declaredVsTimerMin)}m later`})</span></>
                                      )}
                                      {rec.clockOutTime && !isMidnight(rec.clockOutTime)
                                        ? <><br/>Out: <span style={{ color: C.text }}>{fmtDateTime(rec.clockOutTime)}</span></>
                                        : ""}
                                      {rec.hours > 0 ? <><br/>{hrs(rec.hours)} logged</> : ""}
                                      {rec.jobNumber && (
                                        <>
                                          {" · "}
                                          <a
                                            href={`https://secure.getjobber.com/jobs?search=${encodeURIComponent(rec.jobNumber)}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            style={{ color: C.accent, textDecoration: "none" }}
                                            onClick={(e) => e.stopPropagation()}
                                          >
                                            View in Jobber →
                                          </a>
                                        </>
                                      )}
                                    </div>
                                  )}
                                  {isNoteRec && rec.noteText && (
                                    <div style={{ fontSize: 10, color: C.dim, marginTop: 2, fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                      {rec.noteText.length > 80 ? rec.noteText.substring(0, 80) + "..." : rec.noteText}
                                    </div>
                                  )}
                                  {isLateNoteRec && rec.noteCreatedAt && (
                                    <div style={{ fontSize: 10, color: C.dim, marginTop: 2 }}>
                                      Note added: {rec.noteCreatedAt.toLocaleDateString("en-CA", { month: "short", day: "numeric" })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                  {board.entries.length === 0 && (
                    <div style={{ textAlign: "center", padding: "16px 0", color: C.muted, fontSize: 12 }}>No data</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </>
    );
  }

  // ── Sections mode (composite leaderboard drill-down) ──────────────────────
  if (isSections) {
    return (
      <>
        <div
          onClick={onClose}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
            zIndex: 1000, backdropFilter: "blur(2px)"
          }}
        />
        <div style={{
          position: "fixed", top: 0, right: 0, bottom: 0,
          width: "min(520px, 100vw)",
          background: C.bg, borderLeft: `1px solid ${C.border}`,
          zIndex: 1001, display: "flex", flexDirection: "column",
          boxShadow: "-8px 0 32px rgba(0,0,0,0.5)"
        }}>
          <div style={{
            padding: "16px 20px", borderBottom: `1px solid ${C.border}`,
            display: "flex", alignItems: "flex-start", gap: 12, flexShrink: 0
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>{title}</div>
              {techName && (
                <div style={{ fontSize: 12, color: C.accent, marginTop: 2 }}>{techName}</div>
              )}
              {subMetrics && (
                <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                  {subMetrics.map((sm, i) => (
                    <div key={i} style={{ background: `${C.border}30`, borderRadius: 6, padding: "4px 8px" }}>
                      <span style={{ fontSize: 10, color: C.muted }}>{sm.label}: </span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: sm.color }}>{sm.value}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <button
              onClick={onClose}
              style={{
                background: "none", border: "none", color: C.muted,
                cursor: "pointer", fontSize: 22, lineHeight: 1, padding: 4, flexShrink: 0
              }}
            >×</button>
          </div>
          <div style={{ flex: 1, overflowY: "auto", padding: "12px 20px" }}>
            {sections.map((sec, si) => (
              <div key={si} style={{ marginBottom: 28 }}>
                <div style={{
                  display: "flex", alignItems: "center", gap: 10,
                  marginBottom: 10, paddingBottom: 8, borderBottom: `1px solid ${C.border}`
                }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.accent, textTransform: "uppercase", letterSpacing: "0.06em", flex: 1 }}>
                    {sec.title}
                  </div>
                  {sec.subtitle && (
                    <div style={{ fontSize: 13, fontWeight: 700, color: sec.subtitleColor || C.muted }}>{sec.subtitle}</div>
                  )}
                  <div style={{ fontSize: 11, color: C.dim, marginLeft: 8 }}>{sec.records.length} records</div>
                </div>
                {sec.records.length === 0
                  ? <div style={{ textAlign: "center", padding: "16px 0", color: C.muted, fontSize: 12 }}>No records</div>
                  : sec.records.map((r, i) => renderRecord(sec.type, r, i))
                }
              </div>
            ))}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)",
          zIndex: 1000, backdropFilter: "blur(2px)"
        }}
      />
      {/* Drawer */}
      <div style={{
        position: "fixed", top: 0, right: 0, bottom: 0,
        width: "min(520px, 100vw)",
        background: C.bg, borderLeft: `1px solid ${C.border}`,
        zIndex: 1001, display: "flex", flexDirection: "column",
        boxShadow: "-8px 0 32px rgba(0,0,0,0.5)"
      }}>
        {/* Header */}
        <div style={{
          padding: "16px 20px", borderBottom: `1px solid ${C.border}`,
          display: "flex", alignItems: "flex-start", gap: 12, flexShrink: 0
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: C.text }}>{title}</div>
            {techName && (
              <div style={{ fontSize: 12, color: C.accent, marginTop: 2 }}>{techName}</div>
            )}
            {subMetrics && (
              <div style={{ display: "flex", gap: 8, marginTop: 6, flexWrap: "wrap" }}>
                {subMetrics.map((sm, i) => (
                  <div key={i} style={{ background: `${C.border}30`, borderRadius: 6, padding: "4px 8px" }}>
                    <span style={{ fontSize: 10, color: C.muted }}>{sm.label}: </span>
                    <span style={{ fontSize: 12, fontWeight: 700, color: sm.color }}>{sm.value}</span>
                  </div>
                ))}
              </div>
            )}
            <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
              <span style={{ color: hitColor }}>{hitCount} {hitLabel}</span>
              {type !== "total_notes" && type !== "review_stars" && type !== "name_mentions" && (
                <>{" · "}<span style={{ color: missColor }}>{missCount} {missLabel}</span></>
              )}
              {" · "}
              {records.length} total
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: "none", border: "none", color: C.muted,
              cursor: "pointer", fontSize: 22, lineHeight: 1, padding: 4, flexShrink: 0
            }}
          >×</button>
        </div>

        {/* Filter tabs */}
        <div style={{
          padding: "10px 20px", borderBottom: `1px solid ${C.border}`,
          display: "flex", gap: 8, flexShrink: 0
        }}>
          {[
            { key: "all",  label: "All" },
            { key: "hit",  label: type === "notes" ? "With Notes" : type === "job_close" ? "Same Day" : type === "review_stars" ? "Stars" : type === "name_mentions" ? "Mentions" : type === "bonus_rows" ? "Qualifying" : "On Time" },
            ...(type !== "review_stars" && type !== "name_mentions" ? [{ key: "miss", label: type === "notes" ? "Missing" : type === "job_close" ? "Not Same-Day" : type === "bonus_rows" ? "Below Threshold" : "Late" }] : []),
          ].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setFilter(key)}
              style={{
                padding: "6px 14px", borderRadius: 6, border: "none", cursor: "pointer",
                fontSize: 12, fontWeight: 600,
                background: filter === key ? C.accent : C.surface,
                color: filter === key ? "#0d0f12" : C.muted,
              }}
            >{label}</button>
          ))}
          <div style={{ marginLeft: "auto", fontSize: 12, color: C.muted, alignSelf: "center" }}>
            {filtered.length} records
          </div>
        </div>

        {/* Manual add-miss (admin, discipline-metric drawers for one tech) */}
        {data.metric && techName && canEditMetricOverrides && onAddMiss && (
          <div style={{ padding: "8px 20px", borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
            <AddMissForm metric={data.metric} techName={techName} onAddMiss={onAddMiss} />
          </div>
        )}

        {/* Records list */}
        <div style={{ flex: 1, overflowY: "auto", padding: "12px 20px" }}>
          {filtered.length === 0 && (
            <div style={{ textAlign: "center", padding: 40, color: C.muted, fontSize: 13 }}>
              No records
            </div>
          )}
          {filtered.map((r, i) => renderRecord(type, r, i))}
        </div>
      </div>
    </>
  );
}

const selectStyle = {
  padding: "8px 12px", borderRadius: 8,
  border: `1px solid ${C.border}`, background: C.surface, color: C.text,
  fontSize: 13, cursor: "pointer", minWidth: 130
};

// ═════════════════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═════════════════════════════════════════════════════════════════════════════

// Session-scoped cache of the last full data load (~10MB across 6 tables).
// Re-opening the page paints instantly from this while a silent background
// refresh replaces it — the raw tables change slowly (sync crons), so a few
// seconds of staleness is invisible, and any in-page mutation writes to the
// DB first so the refresh can only converge, never revert.
let crewPerfDataCache = null; // { raw, ts }

export default function CrewPerformance() {
  const { isAdmin, hasPermission, jobberEmployeeName } = usePermissions();
  const { session } = useSession();
  const sessionEmail = session?.user?.email?.toLowerCase() || "";
  // Bonus $ visibility: admins always see all; crew see only their own row
  // unless given `view_all_bonus_amounts`. The avg-margin % and job counts
  // are visible to everyone.
  const canSeeAllBonusAmounts = isAdmin || hasPermission("view_all_bonus_amounts");
  const ownTechFirst = firstName(jobberEmployeeName || "");
  function canSeeBonusFor(techNameOrFirst) {
    if (canSeeAllBonusAmounts) return true;
    if (!ownTechFirst) return false;
    return firstName(techNameOrFirst || "") === ownTechFirst;
  }

  const [jobs, setJobs] = useState([]);
  const [timesheets, setTimesheets] = useState([]);
  const [visits, setVisits] = useState([]);
  const [notes, setNotes] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [invoices, setInvoices] = useState([]);
  // jobber_invoices — TRUE issuedDate, used only to date referral payouts.
  const [jobberInvoices, setJobberInvoices] = useState([]);
  const [assessmentRequests, setAssessmentRequests] = useState([]); // jb_requests rows with a scheduled assessment
  const [metricOverrides, setMetricOverrides] = useState([]);       // manual excuse/add rows for discipline metrics
  const [jobberUsers, setJobberUsers] = useState([]);            // [{id, name, role, ...}]
  const [profitBonusOverrides, setProfitBonusOverrides] = useState({}); // (jobNum|techFirst) → row
  const [excludedJobs, setExcludedJobs] = useState(() => new Set()); // job_nums manually removed from bonuses
  const [profitBonusSettings, setProfitBonusSettings] = useState({
    lead_pct: DEFAULT_LEAD_PCT,
    crew_pct: DEFAULT_CREW_PCT,
    revenue_threshold: DEFAULT_REV_MIN,
    margin_threshold: DEFAULT_MARGIN_MIN,
  });
  const [reviewBonusSettings, setReviewBonusSettings] = useState({
    base:        DEFAULT_REVIEW_BONUS_BASE,
    mention:     DEFAULT_REVIEW_BONUS_MENTION,
    monthly_cap: DEFAULT_REVIEW_BONUS_MONTHLY_CAP,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [syncingGoogle, setSyncingGoogle] = useState(false);
  const [googleSyncResult, setGoogleSyncResult] = useState(null);

  // Auto-dismiss Google sync banner after 20 s
  useEffect(() => {
    if (!googleSyncResult) return;
    const t = setTimeout(() => setGoogleSyncResult(null), 20000);
    return () => clearTimeout(t);
  }, [googleSyncResult]);

  // Filters
  const [techFilter, setTechFilter] = useState("all");
  // Land on the current month by default (Dylan 2026-07-22) — "All" stays one
  // click away in the picker.
  const [datePreset, setDatePreset] = useState("this_month");
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");

  // Tab — single source of truth for the segmented control at the top of
  // the page. To add a new tab, append one entry to availableTabs below
  // (filter by permission inline) and add a `tab === "<id>"` content
  // branch further down. Step 2 will add a "Master Report" entry here
  // gated by view_master_crew_report.
  const [tab, setTab] = useState("dashboard");

  // Data-driven tab list. Memoized on the actual permission BOOLEANS so
  // the array reference is stable across renders (otherwise it would
  // recompute every render because hasPermission is a fresh closure).
  const canSeeDetails      = hasPermission("view_job_table");
  const canSeeBonuses      = hasPermission("view_bonus");
  const canSeeMasterReport = hasPermission("view_master_crew_report");
  const availableTabs = useMemo(() => {
    const tabs = [{ id: "dashboard", label: "Dashboard" }];
    if (canSeeDetails)      tabs.push({ id: "details", label: "Individual Details" });
    // Referrals is ungated beyond the page's own view_crew_metrics: the ranking
    // is meant to be seen by everyone (that is the point of a leaderboard) and
    // the dollar columns redact per-row via canSeeBonusFor, exactly like the
    // review-bonus board on the dashboard.
    tabs.push({ id: "referrals", label: "Referral Leaderboard" });
    if (canSeeBonuses)      tabs.push({ id: "bonuses", label: "Bonus Tracker" });
    if (canSeeMasterReport) tabs.push({ id: "report",  label: "Master Report" });
    return tabs;
  }, [canSeeDetails, canSeeBonuses, canSeeMasterReport]);

  // Safety net: if the current tab is no longer in availableTabs (e.g.
  // a permission was revoked mid-session), fall back to the first one.
  useEffect(() => {
    if (!availableTabs.some(t => t.id === tab)) {
      setTab(availableTabs[0].id);
    }
  }, [availableTabs, tab]);

  // Drill-down drawer
  const [drawerData, setDrawerData] = useState(null);

  // Sort for details table
  const [sortCol, setSortCol] = useState("closedDate");
  const [sortDir, setSortDir] = useState("desc");

  // ── Load data ──────────────────────────────────────────────────────────────
  // Applies one full load's worth of raw table data to component state. Split
  // out of loadData so a cached payload (module-level crewPerfDataCache) can
  // hydrate the page instantly on re-open while a background refresh runs.
  const applyLoadedData = useCallback((raw) => {
    const { jobsAll, tsAll, visitAll, notesAll, reviewsAll, invAll, jobberInvAll, assessAll, metricOvAll, settingsRes, overridesRes, usersRes } = raw;

    const normalised = (jobsAll || []).map(j => ({
      ...j,
      _closedDate: parseDate(j.closed_date),
      _leadTech: j.lead_tech ? j.lead_tech.trim() : null,
    }));

    setJobs(normalised);
    setTimesheets(tsAll || []);
    setVisits(visitAll || []);
    setNotes(notesAll || []);
    setReviews(reviewsAll || []);
    setInvoices(invAll || []);
    setJobberInvoices(jobberInvAll || []);
    setAssessmentRequests(assessAll || []);
    setMetricOverrides(metricOvAll || []);
    setJobberUsers(usersRes?.data || []);

    // Profit-bonus settings — fall back to defaults if app_settings is missing keys.
    const settingsMap = {};
    for (const r of (settingsRes?.data || [])) {
      const v = r.value;
      const n = typeof v === "number" ? v : typeof v === "string" ? Number(v) : null;
      if (n != null && !isNaN(n)) settingsMap[r.key] = n;
    }
    setProfitBonusSettings({
      lead_pct:          settingsMap.profit_bonus_lead_pct          ?? DEFAULT_LEAD_PCT,
      crew_pct:          settingsMap.profit_bonus_crew_pct          ?? DEFAULT_CREW_PCT,
      revenue_threshold: settingsMap.profit_bonus_revenue_threshold ?? DEFAULT_REV_MIN,
      margin_threshold:  settingsMap.profit_bonus_margin_threshold  ?? DEFAULT_MARGIN_MIN,
    });
    setReviewBonusSettings({
      base:        settingsMap.review_bonus_base        ?? DEFAULT_REVIEW_BONUS_BASE,
      mention:     settingsMap.review_bonus_mention     ?? DEFAULT_REVIEW_BONUS_MENTION,
      monthly_cap: settingsMap.review_bonus_monthly_cap ?? DEFAULT_REVIEW_BONUS_MONTHLY_CAP,
    });

    // Manually-excluded jobs — stored as a JSONB array under one key.
    const exRow = (settingsRes?.data || []).find(r => r.key === "profit_bonus_excluded_jobs");
    const exArr = Array.isArray(exRow?.value) ? exRow.value : [];
    setExcludedJobs(new Set(exArr.map(v => String(v))));

    // Per-(job, tech_first) overrides keyed by "<jobNum>|<techFirst>".
    const ovMap = {};
    (overridesRes?.data || []).forEach(o => {
      if (!o.job_num || !o.tech_first) return;
      ovMap[`${o.job_num}|${o.tech_first}`] = o;
    });
    setProfitBonusOverrides(ovMap);
  }, []);

  const loadData = useCallback(async (opts = {}) => {
    // background = silent refresh behind a cache-hydrated page: no spinner,
    // and a failure keeps showing the cached data instead of an error page.
    const background = !!opts.background;
    if (!background) setLoading(true);
    setError(null);
    try {
      // Supabase REST silently caps each response at 1000 rows, even with
      // .range(0, 9999). Paginate any table we expect to grow past 1000.
      // Without this we were missing >85% of timesheets, breaking the
      // bonus-row attribution for Jan/Feb jobs.
      // Page fetches run in parallel batches — the sequential version made
      // opening this page cost ~9 serial round-trips on timesheets alone.
      async function fetchAll(builder, pageSize = 1000) {
        const MAX_ROWS = 50000;
        // One page, with one retry. The retry is NOT superstition: it is the
        // difference between a blank Crew tab and a slow one when a single page
        // loses a race with a cron.
        const page = async (from) => {
          let p = await builder().range(from, from + pageSize - 1);
          if (p.error) {
            await new Promise((r) => setTimeout(r, 250));
            p = await builder().range(from, from + pageSize - 1);
            if (p.error) throw p.error;
          }
          return p.data || [];
        };

        // The FIRST page used to be the one call with no retry — and it lands in the
        // widest part of the burst, so it was the likeliest to fail with the least
        // protection. It goes through the same path as every other page now.
        let all = await page(0);
        if (all.length < pageSize) return all;

        // Why the burst stays small: /crew opens ~14 of these 1000-row page queries
        // at once. Each one is bounded by the `authenticated` role's 8 s
        // statement_timeout, so wall-clock contention is a real failure mode, not a
        // theoretical one.
        //
        // ⚠️ HISTORY, so nobody re-tunes BATCH a third time: this page has thrown
        // "canceling statement due to statement timeout" twice. Neither cause was
        // the client. 2026-07-20 was blamed on "6 concurrent OFFSET sorts" and
        // "missing sort indexes" (20260720_pagination_sort_indexes.sql) — that
        // diagnosis was never re-measured and was wrong. 2026-08-20 was a BARE
        // STABLE+SECURITY DEFINER call in the RLS policy being re-evaluated once per
        // scanned row (4990 ms vs 16.5 ms wrapped) — see security/buildRls.js.
        // If this page gets slow again, MEASURE THE SERVER before touching BATCH.
        const BATCH = 3;
        for (let from = pageSize; ; from += pageSize * BATCH) {
          const pages = await Promise.all(
            Array.from({ length: BATCH }, (_, i) => page(from + i * pageSize))
          );
          let done = false;
          for (const rows of pages) {
            // Consume the WHOLE batch even after a short page. These rows are already
            // fetched and paid for, and a short page mid-batch is not proof of
            // end-of-data while the sync crons are writing underneath us — the old
            // `break` here silently dropped rows it had in hand.
            all = all.concat(rows);
            if (rows.length < pageSize) done = true;
          }
          if (done) break;
          if (all.length >= MAX_ROWS) {
            // Used to `return all` truncated, with no error and no log — i.e. show
            // confidently wrong bonus numbers. Loud beats wrong on a payroll surface.
            throw new Error(
              `fetchAll: exceeded ${MAX_ROWS} rows without reaching the end of the table. ` +
                `Refusing to render partial data — this page's totals would be wrong.`
            );
          }
        }
        return all;
      }

      const [jobsAll, tsAll, visitAll, notesAll, reviewsAll, invAll, jobberInvAll, assessAll, metricOvAll, settingsRes, overridesRes, usersRes] = await Promise.all([
        fetchAll(() => supabase.from("jb_jobs").select("*").order("id", { ascending: false })),
        fetchAll(() => supabase.from("jobber_timesheets").select("*").order("start_at", { ascending: false })),
        fetchAll(() => supabase.from("jobber_visits").select("*").order("start_at", { ascending: false })),
        fetchAll(() => supabase.from("jobber_job_notes").select("*").order("created_at", { ascending: false })),
        fetchAll(() => supabase.from("jobber_reviews").select("*").order("created_at", { ascending: false })),
        // .order() is required, not cosmetic: OFFSET paging without an ORDER BY has
        // no cross-query ordering guarantee, so pages could overlap or skip rows.
        // job_nums/issued_date are for the referral engine's bill-month rule:
        // job_number is NULL on every jb_invoices row, so job_nums is the only
        // job link this table actually carries.
        fetchAll(() => supabase.from("jb_invoices").select("job_number,job_nums,issued_date,created_at,due_date,amounts_invoiced,amounts_outstanding,invoice_status").order("created_at", { ascending: false })),
        // Jobber's TRUE issuedDate — jb_invoices.issued_date is only the ingest
        // stamp, which lands a day late on invoices picked up after midnight and
        // moves referral money across a month boundary. Dating only.
        fetchAll(() => supabase.from("jobber_invoices").select("job_number,issued_date").order("issued_date", { ascending: false })),
        // Scheduled assessments (live from Jobber) — powers the Request
        // Timesheets missed-sign-in check.
        fetchAll(() => supabase.from("jb_requests")
          .select("jobber_id,client_name,request_title,status,assessment_start_at,assessment_complete,assessment_completed_at,assessment_assignees")
          .not("assessment_start_at", "is", null)
          .order("assessment_start_at", { ascending: false })),
        // Manual excuse/add overrides for the discipline metrics.
        fetchAll(() => supabase.from("metric_overrides").select("*").order("day", { ascending: false })),
        supabase.from("app_settings").select("key,value").in("key", [
          "profit_bonus_lead_pct", "profit_bonus_crew_pct",
          "profit_bonus_revenue_threshold", "profit_bonus_margin_threshold",
          "profit_bonus_excluded_jobs",
          "review_bonus_base", "review_bonus_mention", "review_bonus_monthly_cap",
        ]),
        supabase.from("profit_bonus_overrides").select("*"),
        supabase.from("jobber_users").select("id,name,role,is_active"),
      ]);

      const raw = { jobsAll, tsAll, visitAll, notesAll, reviewsAll, invAll, jobberInvAll, assessAll, metricOvAll, settingsRes, overridesRes, usersRes };
      crewPerfDataCache = { raw, ts: Date.now() };
      applyLoadedData(raw);
      setLastRefresh(new Date());
    } catch (e) {
      if (!background) setError(e.message || String(e));
      else console.warn("Background refresh failed:", e.message || e);
    } finally {
      if (!background) setLoading(false);
    }
  }, [applyLoadedData]);

  useEffect(() => {
    // Stale-while-revalidate: if a previous visit this session already loaded
    // everything, paint from that instantly and refresh silently behind it.
    if (crewPerfDataCache) {
      applyLoadedData(crewPerfDataCache.raw);
      setLoading(false);
      setLastRefresh(new Date(crewPerfDataCache.ts));
      loadData({ background: true });
    } else {
      loadData();
    }
  }, [loadData, applyLoadedData]);

  // ── Update review assignment (manual credit override) ─────────────────────
  const handleUpdateReview = useCallback(async (id, assignedTechs, assignedJob) => {
    const updates = { assigned_techs: assignedTechs };
    if (assignedJob !== undefined) updates.assigned_job = assignedJob ?? null;
    await supabase.from("jobber_reviews").update(updates).eq("id", id);
    setReviews(prev => prev.map(r => r.id === id ? { ...r, assigned_techs: assignedTechs, ...(assignedJob !== undefined ? { assigned_job: assignedJob ?? null } : {}) } : r));
    setDrawerData(prev => prev ? {
      ...prev,
      records: prev.records.map(rec => rec.id === id ? { ...rec, assignedTechs, ...(assignedJob !== undefined ? { assignedJob: assignedJob ?? null } : {}) } : rec),
    } : prev);
  }, []);

  // ── Manual mention credit (admin: auto-scan missed a guy) ─────────────────
  // Stores canonical full names in jobber_reviews.manual_mentions; pays the
  // mention bonus exactly like a scanned mention on every surface.
  const handleUpdateMentions = useCallback(async (id, mentions) => {
    const value = mentions && mentions.length ? mentions : null;
    const { error } = await supabase.from("jobber_reviews").update({ manual_mentions: value }).eq("id", id);
    if (error) {
      console.error("Failed to update manual mentions:", error);
      throw error;
    }
    setReviews(prev => prev.map(r => r.id === id ? { ...r, manual_mentions: value } : r));
    setDrawerData(prev => prev ? {
      ...prev,
      records: prev.records.map(rec => rec.id === id ? { ...rec, manualMentions: value } : rec),
    } : prev);
  }, []);

  // ── Ignore / restore a review (admin: "nobody was involved") ──────────────
  // Ignored reviews drop out of the unassigned queue AND all review credit /
  // bonus math (stars, mentions, review bonus) on every surface. Restorable
  // from the Ignored Reviews drawer.
  const handleIgnoreReview = useCallback(async (id, ignored) => {
    const updates = {
      ignored,
      ignored_at: ignored ? new Date().toISOString() : null,
      ignored_by: ignored ? sessionEmail : null,
    };
    const { error } = await supabase.from("jobber_reviews").update(updates).eq("id", id);
    if (error) {
      console.error("Failed to update review ignored flag:", error);
      throw error;
    }
    setReviews(prev => prev.map(r => r.id === id ? { ...r, ...updates } : r));
    // Drop it from the open drawer so the queue visibly shrinks.
    setDrawerData(prev => prev ? {
      ...prev,
      records: prev.records.filter(rec => rec.id !== id),
    } : prev);
  }, [sessionEmail]);

  // ── Manual metric overrides: excuse a miss / add one / undo ───────────────
  // Rows live in metric_overrides; the metric builders read them via
  // excuseMap/addMissRows below. Admin-only writes (RLS enforced too).
  const handleMetricOverride = useCallback(async (kind, ov) => {
    if (!ov) return;
    if (kind === "excuse") {
      const note = window.prompt("Excuse this miss — reason (optional):");
      if (note === null) return; // cancelled
      const row = {
        metric: ov.metric, tech_first: ov.techFirst, target_key: ov.targetKey || null,
        day: ov.day, action: "excuse", note: note.trim() || null, created_by: sessionEmail,
      };
      const { data, error } = await supabase.from("metric_overrides").insert(row).select();
      if (error) { window.alert(`Excuse failed: ${error.message}`); return; }
      setMetricOverrides(prev => [...prev, ...(data && data.length ? data : [row])]);
      setDrawerData(prev => prev ? {
        ...prev,
        records: prev.records.map(rec =>
          rec._ov && rec._ov.metric === ov.metric && rec._ov.techFirst === ov.techFirst
            && (rec._ov.targetKey || "") === (ov.targetKey || "") && rec._ov.day === ov.day
            ? { ...rec, excused: true, excuseNote: row.note }
            : rec),
      } : prev);
    } else if (kind === "unexcuse" || kind === "remove_add_miss") {
      const action = kind === "unexcuse" ? "excuse" : "add_miss";
      let q = supabase.from("metric_overrides").delete()
        .eq("metric", ov.metric).eq("tech_first", ov.techFirst).eq("day", ov.day).eq("action", action);
      q = ov.targetKey ? q.eq("target_key", ov.targetKey) : q.is("target_key", null);
      const { error } = await q;
      if (error) { window.alert(`Restore failed: ${error.message}`); return; }
      setMetricOverrides(prev => prev.filter(o =>
        !(o.metric === ov.metric && o.tech_first === ov.techFirst
          && (o.target_key || "") === (ov.targetKey || "") && o.day === ov.day && o.action === action)));
      setDrawerData(prev => prev ? {
        ...prev,
        records: action === "add_miss"
          ? prev.records.filter(rec => !(rec.manualMiss && rec._ov && rec._ov.metric === ov.metric
              && rec._ov.techFirst === ov.techFirst && (rec._ov.targetKey || "") === (ov.targetKey || "") && rec._ov.day === ov.day))
          : prev.records.map(rec =>
              rec._ov && rec._ov.metric === ov.metric && rec._ov.techFirst === ov.techFirst
                && (rec._ov.targetKey || "") === (ov.targetKey || "") && rec._ov.day === ov.day
                ? { ...rec, excused: false, excuseNote: null }
                : rec),
      } : prev);
    }
  }, [sessionEmail]);

  const handleAddMiss = useCallback(async (metric, techName, { day, targetKey, note }) => {
    const row = {
      metric, tech_first: firstName(techName), target_key: targetKey || null,
      day, action: "add_miss", note: note || null, created_by: sessionEmail,
    };
    const { data, error } = await supabase.from("metric_overrides").insert(row).select();
    if (error) throw error;
    setMetricOverrides(prev => [...prev, ...(data && data.length ? data : [row])]);
  }, [sessionEmail]);

  // ── Update visit closed_by (manual same-day attribution override) ─────────
  // closer = canonical full name from allTechNames, or null to clear override
  // and fall back to the assigned lead tech.
  const handleUpdateVisitCloser = useCallback(async (visitId, closer) => {
    const { error } = await supabase
      .from("jobber_visits")
      .update({ closed_by: closer ?? null })
      .eq("id", visitId);
    if (error) {
      // Surface schema/RLS failures instead of silently no-op'ing the UI.
      console.error("Failed to update visit closed_by:", error);
      throw error;
    }
    setVisits(prev => prev.map(v => v.id === visitId ? { ...v, closed_by: closer ?? null } : v));
    setDrawerData(prev => prev ? {
      ...prev,
      records: prev.records.map(rec => rec.id === visitId ? { ...rec, closedBy: closer ?? null } : rec),
    } : prev);
  }, []);

  // ── Sync from Jobber ──────────────────────────────────────────────────────
  const runSync = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await apiFetch("/api/jobber-sync", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "syncAllCrewData", days: 14 })
      });
      const data = await res.json();
      setSyncResult(data);
      if (data.ok) {
        try {
          await apiFetch("/api/jobber-sync", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ action: "matchReviews" })
          });
        } catch (_) {}
        await loadData();
      }
    } catch (e) {
      setSyncResult({ error: e.message });
    } finally {
      setSyncing(false);
    }
  };

  // ── Sync Google reviews (via Outscraper; will move to GBP API once approved) ─
  const runGoogleSync = async () => {
    setSyncingGoogle(true);
    setGoogleSyncResult(null);
    try {
      const res = await apiFetch("/api/google-reviews-sync", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "sync" })
      });
      const data = await res.json();
      setGoogleSyncResult(data);
      if (data.ok) await loadData();
    } catch (e) {
      setGoogleSyncResult({ error: e.message });
    } finally {
      setSyncingGoogle(false);
    }
  };

  // (AI review matcher removed — see Marketing → Reviews tab for the new
  //  manual review→client assignment UI.)

  // ── Date range ────────────────────────────────────────────────────────────
  const dateRange = useMemo(() => {
    if (datePreset === "custom") {
      return {
        from: customFrom ? new Date(customFrom) : null,
        to: customTo ? new Date(customTo + "T23:59:59") : null
      };
    }
    return getDateRange(datePreset);
  }, [datePreset, customFrom, customTo]);

  // ── Manual metric-override lookups ────────────────────────────────────────
  // excuseMap: `${metric}|${techFirst}|${targetKey||""}|${day}` → reason note.
  // addMissRows: admin-added misses, injected into the metric builders.
  const excuseMap = useMemo(() => {
    const m = new Map();
    for (const o of metricOverrides) {
      if (o.action === "excuse") m.set(`${o.metric}|${o.tech_first}|${o.target_key || ""}|${o.day}`, o.note || "");
    }
    return m;
  }, [metricOverrides]);
  const addMissRows = useMemo(() => metricOverrides.filter(o => o.action === "add_miss"), [metricOverrides]);

  // ── Build notes lookup: Map<job_number, Array<{techNorm, techFirst, createdAt, message}>> ──
  // Stores all notes with dates so visit matching can be date-aware.
  const notesLookup = useMemo(() => {
    const map = new Map();
    notes.forEach(n => {
      // A note counts as documentation if it has text OR ≥1 photo/file
      // attachment — a photo-only note has empty message but is still a writeup.
      const hasContent = n.message?.trim() || Number(n.attachment_count) > 0;
      if (!n.job_number || !n.created_by_name || !hasContent) return;
      const key = String(n.job_number);
      if (!map.has(key)) map.set(key, []);
      map.get(key).push({
        techNorm: normName(n.created_by_name),
        techFirst: firstName(n.created_by_name),
        createdAt: parseDate(n.created_at),
        message: (n.message || "").trim() || (Number(n.attachment_count) > 0 ? `[${n.attachment_count} photo/attachment]` : ""),
        // "timesheet" = synthetic row bridged from a Jobber timesheet note —
        // counts identically, but the UI badges it so provenance is visible.
        noteType: n.note_type || "job",
      });
    });
    return map;
  }, [notes]);

  // ── Jobs lookup by job_number → client_name ───────────────────────────────
  const jobsLookupByJobNum = useMemo(() => {
    const map = new Map();
    jobs.forEach(j => {
      if (j.job_num != null) map.set(String(j.job_num), j.client_name || null);
    });
    return map;
  }, [jobs]);

  // ── Latest timesheet start_at per job (for job-close deadline calc) ───────
  const lastTimesheetByJobNum = useMemo(() => {
    const map = new Map();
    timesheets.forEach(ts => {
      if (!ts.job_number) return;
      const jNum = String(ts.job_number);
      const d = parseDate(ts.start_at);
      if (!d) return;
      if (!map.has(jNum) || d > map.get(jNum)) map.set(jNum, d);
    });
    return map;
  }, [timesheets]);

  // ── Visit start_at lookup: "${jobNum}|${yyyy}-${m}-${d}" → ISO start_at ──
  const visitStartByJobDate = useMemo(() => {
    const map = new Map();
    visits.forEach(v => {
      if (!v.job_number || !v.start_at) return;
      const d = parseDate(v.start_at);
      if (!d) return;
      const key = `${String(v.job_number)}|${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map.has(key)) map.set(key, v.start_at);
    });
    return map;
  }, [visits]);

  // ── Timesheet clock-in lookup: "${jobNum}|${techFirst}|${yyyy}-${m}-${d}" → ISO start_at ──
  const tsClockInByJobTechDate = useMemo(() => {
    const map = new Map();
    timesheets.forEach(ts => {
      if (!ts.job_number || !ts.user_name || !ts.start_at) return;
      const d = parseDate(ts.start_at);
      if (!d) return;
      const key = `${String(ts.job_number)}|${firstName(ts.user_name)}|${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
      if (!map.has(key)) map.set(key, ts.start_at);
    });
    return map;
  }, [timesheets]);

  // Hours per (job, tech, day) + the day's top worker on each job. Drives the
  // "did they actually work it" gate on the notes and visit-completion metrics.
  const paperworkHours = useMemo(() => buildPaperworkHoursIndex(timesheets), [timesheets]);

  // ── Canonical name map: lowercase first-name → full name ─────────────────
  // Sourced from ACTIVE jobber_users only. This is the single source of truth
  // for which techs appear on the Crew dashboard:
  //   - Inactive employees (Spencer, etc.) are dropped because their first
  //     name has no entry here.
  //   - Single-name timesheets ("Kyle") and full-name timesheets
  //     ("Kyle Shaefer") collapse onto the same canonical full name.
  // If two active users share a first name, prefer the longer (more complete)
  // full name.
  const canonicalNameByFirst = useMemo(() => {
    const out = new Map();
    jobberUsers.forEach(u => {
      if (u.is_active === false) return;
      const full = (u.name || "").trim().replace(/\s+/g, " ");
      if (!full) return;
      // Shared/agency logins ("Taylor & Mallory") aren't people — without
      // this they collide with Taylor Priegnitz on first name and the
      // leaderboards randomly display the agency account name.
      if (full.includes("&")) return;
      const f = firstName(full);
      if (!f) return;
      const existing = out.get(f);
      if (!existing || full.length > existing.length) out.set(f, full);
    });
    return out;
  }, [jobberUsers]);

  // For raw user_name strings from timesheets/visits: returns the canonical
  // full name if the first name belongs to an active Jobber employee,
  // otherwise null (= filter this row out).
  function canonicalDisplayName(rawName) {
    const f = firstName(rawName);
    if (!f) return null;
    return canonicalNameByFirst.get(f) || null;
  }

  function canonicalName(first) {
    if (!first) return "";
    return canonicalNameByFirst.get(first.toLowerCase()) || (first[0].toUpperCase() + first.slice(1));
  }

  // ── All unique tech names ─────────────────────────────────────────────────
  // Source of truth: canonical full names from ACTIVE jobber_users. Any tech
  // that shows up in timesheets/visits but is no longer active in Jobber
  // (e.g. Spencer) is filtered out.
  const allTechNames = useMemo(() => {
    const set = new Set();
    canonicalNameByFirst.forEach((full) => {
      if (!isExcluded(full)) set.add(full);
    });
    return Array.from(set).sort();
  }, [canonicalNameByFirst]);

  // ════════════════════════════════════════════════════════════════════════
  // ENRICHED ARRAYS FOR THE MASTER REPORT
  // The Master Report wants flat, pre-resolved data so it can filter / sort
  // / search without depending on the date or canonical-name helpers in
  // this file. We build one enriched array per metric here.
  // ════════════════════════════════════════════════════════════════════════

  // ── Enriched timesheets ───────────────────────────────────────────────────
  // Skips inactive techs (canonical name lookup returns null) and excluded
  // techs.
  const enrichedTimesheets = useMemo(() => {
    return timesheets.reduce((acc, ts) => {
      if (isExcluded(ts.user_name)) return acc;
      const techName = canonicalDisplayName(ts.user_name);
      if (!techName) return acc; // inactive employee
      const startDate   = parseDate(ts.start_at);
      const endDate     = parseDate(ts.end_at);
      const createdDate = parseDate(ts.created_at);
      const jobNum      = ts.job_number ? String(ts.job_number) : null;
      const clientName  = jobNum ? (jobsLookupByJobNum.get(jobNum) || null) : null;
      const isOnTime    = (startDate && createdDate) ? sameCalendarDay(startDate, createdDate) : null;
      const daysLate    = (isOnTime === false && startDate && createdDate)
        ? Math.floor((createdDate.getTime() - startDate.getTime()) / 86400000)
        : 0;
      const isForgotSignOut =
        (ts.duration_hours != null && ts.duration_hours > 15) ||
        !!(startDate && endDate && !sameCalendarDay(startDate, endDate));
      acc.push({
        ...ts,
        _startDate: startDate,
        _endDate: endDate,
        _createdDate: createdDate,
        _techName: techName,
        _jobNum: jobNum,
        _clientName: clientName,
        _isOnTime: isOnTime,
        _daysLate: daysLate,
        _isForgotSignOut: isForgotSignOut,
      });
      return acc;
    }, []);
  }, [timesheets, canonicalNameByFirst, jobsLookupByJobNum]);

  // ── Enriched notes ────────────────────────────────────────────────────────
  // Each row carries the canonical tech name, client lookup, parsed
  // created date, and the trimmed note text. Skips notes whose author
  // first name isn't in active jobber_users (so e.g. departed employees
  // don't pollute the cross-tech notes table).
  const enrichedNotes = useMemo(() => {
    return notes.reduce((acc, n) => {
      if (!n.created_by_name || !n.message?.trim()) return acc;
      const techName = canonicalDisplayName(n.created_by_name);
      if (!techName) return acc;
      const createdDate = parseDate(n.created_at);
      const jobNum = n.job_number ? String(n.job_number) : null;
      const clientName = jobNum ? (jobsLookupByJobNum.get(jobNum) || null) : null;
      acc.push({
        ...n,
        _techName: techName,
        _createdDate: createdDate,
        _jobNum: jobNum,
        _clientName: clientName,
        _message: n.message.trim(),
      });
      return acc;
    }, []);
  }, [notes, canonicalNameByFirst, jobsLookupByJobNum]);

  // ── Enriched reviews ──────────────────────────────────────────────────────
  // Resolves the credited tech list (manual assigned_techs override wins
  // over auto_assigned_techs) into canonical full names, and looks up the
  // linked client name from the job number when available.
  const enrichedReviews = useMemo(() => {
    return reviews.map(r => {
      const createdDate = parseDate(r.created_at);
      const jobNum = r.job_number != null ? String(r.job_number) : null;
      const clientName = jobNum ? (jobsLookupByJobNum.get(jobNum) || null) : null;
      // Manual assigned_techs (admin override) wins; otherwise use auto_assigned_techs.
      const rawList = (Array.isArray(r.assigned_techs) && r.assigned_techs.length > 0)
        ? r.assigned_techs
        : (Array.isArray(r.auto_assigned_techs) ? r.auto_assigned_techs : []);
      const creditTechs = Array.from(new Set(
        rawList
          .map(t => canonicalDisplayName(t))
          .filter(Boolean)
      ));
      return {
        ...r,
        _createdDate: createdDate,
        _jobNum: jobNum,
        _clientName: clientName,
        _creditTechs: creditTechs,
        _hasComment: !!(r.comment && r.comment.trim()),
      };
    });
  }, [reviews, canonicalNameByFirst, jobsLookupByJobNum]);

  // ── Filter timesheets by date range + tech ────────────────────────────────
  const filteredTimesheets = useMemo(() => {
    let result = [...timesheets];
    if (techFilter !== "all") {
      const tf = normName(techFilter);
      result = result.filter(ts => normName(ts.user_name) === tf || firstName(ts.user_name) === tf);
    }
    if (dateRange.from) result = result.filter(ts => ts.start_at && new Date(ts.start_at) >= dateRange.from);
    if (dateRange.to) result = result.filter(ts => ts.start_at && new Date(ts.start_at) <= dateRange.to);
    return result;
  }, [timesheets, techFilter, dateRange]);

  // ── Filter completed visits by date range + tech ──────────────────────────
  const filteredVisits = useMemo(() => {
    let result = visits.filter(v => v.is_complete);
    if (techFilter !== "all") {
      const tf = normName(techFilter);
      result = result.filter(v =>
        (v.assigned_users || []).some(u => normName(u.name) === tf || firstName(u.name) === tf)
      );
    }
    if (dateRange.from) result = result.filter(v => v.completed_at && new Date(v.completed_at) >= dateRange.from);
    if (dateRange.to) result = result.filter(v => v.completed_at && new Date(v.completed_at) <= dateRange.to);
    return result;
  }, [visits, techFilter, dateRange]);

  // ── Filter jobs by date range + tech ──────────────────────────────────────
  const filteredJobs = useMemo(() => {
    let result = [...jobs];
    if (techFilter !== "all") {
      const tf = firstName(techFilter);
      result = result.filter(j => j._leadTech && firstName(j._leadTech) === tf);
    }
    if (dateRange.from) result = result.filter(j => j._closedDate && j._closedDate >= dateRange.from);
    if (dateRange.to) result = result.filter(j => j._closedDate && j._closedDate <= dateRange.to);
    return result;
  }, [jobs, techFilter, dateRange]);

  // ── Lead tech lookup: job_number → lead tech first name (from jb_jobs.lead_tech) ──
  const jobLeadTechMap = useMemo(() => {
    const map = new Map();
    jobs.forEach(j => {
      const jNum = String(j.job_num || j.job_number || "");
      if (!jNum || !j._leadTech) return;
      map.set(jNum, firstName(j._leadTech));
    });
    return map;
  }, [jobs]);

  // ── Fallback lead tech: job_number → journeyman with most hours ───────────
  const jobTopJourneymanMap = useMemo(() => {
    const map = new Map(); // jobNum → { techFirst → hours }
    timesheets.forEach(ts => {
      if (!ts.job_number || !ts.user_name) return;
      const techFirst = firstName(ts.user_name);
      if (!JOURNEYMEN_ONLY.includes(techFirst)) return;
      const jNum = String(ts.job_number);
      if (!map.has(jNum)) map.set(jNum, {});
      map.get(jNum)[techFirst] = (map.get(jNum)[techFirst] || 0) + (ts.duration_hours || 0);
    });
    // Resolve to top journeyman per job
    const resolved = new Map();
    map.forEach((hoursByTech, jNum) => {
      const top = Object.entries(hoursByTech).sort((a, b) => b[1] - a[1])[0];
      if (top) resolved.set(jNum, top[0]);
    });
    return resolved;
  }, [timesheets]);

  // ═════════════════════════════════════════════════════════════════════════
  // METRIC 1: Same-Day Timesheet Rate
  // Rules: exclude travel entries, exclude general clock-ins (no job),
  //        dedup to 1 entry per (tech, job, calendar day).
  // ═════════════════════════════════════════════════════════════════════════
  // Same-day engine shared by the Job and Request timesheet leaderboards. The
  // rule is identical for both — dedup to one entry per (tech, target, day),
  // skip travel, on-time iff every non-travel period was created on the same
  // calendar day as its own start_at. Only the target identity and the
  // client/scheduled-time lookups differ, supplied via opts.
  //   kind          "job" | "request" | "general" — drives whether record.jobNumber is set
  //   targetKeyOf   ts => grouping key (job number / request id) or null to skip
  //   titleOf       ts => display title
  //   clientNameOf  (key, ts) => client name or null
  //   scheduledRawOf (key, dateKey) => scheduled-start ISO or null
  //   metricName    metric_overrides key — enables manual excuse/add handling
  const buildTimesheetMetrics = (entries, opts) => {
    const { kind, targetKeyOf, titleOf, clientNameOf, scheduledRawOf, metricName } = opts;
    let onTime = 0;
    let late = 0;
    let unknown = 0;
    const byTech = {};
    const records = [];

    const isTravelLabel = (label) => {
      const s = (label || "").toLowerCase();
      return s.includes("travel") || s.includes("drive") || s.includes("driving");
    };

    // First pass: group ALL entries (incl. travel) by (tech, target, calendar day)
    // so each record can show every clock-in period for that day.
    const tsByDayKey = new Map();
    entries.forEach(ts => {
      if (isExcluded(ts.user_name)) return;
      const targetKey = targetKeyOf(ts);
      if (!targetKey) return;
      const techKey = canonicalDisplayName(ts.user_name);
      if (!techKey) return;
      const startDate = parseDate(ts.start_at);
      if (!startDate) return;
      const k = `${techKey}|${targetKey}|${startDate.getFullYear()}-${startDate.getMonth()}-${startDate.getDate()}`;
      if (!tsByDayKey.has(k)) tsByDayKey.set(k, []);
      tsByDayKey.get(k).push(ts);
    });

    // Second pass: dedup non-travel entries to count one record per (tech, target, day)
    const seenDedupKey = new Set();
    entries.forEach(ts => {
      if (isExcluded(ts.user_name)) return;
      const targetKey = targetKeyOf(ts);
      // Skip clock-ins not tied to this target type
      if (!targetKey) return;
      // Skip travel time entries (Jobber label stored as job_title)
      if (isTravelLabel(ts.job_title)) return;

      const techKey = canonicalDisplayName(ts.user_name);
      if (!techKey) return; // inactive or unknown employee — drop
      if (!byTech[techKey]) byTech[techKey] = { onTime: 0, late: 0, unknown: 0, totalHours: 0, records: [] };
      byTech[techKey].totalHours += ts.duration_hours || 0;

      const startDate = parseDate(ts.start_at);
      const createdDate = parseDate(ts.created_at);

      if (!startDate || !createdDate) {
        unknown++;
        byTech[techKey].unknown++;
        return;
      }

      // Dedup: count only 1 entry per (tech, target, calendar day)
      const dedupKey = `${techKey}|${targetKey}|${startDate.getFullYear()}-${startDate.getMonth()}-${startDate.getDate()}`;
      if (seenDedupKey.has(dedupKey)) return;
      seenDedupKey.add(dedupKey);

      const dateKey = `${startDate.getFullYear()}-${startDate.getMonth()}-${startDate.getDate()}`;

      // All clock-in periods for this (tech, target, day), sorted earliest → latest.
      // Includes travel so the user sees the full picture (travel there, work, travel home).
      const periods = (tsByDayKey.get(dedupKey) || [])
        .map(t => ({
          start: parseDate(t.start_at),
          end: parseDate(t.end_at),
          hours: t.duration_hours || 0,
          label: t.job_title || null,
          isTravel: isTravelLabel(t.job_title),
          // Jobber's createdAt = the moment this row was first written to Jobber's
          // DB. Immutable across edits — taps to "Start" set both start_at and
          // created_at to "now"; later edits to start_at don't move created_at.
          // So if created_at is on a different calendar day from start_at, the
          // tech backdated the entry.
          created: parseDate(t.created_at),
        }))
        .filter(p => p.start)
        .sort((a, b) => a.start.getTime() - b.start.getTime());
      const totalDayHours = periods.reduce((s, p) => s + (p.hours || 0), 0);
      // Earliest start of the day represents when the tech actually started working
      const earliestStart = periods[0]?.start || startDate;
      const latestEnd     = periods[periods.length - 1]?.end || parseDate(ts.end_at) || null;
      // Latest createdAt across all periods — when the day's data was finally
      // fully logged. If this is later than earliestStart's calendar day, at
      // least one entry was backdated.
      const latestEntered = periods.reduce((max, p) => {
        if (!p.created) return max;
        if (!max || p.created.getTime() > max.getTime()) return p.created;
        return max;
      }, null) || createdDate;

      // On-time = EVERY non-travel period was created on the same calendar day
      // as its own start_at. The dedup-winning row's createdAt alone is unreliable
      // when a day has multiple periods: entries are sorted DESC by start_at,
      // so a real-time afternoon clock-in is iterated first, wins the dedup, and
      // would mask a backdated morning period of the same day. Check every period
      // instead. (Travel auto-creates with the work row and shouldn't affect this;
      // fall back to all periods if no non-travel rows exist.)
      const nonTravelPeriods = periods.filter(p => !p.isTravel);
      const periodsForCheck = nonTravelPeriods.length > 0 ? nonTravelPeriods : periods;
      const isOnTime = periodsForCheck.every(p =>
        p.created && p.start && sameCalendarDay(p.start, p.created)
      );

      // Manual excuse: record stays visible in the drawer but counts for
      // nothing (neither hit nor miss) on the board or in bonus deductions.
      const ovKey = metricName ? `${metricName}|${firstName(techKey)}|${targetKey}|${localDayISO(startDate)}` : null;
      const excused = ovKey != null && excuseMap.has(ovKey);
      if (excused) {
        // no counters
      } else if (isOnTime) {
        onTime++;
        byTech[techKey].onTime++;
      } else {
        late++;
        byTech[techKey].late++;
      }

      const scheduledRaw = scheduledRawOf ? scheduledRawOf(targetKey, dateKey) : null;
      const rec = {
        tech: techKey,
        date: startDate,
        jobTitle: titleOf(ts) || null,
        jobNumber: kind === "job" ? targetKey : null,
        hours: ts.duration_hours || 0,
        onTime: isOnTime,
        excused,
        excuseNote: excused ? excuseMap.get(ovKey) : null,
        _ov: metricName ? { metric: metricName, techFirst: firstName(techKey), targetKey, day: localDayISO(startDate) } : null,
        startAt: ts.start_at,
        createdAt: ts.created_at,
        latestEntered,
        clientName: clientNameOf ? clientNameOf(targetKey, ts) : null,
        clockInTime: earliestStart,
        clockOutTime: latestEnd,
        scheduledTime: parseDate(scheduledRaw || null),
        periods,
        totalDayHours,
      };
      records.push(rec);
      byTech[techKey].records.push(rec);
    });

    // Admin-added misses the automation didn't catch (metric_overrides
    // action=add_miss) — count as late and show in the drawer.
    if (metricName) {
      addMissRows.forEach(o => {
        if (o.metric !== metricName) return;
        const d = parseDate(`${o.day}T12:00:00`); // noon local — immune to TZ day-shift
        if (!d) return;
        if (dateRange.from && d < dateRange.from) return;
        if (dateRange.to && d > dateRange.to) return;
        const techKey = canonicalDisplayName(o.tech_first);
        if (!techKey) return;
        if (techFilter !== "all" && normName(techKey) !== normName(techFilter) && firstName(techKey) !== firstName(techFilter)) return;
        if (!byTech[techKey]) byTech[techKey] = { onTime: 0, late: 0, unknown: 0, totalHours: 0, records: [] };
        byTech[techKey].late++;
        late++;
        const rec = {
          tech: techKey,
          date: d,
          jobTitle: o.note ? `Manual miss — ${o.note}` : "Manual miss (admin-added)",
          jobNumber: kind === "job" && o.target_key ? o.target_key : null,
          hours: 0,
          onTime: false,
          manualMiss: true,
          _ov: { metric: metricName, techFirst: o.tech_first, targetKey: o.target_key || "", day: o.day },
          periods: [],
          totalDayHours: 0,
        };
        records.push(rec);
        byTech[techKey].records.push(rec);
      });
    }

    records.sort((a, b) => (b.date?.getTime() || 0) - (a.date?.getTime() || 0));

    const total = onTime + late;
    const rate = total > 0 ? (onTime / total) * 100 : null;

    return { onTime, late, unknown, total, rate, byTech, records };
  };

  // Job Timesheets — entries tracked against a Job (Visit/job_number).
  const timesheetMetrics = useMemo(() => buildTimesheetMetrics(filteredTimesheets, {
    kind: "job",
    targetKeyOf: ts => (ts.job_number ? String(ts.job_number) : null),
    titleOf: ts => ts.job_title || ts.title || null,
    clientNameOf: (key) => jobsLookupByJobNum.get(key) || null,
    scheduledRawOf: (key, dateKey) => visitStartByJobDate.get(`${key}|${dateKey}`) || null,
    metricName: "timesheet",
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [filteredTimesheets, jobsLookupByJobNum, visitStartByJobDate, canonicalNameByFirst, excuseMap, addMissRows]);

  // Request Timesheets — entries tracked against a Request's assessment.
  // Same rules; keyed on request_id with the request's own title/client.
  // PLUS missed sign-ins: a COMPLETED assessment (live from Jobber — moved
  // dates re-sync to the new date, unscheduled ones null out) whose assigned
  // tech logged NO request time that calendar day counts as a miss. Without
  // this the board only graded entries that exist, so a tech who never signed
  // in at all read as 100%.
  const requestTimesheetMetrics = useMemo(() => {
    const base = buildTimesheetMetrics(filteredTimesheets, {
      kind: "request",
      targetKeyOf: ts => (ts.request_id ? String(ts.request_id) : null),
      titleOf: ts => ts.request_title || ts.job_title || null,
      clientNameOf: (key, ts) => ts.request_client || null,
      scheduledRawOf: null,
      metricName: "request_timesheet",
    });

    // Request time tracking went live 2026-06-01 — assessments before that
    // predate the practice and would all read as misses.
    const ASSESS_SIGNIN_FLOOR = new Date(2026, 5, 1);
    const now = new Date();

    // (techFirst|requestGid|calendar day) → tech signed into THAT request that
    // day. Built from the UNfiltered-by-target timesheet set (travel included —
    // any entry on the request counts as a sign-in).
    const signedIn = new Set();
    filteredTimesheets.forEach(ts => {
      if (!ts.request_id || !ts.user_name) return;
      const d = parseDate(ts.start_at);
      if (!d) return;
      signedIn.add(`${firstName(ts.user_name)}|${ts.request_id}|${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
    });

    assessmentRequests.forEach(rq => {
      const start = parseDate(rq.assessment_start_at);
      if (!start || start < ASSESS_SIGNIN_FLOOR) return;
      if (start >= now) return;                    // hasn't happened yet
      if (rq.assessment_complete !== true) return; // cancelled / still open — no verdict
      if (dateRange.from && start < dateRange.from) return;
      if (dateRange.to && start > dateRange.to) return;

      const assignees = Array.isArray(rq.assessment_assignees) ? rq.assessment_assignees : [];
      for (const fullName of assignees) {
        if (isExcluded(fullName)) continue;
        const techKey = canonicalDisplayName(fullName);
        if (!techKey) continue; // inactive or unknown employee
        if (techFilter !== "all" && normName(techKey) !== normName(techFilter) && firstName(techKey) !== firstName(techFilter)) continue;
        const dayKey = `${start.getFullYear()}-${start.getMonth()}-${start.getDate()}`;
        if (signedIn.has(`${firstName(fullName)}|${rq.jobber_id}|${dayKey}`)) continue; // signed in ✓

        // Manual excuse (legit reason for no sign-in) — keep the record
        // visible but count nothing.
        const ovKey = `request_signin|${firstName(fullName)}|${rq.jobber_id}|${localDayISO(start)}`;
        const excused = excuseMap.has(ovKey);

        if (!base.byTech[techKey]) base.byTech[techKey] = { onTime: 0, late: 0, unknown: 0, totalHours: 0, records: [] };
        if (!excused) {
          base.byTech[techKey].late++;
          base.late++;
        }
        const rec = {
          tech: techKey,
          date: start,
          jobTitle: rq.request_title || "Assessment",
          jobNumber: null,
          hours: 0,
          onTime: false,
          missedSignIn: true,
          excused,
          excuseNote: excused ? excuseMap.get(ovKey) : null,
          _ov: { metric: "request_signin", techFirst: firstName(fullName), targetKey: rq.jobber_id, day: localDayISO(start) },
          clientName: rq.client_name || null,
          scheduledTime: start,
          periods: [],
          totalDayHours: 0,
        };
        base.records.push(rec);
        base.byTech[techKey].records.push(rec);
      }
    });

    base.records.sort((a, b) => (b.date?.getTime() || 0) - (a.date?.getTime() || 0));
    base.total = base.onTime + base.late;
    base.rate  = base.total > 0 ? (base.onTime / base.total) * 100 : null;
    return base;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredTimesheets, assessmentRequests, dateRange, techFilter, canonicalNameByFirst, excuseMap, addMissRows]);

  // General Timesheets — clock-ins tied to neither a job nor a request
  // (office/shop/admin time, Jobber's "General" entries). Same same-day rule,
  // one record per (tech, day). This is where Taylor P's and Dylan's day-to-day
  // time tracking gets scored — they rarely appear on the job/request boards.
  const generalTimesheetMetrics = useMemo(() => buildTimesheetMetrics(filteredTimesheets, {
    kind: "general",
    targetKeyOf: ts => (!ts.job_number && !ts.request_id) ? "general" : null,
    titleOf: ts => ts.job_title || "General",
    clientNameOf: () => null,
    scheduledRawOf: null,
    metricName: "general_timesheet",
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [filteredTimesheets, canonicalNameByFirst, excuseMap, addMissRows]);

  // ── Forgot to Sign Out Metric ─────────────────────────────────────────────
  // Flag timesheets with duration > 15h or end_at on different calendar day
  const forgotSignOutMetrics = useMemo(() => {
    const byTech = {};
    const records = [];

    filteredTimesheets.forEach(ts => {
      if (isExcluded(ts.user_name)) return;
      const techKey = canonicalDisplayName(ts.user_name);
      if (!techKey) return; // inactive or unknown employee — drop
      if (!byTech[techKey]) byTech[techKey] = { forgot: 0, total: 0, records: [] };
      byTech[techKey].total++;

      const startDate = parseDate(ts.start_at);
      const endDate = parseDate(ts.end_at);

      const isForgot =
        (ts.duration_hours != null && ts.duration_hours > 15) ||
        (startDate && endDate && !sameCalendarDay(startDate, endDate));

      if (isForgot) {
        byTech[techKey].forgot++;
        const jobNum = ts.job_number ? String(ts.job_number) : null;
        const rec = {
          tech: techKey,
          date: startDate,
          endDate,
          jobNumber: jobNum,
          hours: ts.duration_hours || 0,
          clientName: jobNum ? (jobsLookupByJobNum.get(jobNum) || null) : null,
          reason: (ts.duration_hours != null && ts.duration_hours > 15)
            ? `${(ts.duration_hours || 0).toFixed(1)}h duration`
            : "overnight entry",
        };
        records.push(rec);
        byTech[techKey].records.push(rec);
      }
    });

    records.sort((a, b) => (b.date?.getTime() || 0) - (a.date?.getTime() || 0));
    return { byTech, records };
  }, [filteredTimesheets, jobsLookupByJobNum, canonicalNameByFirst]);

  // ═════════════════════════════════════════════════════════════════════════
  // METRIC 2: Same-Day Notes Rate
  // For each completed visit, did the assigned tech write a note on that job
  // on the same calendar day as the visit?
  // ═════════════════════════════════════════════════════════════════════════
  const visitNoteMetrics = useMemo(() => {
    let withNote = 0;
    let withoutNote = 0;
    const byTech = {};
    const missingDetails = []; // track visits without notes for debugging
    const records = [];
    // Deduplicate: one expected note per (job, tech, calendar date)
    const seen = new Set();

    filteredVisits.forEach(v => {
      const assignedUsers = v.assigned_users || [];
      if (assignedUsers.length === 0) return;

      const jobNum = String(v.job_number || "");
      const visitStartDate = parseDate(v.start_at);
      const jobNotes = notesLookup.get(jobNum); // Array<{techNorm, techFirst, createdAt, message}>

      assignedUsers.forEach(u => {
        if (isExcluded(u.name)) return;
        const techKey = canonicalDisplayName(u.name);
        if (!techKey) return; // inactive or unknown employee — drop

        // Deduplicate: skip if we already counted this job+tech+day
        const dateStr = visitStartDate
          ? `${visitStartDate.getFullYear()}-${visitStartDate.getMonth()}-${visitStartDate.getDate()}`
          : "unknown";
        const seenKey = `${jobNum}|${techKey}|${dateStr}`;
        if (seen.has(seenKey)) return;
        seen.add(seenKey);

        // Only the people who actually worked the job that day owe a note.
        // Being on the visit roster isn't enough — a lead who stops in to rep
        // the crew doesn't owe the write-up for the guys who worked the day.
        // Grandfathered before PAPERWORK_GATE_START; see lib/profitBonus.js.
        if (!owesPaperwork({
          jobNumber: jobNum,
          techFirst: firstName(u.name),
          date: visitStartDate,
          hoursIndex: paperworkHours,
        })) return;

        if (!byTech[techKey]) byTech[techKey] = { withNote: 0, withoutNote: 0, totalVisits: 0, records: [] };
        byTech[techKey].totalVisits++;

        // Match note by tech name AND same calendar day as the visit.
        // Prefer a real job note over a bridged timesheet note when both exist.
        let hasNote = false;
        let noteText = null;
        let noteFromTimesheet = false;
        if (jobNotes) {
          const techNorm = normName(u.name);
          const techFirst = firstName(u.name);
          const matches = jobNotes.filter(n => {
            if (n.techNorm !== techNorm && n.techFirst !== techFirst) return false;
            if (!visitStartDate || !n.createdAt) return true; // no date to compare — accept
            return sameCalendarDay(n.createdAt, visitStartDate);
          });
          const match = matches.find(n => n.noteType !== "timesheet") || matches[0];
          if (match) { hasNote = true; noteText = match.message; noteFromTimesheet = match.noteType === "timesheet"; }
        }

        // Manual excuse — keep the record visible but count nothing (board
        // rate AND the Kyle/Tommy note deduction both skip it).
        const ovDay = visitStartDate ? localDayISO(visitStartDate) : "unknown";
        const ovKey = `note|${firstName(techKey)}|${jobNum}|${ovDay}`;
        const excused = excuseMap.has(ovKey);

        if (excused) {
          byTech[techKey].totalVisits--; // undo the count above — excused visits grade nothing
        } else if (hasNote) {
          withNote++;
          byTech[techKey].withNote++;
        } else {
          withoutNote++;
          byTech[techKey].withoutNote++;
          if (missingDetails.length < 50) {
            missingDetails.push({ visitId: v.id, jobNumber: jobNum, tech: techKey, completedAt: v.completed_at });
          }
        }

        const clockInKey = jobNum && visitStartDate
          ? `${jobNum}|${firstName(u.name)}|${visitStartDate.getFullYear()}-${visitStartDate.getMonth()}-${visitStartDate.getDate()}`
          : null;
        const rec = {
          tech: techKey,
          date: parseDate(v.completed_at),
          jobNumber: jobNum,
          jobTitle: v.title || v.job_title || null,
          hasNote,
          noteText,
          noteFromTimesheet,
          excused,
          excuseNote: excused ? excuseMap.get(ovKey) : null,
          _ov: { metric: "note", techFirst: firstName(techKey), targetKey: jobNum, day: ovDay },
          clientName: jobNum ? (jobsLookupByJobNum.get(jobNum) || null) : null,
          scheduledTime: visitStartDate,
          clockInTime: clockInKey ? parseDate(tsClockInByJobTechDate.get(clockInKey) || null) : null,
        };
        records.push(rec);
        byTech[techKey].records.push(rec);
      });
    });

    // Admin-added note misses the automation didn't catch.
    addMissRows.forEach(o => {
      if (o.metric !== "note") return;
      const d = parseDate(`${o.day}T12:00:00`);
      if (!d) return;
      if (dateRange.from && d < dateRange.from) return;
      if (dateRange.to && d > dateRange.to) return;
      const techKey = canonicalDisplayName(o.tech_first);
      if (!techKey) return;
      if (techFilter !== "all" && normName(techKey) !== normName(techFilter) && firstName(techKey) !== firstName(techFilter)) return;
      if (!byTech[techKey]) byTech[techKey] = { withNote: 0, withoutNote: 0, totalVisits: 0, records: [] };
      byTech[techKey].totalVisits++;
      byTech[techKey].withoutNote++;
      withoutNote++;
      const rec = {
        tech: techKey,
        date: d,
        jobNumber: o.target_key || null,
        jobTitle: o.note ? `Manual miss — ${o.note}` : "Manual miss (admin-added)",
        hasNote: false,
        noteText: null,
        manualMiss: true,
        _ov: { metric: "note", techFirst: o.tech_first, targetKey: o.target_key || "", day: o.day },
        clientName: o.target_key ? (jobsLookupByJobNum.get(String(o.target_key)) || null) : null,
        scheduledTime: null,
        clockInTime: null,
      };
      records.push(rec);
      byTech[techKey].records.push(rec);
    });

    records.sort((a, b) => (b.date?.getTime() || 0) - (a.date?.getTime() || 0));

    const total = withNote + withoutNote;
    const rate = total > 0 ? (withNote / total) * 100 : null;

    return { withNote, withoutNote, total, rate, byTech, missingDetails, records };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredVisits, notesLookup, jobsLookupByJobNum, tsClockInByJobTechDate, paperworkHours, canonicalNameByFirst, excuseMap, addMissRows, dateRange, techFilter]);

  // ── Same-Day Visit Completion Metric ─────────────────────────────────────
  // Completed visits where completed_at is on the same calendar day as start_at.
  // Only tracked for journeymen (JOURNEYMEN_ONLY); attributed to the lead tech
  // UNLESS jobber_visits.closed_by has been manually set (admin override —
  // because Jobber's GraphQL doesn't tell us who actually clicked Complete).
  // When closed_by is set and points to a non-journeyman (e.g. owner closed it
  // for a tech after the fact), the visit is excluded from the leaderboard
  // entirely — it neither pads nor penalises anyone's same-day rate, but the
  // record still shows up in the drawer so it's visible.
  const visitCompletionMetrics = useMemo(() => {
    const byTech = {};
    const records = [];
    filteredVisits.forEach(v => {
      const startDate     = parseDate(v.start_at);
      const completedDate = parseDate(v.completed_at);
      if (!startDate || !completedDate) return;
      const isSD   = sameCalendarDay(startDate, completedDate);
      const jobNum = String(v.job_number || "");

      // Determine lead tech (journeymen only) — used as the default
      // attribution and as the "Auto" label in the closed-by editor.
      let leadFirst = jobNum ? (jobLeadTechMap.get(jobNum) || jobTopJourneymanMap.get(jobNum)) : null;
      if (!leadFirst) {
        // Fallback: first journeyman in assigned users for this visit
        const jmUser = (v.assigned_users || []).find(u => JOURNEYMEN_ONLY.includes(firstName(u.name)));
        leadFirst = jmUser ? firstName(jmUser.name) : null;
      }
      const leadName = leadFirst ? (canonicalNameByFirst.get(leadFirst) || null) : null;

      // Manual override: closed_by stores the canonical full name of whoever
      // actually closed the visit. If it's set, that overrides lead attribution.
      const closedBy = v.closed_by ? String(v.closed_by).trim() : null;
      const closerFirst = closedBy ? firstName(closedBy) : null;

      // Preserve original drop behavior for apprentice-only visits: if the
      // assigned lead isn't a journeyman AND there's no manual override, this
      // visit was never on the leaderboard and shouldn't clutter the drawer.
      const leadIsJourneyman = leadFirst && JOURNEYMEN_ONLY.includes(leadFirst);
      if (!leadIsJourneyman && !closedBy) return;

      // Pick the attributor (whose leaderboard this counts for, if at all).
      const attribFirst = closerFirst || leadFirst;
      const attribIsJourneyman = attribFirst && JOURNEYMEN_ONLY.includes(attribFirst);
      const techKey = attribIsJourneyman ? canonicalNameByFirst.get(attribFirst) : null;

      // Manual excuse — record stays in the drawer, counts for nothing.
      const ovKey = techKey ? `visit_completion|${firstName(techKey)}|${jobNum}|${localDayISO(startDate)}` : null;
      const excused = ovKey != null && excuseMap.has(ovKey);

      // An auto-attributed lead only owes the close if they actually worked the
      // job that day — the Lead Tech field otherwise makes them eat every late
      // close on that job for its whole life, including days they never showed.
      // An explicit closed_by is a human saying who clicked Complete, so it
      // overrides the gate the same way it overrides lead attribution.
      const attribWorked = closerFirst
        ? true
        : owesPaperwork({
            jobNumber: jobNum,
            techFirst: attribFirst,
            date: startDate,
            hoursIndex: paperworkHours,
          });

      const clockInKey = jobNum && startDate && leadFirst
        ? `${jobNum}|${leadFirst}|${startDate.getFullYear()}-${startDate.getMonth()}-${startDate.getDate()}`
        : null;
      const rec = {
        id: v.id,
        tech: techKey,
        leadFirst,
        leadName,
        closedBy,
        scheduledDate: startDate,
        completedDate,
        jobNumber: jobNum,
        jobTitle: v.title || v.job_title || null,
        onTime: isSD,
        excused,
        excuseNote: excused ? excuseMap.get(ovKey) : null,
        _ov: techKey ? { metric: "visit_completion", techFirst: firstName(techKey), targetKey: jobNum, day: localDayISO(startDate) } : null,
        clientName: jobNum ? (jobsLookupByJobNum.get(jobNum) || null) : null,
        scheduledTime: startDate,
        clockInTime: clockInKey ? parseDate(tsClockInByJobTechDate.get(clockInKey) || null) : null,
        // True when an admin/non-journeyman is on the hook for this close —
        // shown as a badge in the drawer so the inflated leadtech doesn't get
        // silently credited.
        excludedFromLeaderboard: !attribIsJourneyman || !attribWorked,
        // Lead was on the job's roster but logged no meaningful hours that day.
        notOnJob: !attribWorked,
      };

      if (techKey) {
        if (!byTech[techKey]) byTech[techKey] = { sameDay: 0, total: 0, onTime: 0, late: 0, records: [] };
        if (!excused && attribWorked) {
          byTech[techKey].total++;
          if (isSD) { byTech[techKey].sameDay++; byTech[techKey].onTime++; }
          else { byTech[techKey].late++; }
        }
        byTech[techKey].records.push(rec);
      }
      records.push(rec);
    });

    // Admin-added visit-close misses the automation didn't catch.
    addMissRows.forEach(o => {
      if (o.metric !== "visit_completion") return;
      const d = parseDate(`${o.day}T12:00:00`);
      if (!d) return;
      if (dateRange.from && d < dateRange.from) return;
      if (dateRange.to && d > dateRange.to) return;
      const techKey = canonicalDisplayName(o.tech_first);
      if (!techKey) return;
      if (techFilter !== "all" && normName(techKey) !== normName(techFilter) && firstName(techKey) !== firstName(techFilter)) return;
      if (!byTech[techKey]) byTech[techKey] = { sameDay: 0, total: 0, onTime: 0, late: 0, records: [] };
      byTech[techKey].total++;
      byTech[techKey].late++;
      const rec = {
        id: null,
        tech: techKey,
        scheduledDate: d,
        completedDate: d,
        jobNumber: o.target_key || null,
        jobTitle: o.note ? `Manual miss — ${o.note}` : "Manual miss (admin-added)",
        onTime: false,
        manualMiss: true,
        _ov: { metric: "visit_completion", techFirst: o.tech_first, targetKey: o.target_key || "", day: o.day },
        clientName: o.target_key ? (jobsLookupByJobNum.get(String(o.target_key)) || null) : null,
        excludedFromLeaderboard: false,
      };
      records.push(rec);
      byTech[techKey].records.push(rec);
    });

    records.sort((a, b) => (b.completedDate?.getTime() || 0) - (a.completedDate?.getTime() || 0));
    const onTime = Object.values(byTech).reduce((s, d) => s + d.sameDay, 0);
    const total  = Object.values(byTech).reduce((s, d) => s + d.total, 0);
    const late   = total - onTime;
    const rate   = total > 0 ? (onTime / total) * 100 : null;
    return { byTech, records, onTime, late, total, rate };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredVisits, jobsLookupByJobNum, tsClockInByJobTechDate, paperworkHours, jobLeadTechMap, jobTopJourneymanMap, canonicalNameByFirst, excuseMap, addMissRows, dateRange, techFilter]);

  // ═════════════════════════════════════════════════════════════════════════
  // ═════════════════════════════════════════════════════════════════════════
  // Per-Tech Scorecards (combining both metrics + job data)
  // ═════════════════════════════════════════════════════════════════════════
  const techScores = useMemo(() => {
    // Collect all unique tech names from all metrics
    const techSet = new Set();
    Object.keys(timesheetMetrics.byTech).forEach(t => techSet.add(t));
    Object.keys(visitNoteMetrics.byTech).forEach(t => techSet.add(t));
    Object.keys(visitCompletionMetrics.byTech).forEach(t => techSet.add(t));

    return Array.from(techSet).map(tech => {
      const ts = timesheetMetrics.byTech[tech] || { onTime: 0, late: 0, unknown: 0, totalHours: 0 };
      const vn = visitNoteMetrics.byTech[tech] || { withNote: 0, withoutNote: 0, totalVisits: 0 };
      const vc = visitCompletionMetrics.byTech[tech] || { onTime: 0, late: 0 };

      const tsTotal = ts.onTime + ts.late;
      const sameDayRate = tsTotal > 0 ? (ts.onTime / tsTotal) * 100 : null;
      const noteRate = vn.totalVisits > 0 ? (vn.withNote / vn.totalVisits) * 100 : null;
      const vcTotal = vc.onTime + vc.late;
      const visitCompletionRate = vcTotal > 0 ? (vc.onTime / vcTotal) * 100 : null;

      // Match jobs by first name — lead_tech first, fallback to top journeyman by hours
      const techFirst = firstName(tech);
      const techJobs = filteredJobs.filter(j => {
        const jNum = String(j.job_num || "");
        const lt = j._leadTech ? firstName(j._leadTech) : (jNum ? jobTopJourneymanMap.get(jNum) : null);
        return lt === techFirst;
      });
      const totalRevenue = techJobs.reduce((s, j) => s + (j.total_revenue || 0), 0);
      const totalProfit = techJobs.reduce((s, j) => s + (j.profit || 0), 0);
      const margins = techJobs.filter(j => j.profit_pct != null).map(j => j.profit_pct);
      const avgMargin = margins.length > 0 ? margins.reduce((a, b) => a + b, 0) / margins.length : null;

      return {
        tech,
        // Timesheet metrics
        sameDayRate,
        tsOnTime: ts.onTime,
        tsLate: ts.late,
        tsUnknown: ts.unknown,
        tsTotal,
        totalHours: ts.totalHours,
        // Visit/note metrics
        noteRate,
        visitsWithNote: vn.withNote,
        visitsWithoutNote: vn.withoutNote,
        totalVisits: vn.totalVisits,
        // Visit completion metrics
        visitCompletionRate,
        vcOnTime: vc.onTime,
        vcLate: vc.late,
        vcTotal,
        // Job metrics
        jobCount: techJobs.length,
        totalRevenue,
        totalProfit,
        avgMargin,
      };
    })
      .filter(t => t.tsTotal > 0 || t.totalVisits > 0 || t.vcTotal > 0 || t.jobCount > 0)
      .sort((a, b) => (b.totalRevenue || 0) - (a.totalRevenue || 0));
  }, [timesheetMetrics, visitNoteMetrics, visitCompletionMetrics, filteredJobs, jobTopJourneymanMap]);

  // ── Leaderboard data ──────────────────────────────────────────────────────
  const timesheetLeaderboard = useMemo(() => {
    return Object.entries(timesheetMetrics.byTech)
      .map(([tech, d]) => {
        const total = d.onTime + d.late;
        const rate  = total > 0 ? (d.onTime / total) * 100 : null;
        return { tech, rate, count: `${d.onTime}/${total}` };
      })
      .filter(t => t.rate != null)
      .sort((a, b) => b.rate - a.rate);
  }, [timesheetMetrics]);

  const requestTimesheetLeaderboard = useMemo(() => {
    return Object.entries(requestTimesheetMetrics.byTech)
      .map(([tech, d]) => {
        const total = d.onTime + d.late;
        const rate  = total > 0 ? (d.onTime / total) * 100 : null;
        return { tech, rate, count: `${d.onTime}/${total}` };
      })
      .filter(t => t.rate != null)
      .sort((a, b) => b.rate - a.rate);
  }, [requestTimesheetMetrics]);

  const generalTimesheetLeaderboard = useMemo(() => {
    return Object.entries(generalTimesheetMetrics.byTech)
      .map(([tech, d]) => {
        const total = d.onTime + d.late;
        const rate  = total > 0 ? (d.onTime / total) * 100 : null;
        return { tech, rate, count: `${d.onTime}/${total}` };
      })
      .filter(t => t.rate != null)
      .sort((a, b) => b.rate - a.rate);
  }, [generalTimesheetMetrics]);

  const visitCompletionLeaderboard = useMemo(() => {
    return Object.entries(visitCompletionMetrics.byTech)
      .map(([tech, d]) => {
        const rate = d.total > 0 ? (d.sameDay / d.total) * 100 : null;
        return { tech, rate, count: `${d.sameDay}/${d.total}` };
      })
      .filter(t => t.rate != null && JOURNEYMEN_ONLY.includes(firstName(t.tech)))
      .sort((a, b) => b.rate - a.rate);
  }, [visitCompletionMetrics]);

  // ═════════════════════════════════════════════════════════════════════════
  // METRIC 4: On Time Job Close Rate
  // Per closed job: compare closed_date vs last timesheet date for that job.
  // On time = closed within 1 day of the last timesheet. Attributed to lead tech.
  // ═════════════════════════════════════════════════════════════════════════
  const jobCloseMetrics = useMemo(() => {
    const byTech = {};
    const records = [];

    filteredJobs.forEach(j => {
      if (!j._closedDate) return;
      const jobNum = String(j.job_num || "");
      if (!jobNum) return;

      // Attribute to lead tech only (journeymen only)
      const leadFirst = jobLeadTechMap.get(jobNum) || jobTopJourneymanMap.get(jobNum);
      if (!leadFirst || !JOURNEYMEN_ONLY.includes(leadFirst)) return;

      // Last timesheet date = proxy for when work was last done on this job
      const lastTsDate = lastTimesheetByJobNum.get(jobNum);
      if (!lastTsDate) return;

      // On time = job closed within 1 day of the last timesheet entry
      const deadline = new Date(lastTsDate.getTime() + 24 * 60 * 60 * 1000);
      const isOnTime = j._closedDate <= deadline;

      const techKey = leadFirst.charAt(0).toUpperCase() + leadFirst.slice(1);
      if (!byTech[techKey]) byTech[techKey] = { sameDay: 0, total: 0, records: [] };
      byTech[techKey].total++;
      if (isOnTime) byTech[techKey].sameDay++;

      const rec = {
        tech: techKey,
        date: lastTsDate,
        closeDate: j._closedDate,
        jobNumber: jobNum,
        jobTitle: j.title || j.client_name || null,
        onTime: isOnTime,
      };
      records.push(rec);
      byTech[techKey].records.push(rec);
    });

    records.sort((a, b) => (b.date?.getTime() || 0) - (a.date?.getTime() || 0));
    const totalSD = Object.values(byTech).reduce((s, d) => s + d.sameDay, 0);
    const total   = Object.values(byTech).reduce((s, d) => s + d.total, 0);
    const rate    = total > 0 ? (totalSD / total) * 100 : null;
    return { byTech, records, totalSameDay: totalSD, total, rate };
  }, [filteredJobs, jobLeadTechMap, jobTopJourneymanMap, lastTimesheetByJobNum]);

  // ── Same-Day Notes Leaderboard ────────────────────────────────────────────
  // Ranked by: % of visits where the tech wrote a note on the same day
  const sameDayNotesLeaderboard = useMemo(() => {
    return Object.entries(visitNoteMetrics.byTech)
      .map(([tech, d]) => {
        const rate = d.totalVisits > 0 ? (d.withNote / d.totalVisits) * 100 : null;
        return { tech, rate, countStr: `${d.withNote}/${d.totalVisits}`, countLabel: "noted", records: d.records };
      })
      .filter(e => e.rate != null)
      .sort((a, b) => (b.rate ?? 0) - (a.rate ?? 0));
  }, [visitNoteMetrics]);

  const jobCloseLeaderboard = useMemo(() => {
    return Object.entries(jobCloseMetrics.byTech)
      .map(([tech, d]) => {
        const rate = d.total > 0 ? (d.sameDay / d.total) * 100 : null;
        return { tech, rate, count: `${d.sameDay}/${d.total}` };
      })
      .filter(t => t.rate != null && JOURNEYMEN_ONLY.includes(firstName(t.tech)))
      .sort((a, b) => b.rate - a.rate);
  }, [jobCloseMetrics]);

  // ── Per-job invoice rollup: dates, totals, status ─────────────────────────
  const invoiceInfoByJob = useMemo(() => {
    const map = new Map(); // jobNum → { dates:[], invoiced:$, outstanding:$ }
    invoices.forEach(inv => {
      const jn = String(inv.job_number || "").trim();
      if (!jn) return;
      const dStr = inv.created_at || inv.due_date;
      const d = parseDate(dStr);
      const slot = map.get(jn) || { dates: [], invoiced: 0, outstanding: 0 };
      if (d) slot.dates.push(d);
      slot.invoiced    += Number(inv.amounts_invoiced)    || 0;
      slot.outstanding += Number(inv.amounts_outstanding) || 0;
      map.set(jn, slot);
    });
    map.forEach(v => v.dates.sort((a, b) => a.getTime() - b.getTime()));
    return map;
  }, [invoices]);

  // Dating feed for the referral engine: jobber_invoices for Jobber's TRUE
  // issuedDate, jb_invoices for the job link (job_nums) on rows the archive is
  // missing. The engine takes the earliest date per job, and an ingest stamp
  // can never precede the issue it records, so the true date always wins.
  const referralInvoices = useMemo(
    () => [...jobberInvoices, ...invoices],
    [jobberInvoices, invoices]
  );

  // job_number → invoice status: "completed" | "invoiced" | "partial" | "paid".
  // Primary source is the jb_jobs invoice ROLLUP (amount_invoiced /
  // amount_outstanding / derived_status, written by refreshJobRevenue) — the
  // same fields BonusTracker's client-paid gate reads, and since 2026-07-30
  // they include NATIVE ElecBid→QBO invoices too. The old path read
  // jb_invoices.amounts_invoiced/amounts_outstanding, columns no live sync
  // populates, so every job reported "completed" regardless of payment.
  function jobInvoiceStatus(jobNum, job) {
    const j = job || null;
    const invoiced = Number(j?.amount_invoiced) || 0;
    if (invoiced > 0) {
      const outstanding = Number(j?.amount_outstanding) || 0;
      const paid = Math.max(0, invoiced - outstanding);
      const ds = String(j?.derived_status || "").toLowerCase();
      if (outstanding <= 0.01 || ds === "paid") return { status: "paid", invoiced, outstanding: 0, paid };
      if (paid > 0.01 || ds === "partially_paid") return { status: "partial", invoiced, outstanding, paid };
      return { status: "invoiced", invoiced, outstanding, paid: 0 };
    }
    // Legacy fallback (rollup empty): the old jb_invoices-derived map.
    const inv = invoiceInfoByJob.get(String(jobNum));
    if (!inv || inv.invoiced <= 0) return { status: "completed", invoiced: 0, outstanding: 0, paid: 0 };
    const paid = Math.max(0, inv.invoiced - inv.outstanding);
    if (inv.outstanding <= 0.01) return { status: "paid", invoiced: inv.invoiced, outstanding: 0, paid };
    if (paid > 0.01) return { status: "partial", invoiced: inv.invoiced, outstanding: inv.outstanding, paid };
    return { status: "invoiced", invoiced: inv.invoiced, outstanding: inv.outstanding, paid: 0 };
  }

  // ── Role lookup by lowercase first-name (from jobber_users) ────────────────
  // Handles whichever form Jobber returns ("ops lead tech" / "ops lead" etc.)
  const roleByFirst = useMemo(() => {
    const out = new Map();
    jobberUsers.forEach(u => {
      const first = firstName(u.name || "");
      if (!first || !u.role || u.is_active === false) return;
      out.set(first, String(u.role).toLowerCase().trim());
    });
    return out;
  }, [jobberUsers]);

  // ── Per-job tech hours: jobNum → Map<techFirst, hours> ────────────────────
  const jobTechHoursMap = useMemo(() => {
    const map = new Map();
    timesheets.forEach(ts => {
      const jn = String(ts.job_number || "").trim();
      if (!jn || !ts.user_name) return;
      const f = firstName(ts.user_name);
      if (!f) return;
      const inner = map.get(jn) || new Map();
      inner.set(f, (inner.get(f) || 0) + (Number(ts.duration_hours) || 0));
      map.set(jn, inner);
    });
    return map;
  }, [timesheets]);

  // ── Per-job participation rows (jobsRows below; "bonus" is a subset) ──────
  // Build one row per (job, tech, role) for every closed job since the floor —
  // NOT just bonus-qualifying jobs. The leaderboard ranks by avg margin across
  // all rows, so techs see all the jobs they ran (or were on) regardless of
  // whether the threshold was hit. The `qualifies` flag drives whether the row
  // contributes a real $ bonus and shows up in the Bonus Tracker.
  const bonusRows = useMemo(() => computeBonusRows({
    jobs: filteredJobs,
    jobTechHoursMap,
    roleByFirst,
    overrides: profitBonusOverrides,
    settings: profitBonusSettings,
    getStatus: jobInvoiceStatus,
    canonicalName,
    excludedJobs,
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [filteredJobs, jobTechHoursMap, roleByFirst, profitBonusOverrides, profitBonusSettings, invoiceInfoByJob, excludedJobs]);

  // ── ONE leaderboard: avg gross profit % across ALL jobs a tech touched ────
  // (Dylan 2026-07-23) Lead + crew rows merge per tech, so a lead who helped
  // as crew on one low-margin job isn't shown as a weird 1-job crew entry —
  // that job just weighs into their overall average. Bonus ALLOCATION is
  // untouched: lead/crew $ still come from computeBonusRows exactly as before;
  // this only changes how the ranking % is grouped. Includes non-qualifying
  // jobs too — qualifying-only flows go to Bonus Tracker.
  function buildLeaderboard() {
    const byTech = {};
    bonusRows.forEach(r => {
      if (!byTech[r.techName]) byTech[r.techName] = { records: [], totalBonus: 0, margins: [], qualCount: 0, leadCount: 0 };
      const d = byTech[r.techName];
      d.records.push(r);
      d.totalBonus += r.bonus;
      d.margins.push(r.profitPct);
      if (r.qualifies) d.qualCount += 1;
      if (r.role === "lead") d.leadCount += 1;
    });
    return Object.entries(byTech).map(([tech, d]) => ({
      tech,
      rate: d.margins.length ? d.margins.reduce((a, b) => a + b, 0) / d.margins.length : 0,
      count: d.records.length,
      qualCount: d.qualCount,
      leadCount: d.leadCount,
      totalBonus: d.totalBonus,
      records: d.records.slice().sort((a, b) => (b.closedDate?.getTime() || 0) - (a.closedDate?.getTime() || 0)),
    })).sort((a, b) => b.rate - a.rate);
  }
  const profitLeaderboard = useMemo(() => buildLeaderboard(), [bonusRows]);

  // ── All closed jobs for the Master Report Profitability sub-view ─────────
  // We deliberately do NOT reuse bonusRows here. bonusRows skips a job
  // when no lead can be determined from timesheets — which is correct for
  // bonus assignment (don't pay someone who didn't clock in) but wrong
  // for a cross-tech profitability table that should show EVERY closed
  // job, even ones where the named lead didn't log hours yet (sub work,
  // late timesheet sync, manual scheduling, etc).
  //
  // Lead-tech determination here is more permissive:
  //   1. jb_jobs.lead_tech custom field (canonicalized) — used as-is, no
  //      timesheet-hours requirement
  //   2. Top-hours Ops Lead Tech from timesheets — fallback when custom
  //      field is empty
  //   3. null — show "—" in the lead column rather than dropping the row
  const enrichedJobs = useMemo(() => {
    const { lead_pct, revenue_threshold, margin_threshold } = profitBonusSettings;
    return jobs.reduce((acc, j) => {
      if (!j._closedDate || j._closedDate < PROFIT_BONUS_START) return acc;
      if (!(j.total_revenue > 0)) return acc;
      if (j.profit_pct == null) return acc;
      if (excludedJobs.has(String(j.job_num || ""))) return acc; // manually excluded

      const jobNum = String(j.job_num || "");

      // Lead tech: custom field first, then top-hours fallback among any
      // LEAD-eligible role (journeyman / ops lead) — kept symmetric with
      // bonusRows so the Master Report and the main leaderboards attribute
      // jobs the same way.
      let leadFirst = null;
      let leadName  = null;
      if (j._leadTech) {
        leadFirst = firstName(j._leadTech);
        leadName  = canonicalNameByFirst.get(leadFirst)
                    || (leadFirst.charAt(0).toUpperCase() + leadFirst.slice(1));
      } else {
        const techHours = jobTechHoursMap.get(jobNum);
        if (techHours) {
          const candidates = [];
          techHours.forEach((hrs, f) => {
            if (isBonusExcluded(f) || NOT_LEAD_FIRST.includes(f)) return;
            const role = roleByFirst.get(f);
            if (role && LEAD_ROLES.includes(role)) {
              candidates.push({ first: f, hours: hrs });
            }
          });
          candidates.sort((a, b) => b.hours - a.hours);
          if (candidates.length) {
            leadFirst = candidates[0].first;
            leadName  = canonicalNameByFirst.get(leadFirst)
                        || (leadFirst.charAt(0).toUpperCase() + leadFirst.slice(1));
          }
        }
      }

      const qualifies = jobQualifies(j.total_revenue, j.profit_pct, revenue_threshold, margin_threshold);
      const excess    = qualifies ? Math.max(0, calcExcess(j.total_revenue, j.profit, margin_threshold)) : 0;
      const ov        = leadFirst ? profitBonusOverrides[`${jobNum}|${leadFirst}`] : null;
      const bonusPct  = ov?.bonus_pct != null ? Number(ov.bonus_pct) : lead_pct;
      // manual_amount REPLACES the computed bonus (same rule as computeBonusRows
      // — the Bonus Tracker's custom-$ override) so the Master Report can't
      // show a different figure than the card pays.
      const hasManual = ov?.manual_amount != null && isFinite(Number(ov.manual_amount));
      const bonus     = hasManual ? Number(ov.manual_amount) : (qualifies ? excess * (bonusPct / 100) : 0);
      const status    = jobInvoiceStatus(jobNum, j);

      acc.push({
        jobNum,
        techFirst:  leadFirst,
        techName:   leadName,
        role:       "lead",
        closedDate: j._closedDate,
        jobUpdatedAt: parseDate(j.updated_at),
        clientName: j.client_name,
        jobTitle:   j.title,
        revenue:    Number(j.total_revenue) || 0,
        profit:     Number(j.profit) || 0,
        profitPct:  Number(j.profit_pct),
        qualifies,
        bonus,
        bonus_pct:  bonusPct,
        status:        status.status,
        invoiced:      status.invoiced,
        outstanding:   status.outstanding,
        paidOnInvoice: status.paid,
        paid:       !!ov?.paid,
      });
      return acc;
    }, []);
  }, [jobs, canonicalNameByFirst, roleByFirst, jobTechHoursMap, profitBonusSettings, profitBonusOverrides, invoiceInfoByJob, excludedJobs]);

  const lastJobClosedDate = useMemo(() => {
    let latest = null;
    jobs.forEach(j => { if (j._closedDate && (!latest || j._closedDate > latest)) latest = j._closedDate; });
    return latest;
  }, [jobs]);

  const lastReviewDate = useMemo(() => {
    let latest = null;
    reviews.forEach(r => { const d = parseDate(r.created_at); if (d && (!latest || d > latest)) latest = d; });
    return latest;
  }, [reviews]);

  // ═════════════════════════════════════════════════════════════════════════
  // METRIC 7: Review Stars
  // Attribution priority (highest → lowest):
  //   0. assigned_techs (manual override set in UI) — always wins
  //   1. auto_assigned_techs (DB-stored, populated by backfill/sync):
  //        reviewer_name → fuzzy jb_jobs client match → most recent completed
  //        job before review date → timesheets for that job
  //   2. Runtime fallback (for reviews not yet backfilled):
  //      a. Tech name mentioned in comment (word-boundary scan)
  //      b. reviewer_name matches jb_jobs client → timesheets for that job
  //      c. Last resort: techs with timesheets in 14-day window before review
  // ═════════════════════════════════════════════════════════════════════════
  const reviewStarsMetrics = useMemo(() => {
    // job_num → Set<tech display names> from timesheets
    const techsByJobNum = new Map();
    timesheets.forEach(ts => {
      if (!ts.job_number || !ts.user_name || isExcluded(ts.user_name) || isNonCrew(ts.user_name)) return;
      const techKey = canonicalDisplayName(ts.user_name);
      if (!techKey) return; // inactive or unknown employee — drop
      const key = String(ts.job_number);
      if (!techsByJobNum.has(key)) techsByJobNum.set(key, new Set());
      techsByJobNum.get(key).add(techKey);
    });

    // norm(client_name) → [job_num strings]
    const jobsByClient = new Map();
    jobs.forEach(j => {
      if (!j.client_name || j.job_num == null) return;
      const key = normName(j.client_name);
      if (!jobsByClient.has(key)) jobsByClient.set(key, []);
      jobsByClient.get(key).push(String(j.job_num));
    });

    // norm(client_name) → { clientName, jobNum } for display in records
    const jobClientInfo = new Map();
    jobs.forEach(j => {
      if (!j.client_name || j.job_num == null) return;
      const key = normName(j.client_name);
      if (!jobClientInfo.has(key)) jobClientInfo.set(key, { clientName: j.client_name, jobNum: String(j.job_num) });
    });

    // job_num → client_name — fallback for reviews where reviewer_name is "Customer" or unmatched
    const clientByJobNum = new Map();
    jobs.forEach(j => {
      if (!j.client_name || j.job_num == null) return;
      clientByJobNum.set(String(j.job_num), j.client_name);
    });

    const findClientInfo = (name) => {
      if (!name) return null;
      const norm = normName(name);
      if (!norm) return null;
      if (jobClientInfo.has(norm)) return jobClientInfo.get(norm);
      for (const [clientNorm, info] of jobClientInfo) {
        if (norm.length >= 4 && (clientNorm.includes(norm) || norm.includes(clientNorm))) return info;
      }
      return null;
    };

    const techsForJobNums = (jobNums) => {
      const result = new Set();
      jobNums.forEach(jn => {
        const techs = techsByJobNum.get(jn);
        if (techs) techs.forEach(t => result.add(t));
      });
      return result;
    };

    const findTechsByClientName = (name) => {
      if (!name) return null;
      const norm = normName(name);
      if (!norm) return null;
      if (jobsByClient.has(norm)) return techsForJobNums(jobsByClient.get(norm));
      // Partial match: client name contains search term or vice versa
      for (const [clientNorm, jobNums] of jobsByClient) {
        if (norm.length >= 4 && (clientNorm.includes(norm) || norm.includes(clientNorm))) {
          return techsForJobNums(jobNums);
        }
      }
      return null;
    };

    const byTech = {};
    const unattributedRecords = [];
    const ignoredRecords = [];

    reviews.forEach(r => {
      if (!r.rating) return;
      const reviewDate = parseDate(r.created_at);
      if (dateRange.from && reviewDate && reviewDate < dateRange.from) return;
      if (dateRange.to && reviewDate && reviewDate > dateRange.to) return;

      let autoTechs;
      let autoAttributed;

      // Priority 1: DB-stored auto attribution (from backfill).
      // An ARRAY (even empty []) means the backfill already ran — honour it and
      // do NOT fall through to the runtime fallback. Empty [] = no match found.
      if (Array.isArray(r.auto_assigned_techs)) {
        const filtered = new Set(
          r.auto_assigned_techs
            .filter(t => !isExcluded(t) && !isNonCrew(t))
            .map(t => canonicalDisplayName(t))
            .filter(Boolean) // drop inactive/unknown employees
        );
        autoTechs = filtered; // may be empty — that is intentional
        autoAttributed = filtered.size > 0 ? "auto_db" : "none";
      }

      // Priority 2: Runtime fallback — ONLY for reviews where auto_assigned_techs
      // is still NULL (backfill has not yet processed this review).
      // Strict: client name → job → timesheets only. No name-mention scan,
      // no 14-day timesheet window — those cause mass over-attribution.
      if (!autoTechs) {
        let jobTechs = null;
        if (r.reviewer_name) jobTechs = findTechsByClientName(r.reviewer_name);

        if (jobTechs && jobTechs.size > 0) {
          autoTechs = jobTechs;
          autoAttributed = "client_match";
        } else {
          autoTechs = new Set();
          autoAttributed = "none";
        }
      }

      // Manual override always wins
      let creditTechs;
      let attributed;

      if (r.assigned_techs && Array.isArray(r.assigned_techs) && r.assigned_techs.length > 0) {
        creditTechs = new Set(r.assigned_techs);
        attributed = "manual";
      } else {
        creditTechs = autoTechs;
        attributed = autoAttributed;
      }

      const autoTechsArr = [...autoTechs];
      // Manual assigned_job override always wins over auto-detected job_number
      // for header display purposes — same precedence the manual-assign UI uses
      // for techs. Without this the header keeps showing the original job's
      // client even after the user picks a different one in the override popup.
      const effectiveJobNum = r.assigned_job || (r.job_number != null ? String(r.job_number) : null);
      const reviewerMatch = findClientInfo(r.reviewer_name);
      const jobNumClientName = !reviewerMatch && effectiveJobNum
        ? clientByJobNum.get(effectiveJobNum)
        : null;
      const matchedClient = reviewerMatch ||
        (jobNumClientName ? { clientName: jobNumClientName, jobNum: effectiveJobNum } : null);

      // Ignored (admin marked "nobody was involved") — no credit, no unassigned
      // queue. Collected separately so the Ignored drawer can list + restore.
      if (r.ignored) {
        ignoredRecords.push({
          id: r.id,
          assignedTechs: r.assigned_techs || null,
          assignedJob: r.assigned_job || null,
          autoTechs: autoTechsArr,
          manualMentions: r.manual_mentions || null,
          tech: null,
          rating: r.rating,
          reviewText: r.comment || "",
          reviewerName: r.reviewer_name || "",
          clientName: matchedClient?.clientName || null,
          jobNumber: effectiveJobNum || matchedClient?.jobNum || null,
          date: reviewDate,
          attributed: "none",
          ignored: true,
        });
        return;
      }

      creditTechs.forEach(tech => {
        if (techFilter !== "all" && normName(tech) !== normName(techFilter) && firstName(tech) !== firstName(techFilter)) return;
        if (!byTech[tech]) byTech[tech] = { totalStars: 0, reviewCount: 0, records: [] };
        byTech[tech].totalStars += r.rating;
        byTech[tech].reviewCount++;
        byTech[tech].records.push({
          id: r.id,
          assignedTechs: r.assigned_techs || null,
          assignedJob: r.assigned_job || null,
          autoTechs: autoTechsArr,
          manualMentions: r.manual_mentions || null,
          tech,
          rating: r.rating,
          reviewText: r.comment || "",
          reviewerName: r.reviewer_name || "",
          clientName: matchedClient?.clientName || null,
          jobNumber: effectiveJobNum || matchedClient?.jobNum || null,
          date: reviewDate,
          attributed,
          aiConfidence: r.ai_confidence || null,
          aiReasoning:  r.ai_reasoning  || null,
        });
      });

      // No tech could be credited (name didn't match a client/job and the
      // review wasn't manually assigned). Collect it so it still surfaces in
      // Crew Performance as an "unassigned" review the user can hand-assign,
      // instead of silently vanishing. techFilter doesn't gate this — the set
      // of unattributed reviews is global.
      if (creditTechs.size === 0) {
        unattributedRecords.push({
          id: r.id,
          assignedTechs: r.assigned_techs || null,
          assignedJob: r.assigned_job || null,
          autoTechs: autoTechsArr,
          manualMentions: r.manual_mentions || null,
          tech: null,
          rating: r.rating,
          reviewText: r.comment || "",
          reviewerName: r.reviewer_name || "",
          clientName: matchedClient?.clientName || null,
          jobNumber: effectiveJobNum || matchedClient?.jobNum || null,
          date: reviewDate,
          attributed: "none",
          aiConfidence: r.ai_confidence || null,
          aiReasoning:  r.ai_reasoning  || null,
        });
      }
    });

    const totalReviews = reviews.length;
    const ratedReviews = reviews.filter(r => r.rating);
    const avgRating = ratedReviews.length > 0
      ? ratedReviews.reduce((s, r) => s + r.rating, 0) / ratedReviews.length
      : null;

    return { byTech, totalReviews, avgRating, unattributedRecords, ignoredRecords };
  }, [reviews, timesheets, jobs, allTechNames, dateRange, techFilter, canonicalNameByFirst]);

  // Reviews credited to nobody AND not naming any tech — the "truly unassigned"
  // set. A review that names a tech is handled by the mention→base rule, so it's
  // excluded here. These surface in a banner for one-click manual assignment.
  const unassignedReviews = useMemo(() => {
    const recs = reviewStarsMetrics.unattributedRecords || [];
    const hasMention = (comment) => {
      const lower = (comment || "").toLowerCase();
      if (!lower) return false;
      return allTechNames.some(techFull => {
        if (isNonCrew(techFull)) return false; // their mentions don't imply a credited tech
        const fn = firstName(techFull);
        if (!fn) return false;
        let from = 0;
        while (true) {
          const idx = lower.indexOf(fn, from);
          if (idx === -1) return false;
          const before = idx > 0 ? lower[idx - 1] : " ";
          const after = idx + fn.length < lower.length ? lower[idx + fn.length] : " ";
          if (!/[a-z]/.test(before) && !/[a-z]/.test(after)) return true;
          from = idx + fn.length;
        }
      });
    };
    return recs
      // A manual mention counts as handled (it pays the guy via the
      // mention→base rule), same as a name in the text.
      .filter(r => !hasMention(r.reviewText) && !(r.manualMentions || []).length)
      .sort((a, b) => (b.date?.getTime?.() || 0) - (a.date?.getTime?.() || 0));
  }, [reviewStarsMetrics, allTechNames]);

  // Reviews an admin ignored ("nobody was involved") within the current period
  // filter — listed in their own drawer so a mis-click can be restored.
  const ignoredReviews = useMemo(() => {
    return (reviewStarsMetrics.ignoredRecords || [])
      .slice()
      .sort((a, b) => (b.date?.getTime?.() || 0) - (a.date?.getTime?.() || 0));
  }, [reviewStarsMetrics]);

  // ═════════════════════════════════════════════════════════════════════════
  // METRIC 8: Name Mentions
  // Scan each review's comment for tech first names (case-insensitive,
  // word-boundary match). Each mention = 1 point for that tech.
  // ═════════════════════════════════════════════════════════════════════════
  const nameMentionMetrics = useMemo(() => {
    const byTech = {};
    const totalReviews = reviews.length;
    const reviewsWithAnyMention = new Set();

    const mentionClientByJobNum = new Map();
    jobs.forEach(j => {
      if (!j.client_name || j.job_num == null) return;
      mentionClientByJobNum.set(String(j.job_num), j.client_name);
    });

    reviews.forEach(r => {
      // Manual mentions (admin credited a guy the scan missed) count even on
      // reviews with no comment text.
      const manualFirsts = new Set(
        (Array.isArray(r.manual_mentions) ? r.manual_mentions : []).map(n => firstName(n))
      );
      if (!r.comment && manualFirsts.size === 0) return;
      if (r.ignored) return; // "nobody was involved" — mentions don't pay either
      const reviewDate = parseDate(r.created_at);
      if (dateRange.from && reviewDate && reviewDate < dateRange.from) return;
      if (dateRange.to && reviewDate && reviewDate > dateRange.to) return;

      const lower = (r.comment || "").toLowerCase();
      // Same effective-job rule: manual assigned_job override beats job_number.
      const effectiveJobNumForMentions = r.assigned_job || (r.job_number != null ? String(r.job_number) : null);
      const clientName = effectiveJobNumForMentions
        ? (mentionClientByJobNum.get(effectiveJobNumForMentions) || null)
        : null;

      allTechNames.forEach(techFull => {
        const fn = firstName(techFull); // already lowercase
        if (!fn) return;
        // Office/social staff (Taylor/Mallory) get NO auto credit — reviews
        // thanking "Taylor" are CSR praise. A MANUAL mention (admin checked
        // them deliberately) does pay, same rule as manual assigned_techs.
        if (isNonCrew(techFull) && !manualFirsts.has(fn)) return;
        // Hit = manual credit OR word-boundary text match (char before and
        // after must not be a-z).
        let hit = manualFirsts.has(fn);
        let searchFrom = 0;
        while (!hit) {
          const idx = lower.indexOf(fn, searchFrom);
          if (idx === -1) break;
          const before = idx > 0 ? lower[idx - 1] : " ";
          const after = idx + fn.length < lower.length ? lower[idx + fn.length] : " ";
          if (!/[a-z]/.test(before) && !/[a-z]/.test(after)) hit = true;
          else searchFrom = idx + fn.length;
        }
        if (!hit) return;
        if (techFilter !== "all" && normName(techFull) !== normName(techFilter) && firstName(techFull) !== firstName(techFilter)) return;
        if (!byTech[techFull]) byTech[techFull] = { mentions: 0, reviewCount: 0, records: [] };
        byTech[techFull].mentions++;
        byTech[techFull].reviewCount++;
        reviewsWithAnyMention.add(r.id);
        byTech[techFull].records.push({
          id: r.id,
          tech: techFull,
          rating: r.rating,
          reviewText: r.comment || "",
          reviewerName: r.reviewer_name || "",
          clientName,
          jobNumber: effectiveJobNumForMentions || null,
          date: reviewDate,
          mentionedName: fn,
          manualMention: manualFirsts.has(fn),
        });
      });
    });

    return { byTech, totalReviews, reviewsWithMentions: reviewsWithAnyMention.size };
  }, [reviews, jobs, allTechNames, dateRange, techFilter]);

  const reviewStarsLeaderboard = useMemo(() => {
    return Object.entries(reviewStarsMetrics.byTech)
      .map(([tech, d]) => ({
        tech,
        totalStars: d.totalStars,
        reviewCount: d.reviewCount,
        avgStars: d.reviewCount > 0 ? d.totalStars / d.reviewCount : 0,
      }))
      .filter(t => t.reviewCount > 0)
      .sort((a, b) => b.totalStars - a.totalStars);
  }, [reviewStarsMetrics]);

  const nameMentionLeaderboard = useMemo(() => {
    return Object.entries(nameMentionMetrics.byTech)
      .map(([tech, d]) => ({
        tech,
        mentions: d.mentions,
        reviewCount: d.reviewCount,
      }))
      .sort((a, b) => b.mentions - a.mentions);
  }, [nameMentionMetrics]);

  // ═════════════════════════════════════════════════════════════════════════
  // REVIEW BONUS — $25 per 5★ review (per tech who worked) + $25 per name
  // mention, capped at $300/tech/month. Only 5★ reviews qualify, only
  // mentions in 5★ reviews qualify. Amounts overridable via app_settings.
  // ═════════════════════════════════════════════════════════════════════════
  // Split into TWO boards (the single combined board over-credited — it read as
  // "$25 + $25 for anyone who got a review"):
  //   • Review Bonus  = base $/review for each 5★ review the tech WORKED.
  //   • Mention Bonus = mention $ for each 5★ review that NAMES the tech.
  // The $/mo cap is the real per-tech payout ceiling, so it's applied to the
  // COMBINED monthly total — base fills the cap first, mention takes the
  // remainder — so the two boards always sum to the actual capped payout.
  const { reviewBonusBoard, mentionBonusBoard } = useMemo(() => {
    const { base, mention, monthly_cap } = reviewBonusSettings;
    // tech → month("YYYY-MM") → { baseIds:Set, mentionIds:Set }. We track review
    // IDs (not raw counts) so the base "worked" set and the mention set can be
    // unioned without double-counting a review the tech both worked AND was
    // named in.
    const byTechMonth = new Map();
    const ensure = (tech, month) => {
      if (!byTechMonth.has(tech)) byTechMonth.set(tech, new Map());
      const m = byTechMonth.get(tech);
      if (!m.has(month)) m.set(month, { baseIds: new Set(), mentionIds: new Set() });
      return m.get(month);
    };
    const monthKey = (d) => d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}` : "no-date";
    const ridOf = (r) => r.id || `${r.date?.toISOString?.() || ""}|${r.reviewerName || ""}`;

    // 5★ reviews on jobs the tech worked → base credit.
    for (const [tech, d] of Object.entries(reviewStarsMetrics.byTech)) {
      if (isBonusExcluded(firstName(tech))) continue;
      for (const r of d.records) {
        if (r.rating !== 5) continue;
        ensure(tech, monthKey(r.date)).baseIds.add(ridOf(r));
      }
    }
    // 5★ reviews that NAME the tech → mention credit, AND base credit too: being
    // named in a review proves the tech worked that job, so they earn the
    // "review worked" base even when timesheet/client-name attribution missed
    // them. This keeps the two boards consistent (everyone on Mention Bonus is
    // also on Review Bonus).
    for (const [tech, d] of Object.entries(nameMentionMetrics.byTech)) {
      if (isBonusExcluded(firstName(tech))) continue;
      for (const r of d.records) {
        if (r.rating !== 5) continue;
        const cell = ensure(tech, monthKey(r.date));
        const rid = ridOf(r);
        cell.mentionIds.add(rid);
        cell.baseIds.add(rid);
      }
    }

    const reviewBonusBoard = [];
    const mentionBonusBoard = [];
    for (const [tech, months] of byTechMonth.entries()) {
      let baseBonus = 0, mentionBonus = 0;
      let reviewCount = 0, mentionCount = 0, cappedMonths = 0;
      for (const [, cell] of months.entries()) {
        const bCount = cell.baseIds.size;
        const mCount = cell.mentionIds.size;
        const baseRaw     = bCount * base;
        const mentionRaw  = mCount * mention;
        // Shared cap: base fills the cap first, mention gets what's left.
        const baseShown    = Math.min(baseRaw, monthly_cap);
        const mentionShown = Math.min(mentionRaw, monthly_cap - baseShown);
        if (baseRaw + mentionRaw > monthly_cap) cappedMonths++;
        baseBonus     += baseShown;
        mentionBonus  += mentionShown;
        reviewCount   += bCount;
        mentionCount  += mCount;
      }
      if (baseBonus > 0)    reviewBonusBoard.push({ tech, totalBonus: baseBonus, reviewCount, cappedMonths });
      if (mentionBonus > 0) mentionBonusBoard.push({ tech, totalBonus: mentionBonus, mentionCount, cappedMonths });
    }
    reviewBonusBoard.sort((a, b) => b.totalBonus - a.totalBonus);
    mentionBonusBoard.sort((a, b) => b.totalBonus - a.totalBonus);
    return { reviewBonusBoard, mentionBonusBoard };
  }, [reviewStarsMetrics, nameMentionMetrics, reviewBonusSettings]);

  // ── Sorted detail table ───────────────────────────────────────────────────
  const sortedJobs = useMemo(() => {
    const arr = [...filteredJobs];
    arr.sort((a, b) => {
      let va, vb;
      if (sortCol === "closedDate") { va = a._closedDate?.getTime() || 0; vb = b._closedDate?.getTime() || 0; }
      else { va = a[sortCol] ?? ""; vb = b[sortCol] ?? ""; if (typeof va === "string") { va = va.toLowerCase(); vb = (vb + "").toLowerCase(); } }
      return sortDir === "asc" ? (va < vb ? -1 : va > vb ? 1 : 0) : (va > vb ? -1 : va < vb ? 1 : 0);
    });
    return arr;
  }, [filteredJobs, sortCol, sortDir]);

  const toggleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("desc"); }
  };

  // ─── Total KPIs ──────────────────────────────────────────────────────────
  const totalHours = filteredTimesheets.reduce((s, ts) => s + (ts.duration_hours || 0), 0);
  const totalRevenue = filteredJobs.reduce((s, j) => s + (j.total_revenue || 0), 0);

  // ─── Tab / style helpers ─────────────────────────────────────────────────
  const tabBtn = (t) => ({
    display: "block", width: "100%", textAlign: "left",
    padding: "13px 16px", marginBottom: 8, borderRadius: 8, cursor: "pointer",
    fontSize: 15, fontWeight: tab === t ? 700 : 500,
    background: tab === t ? C.accentDim : C.surface,
    color: tab === t ? C.accent : C.text,
    border: `1px solid ${tab === t ? C.accent : C.border}`,
    borderLeft: `4px solid ${tab === t ? C.accent : "transparent"}`,
    transition: "all 0.15s"
  });

  const thStyle = (col) => ({
    padding: "10px 12px", textAlign: "left", fontSize: 11, fontWeight: 700,
    color: sortCol === col ? C.accent : C.muted, cursor: "pointer", whiteSpace: "nowrap",
    textTransform: "uppercase", letterSpacing: "0.06em",
    borderBottom: `1px solid ${C.border}`, background: C.surface,
    position: "sticky", top: 0, zIndex: 2
  });

  const tdStyle = {
    padding: "10px 12px", fontSize: 13, borderBottom: `1px solid ${C.border}`,
    color: C.text, whiteSpace: "nowrap"
  };

  // ═════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═════════════════════════════════════════════════════════════════════════
  return (
    <div style={{ minHeight: "100vh", background: C.bg, color: C.text, fontFamily: "'IBM Plex Sans', 'Roboto', sans-serif" }}>

      {/* ── Header ── */}
      <div style={{
        background: C.surface, borderBottom: `1px solid ${C.border}`,
        padding: "16px 20px"
      }}>
        <div style={{ fontSize: 20, fontWeight: 700 }}>Crew Performance</div>
        <div style={{ fontSize: 12, color: C.muted, marginTop: 2 }}>
          {timesheets.length} timesheets · {visits.filter(v => v.is_complete).length} completed visits · {notes.length} notes · {jobs.length} jobs · {reviews.length} reviews
          {lastRefresh && ` · Updated ${lastRefresh.toLocaleTimeString("en-CA", { hour: "2-digit", minute: "2-digit" })}`}
        </div>
      </div>

      {/* ── Body: left tab rail + main column ── */}
      <div style={{ display: "flex", alignItems: "flex-start", flexWrap: "wrap" }}>

        {/* Left tab rail — primary navigation for this page */}
        <nav style={{
          width: 220, flexShrink: 0, boxSizing: "border-box",
          position: "sticky", top: 0, alignSelf: "flex-start",
          padding: "16px 12px",
          background: C.bg, borderRight: `1px solid ${C.border}`,
        }}>
          {availableTabs.map(t => (
            <button key={t.id} style={tabBtn(t.id)} onClick={() => setTab(t.id)}>{t.label}</button>
          ))}
        </nav>

        {/* Main column — filters + tab content */}
        <div style={{ flex: 1, minWidth: 320 }}>

      {/* ── Filters (hidden on Bonus Tracker tab) ── */}
      <div style={{
        position: "sticky", top: 0, zIndex: 20,
        background: C.bg, borderBottom: `1px solid ${C.border}`,
        // Referrals carries its own month picker (payouts are month-scoped, not
        // date-preset scoped), so the shared filter bar is hidden there too.
        padding: "10px 20px", display: (tab === "bonuses" || tab === "report" || tab === "referrals") ? "none" : "flex", gap: 10, alignItems: "center", flexWrap: "wrap"
      }}>
        <select value={techFilter} onChange={e => setTechFilter(e.target.value)} style={selectStyle}>
          <option value="all">All Techs</option>
          {allTechNames.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <select value={datePreset} onChange={e => setDatePreset(e.target.value)} style={selectStyle}>
          <option value="all">All Time</option>
          <option value="this_week">This Week</option>
          <option value="last_week">Last Week</option>
          <option value="this_month">This Month</option>
          <option value="last_month">Last Month</option>
          <option value="this_quarter">This Quarter</option>
          <option value="this_year">This Year</option>
          <option value="this_fiscal">Fiscal Year (Jun-May)</option>
          <option value="custom">Custom Range</option>
        </select>
        {datePreset === "custom" && (
          <>
            <input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} style={{ ...selectStyle, width: 140 }} />
            <span style={{ color: C.muted, fontSize: 12 }}>to</span>
            <input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} style={{ ...selectStyle, width: 140 }} />
          </>
        )}
        <div style={{ marginLeft: "auto", fontSize: 12, color: C.muted }}>
          {filteredTimesheets.length} timesheets · {filteredVisits.length} visits · {filteredJobs.length} jobs
        </div>
      </div>

      {/* ── Content ── */}
      <div style={{ padding: "20px 20px", maxWidth: 1400, margin: "0 auto" }}>

        {loading && (
          <div style={{ textAlign: "center", padding: 80, color: C.muted }}>
            <div style={{ width: 36, height: 36, margin: "0 auto 16px", border: `3px solid ${C.accentDim}`, borderTopColor: C.accent, borderRadius: "50%", animation: "spin 0.8s linear infinite" }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            Loading crew data...
          </div>
        )}

        {error && !loading && (
          <div style={{ padding: 20, background: C.redDim, border: `1px solid ${C.red}`, borderRadius: 12, color: C.red, fontSize: 14 }}>
            Error: {error}
          </div>
        )}

        {!loading && !error && (
          <>
            {/* ═══════════════════════════════════════════════════════════════ */}
            {/* DASHBOARD TAB */}
            {/* ═══════════════════════════════════════════════════════════════ */}
            {tab === "dashboard" && (
              <>
                {/* Core Accountability KPIs */}
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 24 }}>
                  <KPICard
                    label="Job Timesheets"
                    value={timesheetMetrics.rate != null ? pct(timesheetMetrics.rate) : "No data"}
                    color={rateColor(timesheetMetrics.rate)}
                    sub={`${timesheetMetrics.onTime} same-day / ${timesheetMetrics.late} late${timesheetMetrics.unknown > 0 ? ` / ${timesheetMetrics.unknown} unknown` : ""}`}
                    onClick={timesheetMetrics.records?.length > 0 ? () => setDrawerData({
                      type: "timesheet",
                      techName: null,
                      title: "Job Timesheets",
                      records: timesheetMetrics.records,
                    }) : undefined}
                  />
                  <KPICard
                    label="Notes Completed"
                    value={visitNoteMetrics.rate != null ? pct(visitNoteMetrics.rate) : "No data"}
                    color={rateColor(visitNoteMetrics.rate)}
                    sub={`${visitNoteMetrics.withNote} completed / ${visitNoteMetrics.withoutNote} missing`}
                    onClick={visitNoteMetrics.records?.length > 0 ? () => setDrawerData({
                      type: "notes",
                      techName: null,
                      title: "Same-Day Notes",
                      records: visitNoteMetrics.records,
                    }) : undefined}
                  />
                  <KPICard
                    label="Visits Closed On Time"
                    value={visitCompletionMetrics.rate != null ? pct(visitCompletionMetrics.rate) : "No data"}
                    color={visitCompletionMetrics.rate != null ? (visitCompletionMetrics.rate >= 85 ? C.green : visitCompletionMetrics.rate >= 70 ? C.yellow : C.red) : C.muted}
                    sub={`${visitCompletionMetrics.onTime} on time / ${visitCompletionMetrics.late} late`}
                  />
                  <KPICard
                    label="Hours Logged"
                    value={hrs(totalHours)}
                    color={C.purple}
                    sub={`${filteredTimesheets.length} entries`}
                  />
                  <KPICard
                    label="Jobs Completed"
                    value={filteredJobs.length}
                    color={C.accent}
                  />
                  {hasPermission("view_revenue_per_tech") && (
                    <KPICard
                      label="Revenue"
                      value={dollar(totalRevenue)}
                      color={C.green}
                    />
                  )}
                </div>

                {/* Data Integrity Note */}
                {timesheetMetrics.unknown > 0 && (
                  <div style={{
                    padding: "10px 16px", marginBottom: 20, borderRadius: 8,
                    background: C.yellowDim, border: `1px solid ${C.yellow}30`,
                    fontSize: 12, color: C.yellow
                  }}>
                    {timesheetMetrics.unknown} timesheet entries are missing created_at timestamps and are excluded from the same-day rate calculation.
                  </div>
                )}

                {/* Review attribution moved to Marketing → Reviews tab.
                    The old AI matcher here was unreliable when reviewer names
                    didn't match Jobber clients — replaced by a manual
                    searchbar UI on /marketing. Manual assignments still write
                    to assigned_techs/assigned_job, which the leaderboard logic
                    below already prefers over auto_assigned_techs. */}

                {/* Unassigned reviews — couldn't be matched to a tech (name
                    didn't match a client/job, nobody named in the text). Surface
                    them so they don't vanish; click to open the assignment
                    drawer, after which they credit the assigned tech(s). */}
                {unassignedReviews.length > 0 && (
                  <div
                    onClick={() => setDrawerData({
                      type: "review_stars",
                      techName: null,
                      title: "Unassigned Reviews",
                      records: unassignedReviews,
                      subMetrics: [{ label: "Need assigning", value: String(unassignedReviews.length), color: C.yellow }],
                    })}
                    style={{
                      padding: "10px 16px", marginBottom: 20, borderRadius: 8,
                      background: C.yellowDim, border: `1px solid ${C.yellow}30`,
                      fontSize: 12, color: C.yellow, cursor: "pointer",
                      display: "flex", alignItems: "center", gap: 8,
                    }}
                  >
                    <span style={{ fontSize: 14 }}>⚠</span>
                    <span>
                      {unassignedReviews.length} review{unassignedReviews.length !== 1 ? "s" : ""} couldn't be
                      matched to a tech — click to assign or ignore
                    </span>
                  </div>
                )}

                {/* Ignored reviews (this period) — restorable if mis-clicked */}
                {ignoredReviews.length > 0 && (
                  <div
                    onClick={() => setDrawerData({
                      type: "review_stars",
                      techName: null,
                      title: "Ignored Reviews",
                      records: ignoredReviews,
                      subMetrics: [{ label: "Ignored", value: String(ignoredReviews.length), color: C.muted }],
                    })}
                    style={{
                      padding: "8px 16px", marginTop: unassignedReviews.length > 0 ? -12 : 0,
                      marginBottom: 20, borderRadius: 8,
                      background: `${C.border}25`, border: `1px solid ${C.border}`,
                      fontSize: 11, color: C.muted, cursor: "pointer",
                      display: "flex", alignItems: "center", gap: 8,
                    }}
                  >
                    <span>
                      {ignoredReviews.length} ignored review{ignoredReviews.length !== 1 ? "s" : ""} in this
                      period (nobody involved) — click to view / restore
                    </span>
                  </div>
                )}

                {/* Leaderboards — grouped into sections */}
                {/* Section header helper */}
                {[
                  {
                    label: "Field Discipline",
                    boards: [
                      <TechLeaderboard
                        key="timesheets"
                        title="Job Timesheets"
                        entries={timesheetLeaderboard}
                        countLabel="same-day"
                        onEntryClick={(entry) => {
                          const records = timesheetMetrics.byTech[entry.tech]?.records || [];
                          if (records.length === 0) return;
                          setDrawerData({ type: "timesheet", metric: "timesheet", techName: entry.tech, title: "Job Timesheets — " + entry.tech, records });
                        }}
                      />,
                      <TechLeaderboard
                        key="request_timesheets"
                        title="Request Timesheets"
                        entries={requestTimesheetLeaderboard}
                        countLabel="same-day"
                        emptyMessage="No request time tracked yet"
                        onEntryClick={(entry) => {
                          const records = requestTimesheetMetrics.byTech[entry.tech]?.records || [];
                          if (records.length === 0) return;
                          setDrawerData({ type: "timesheet", metric: "request_timesheet", techName: entry.tech, title: "Request Timesheets — " + entry.tech, records });
                        }}
                      />,
                      <TechLeaderboard
                        key="general_timesheets"
                        title="General Timesheets"
                        entries={generalTimesheetLeaderboard}
                        countLabel="same-day"
                        emptyMessage="No general time tracked yet"
                        onEntryClick={(entry) => {
                          const records = generalTimesheetMetrics.byTech[entry.tech]?.records || [];
                          if (records.length === 0) return;
                          setDrawerData({ type: "timesheet", metric: "general_timesheet", techName: entry.tech, title: "General Timesheets — " + entry.tech, records });
                        }}
                      />,
                      <TechLeaderboard
                        key="notes"
                        title="Notes Completed"
                        entries={sameDayNotesLeaderboard}
                        countLabel="noted"
                        rateColorFn={(e) => rateColor(e.rate)}
                        renderValue={(e) => pct(e.rate)}
                        renderSubline={(e) => e.countStr}
                        onEntryClick={(entry) => {
                          const records = (entry.records || []).slice().sort((a, b) => (b.date?.getTime() || 0) - (a.date?.getTime() || 0));
                          if (records.length === 0) return;
                          setDrawerData({ type: "notes", metric: "note", techName: entry.tech, title: "Notes Completed — " + entry.tech, records });
                        }}
                      />,
                      <TechLeaderboard
                        key="visits"
                        title="Visits Closed On Time"
                        entries={visitCompletionLeaderboard}
                        countLabel="same-day"
                        onEntryClick={(entry) => {
                          const records = visitCompletionMetrics.byTech[entry.tech]?.records || [];
                          if (records.length === 0) return;
                          setDrawerData({ type: "visits", metric: "visit_completion", techName: entry.tech, title: "Visit Completion — " + entry.tech, records });
                        }}
                      />,
                      <TechLeaderboard
                        key="jobclose"
                        title="Jobs Closed On Time"
                        entries={jobCloseLeaderboard}
                        countLabel="on time"
                        emptyMessage={lastJobClosedDate ? `No closures in this period — last job closed ${lastJobClosedDate.toLocaleDateString()}` : "No job data found"}
                        onEntryClick={(entry) => {
                          const records = jobCloseMetrics.byTech[entry.tech]?.records || [];
                          if (records.length === 0) return;
                          setDrawerData({ type: "job_close", techName: entry.tech, title: "Job Close Rate — " + entry.tech, records });
                        }}
                      />,
                    ],
                  },
                  {
                    label: "Client Feedback",
                    boards: [
                      <TechLeaderboard
                        key="stars"
                        title="Review Stars"
                        entries={reviewStarsLeaderboard}
                        countLabel="reviews"
                        emptyMessage={lastReviewDate ? `No attributed reviews in this period — last review ${lastReviewDate.toLocaleDateString()}` : "No reviews found"}
                        rateColorFn={() => C.yellow}
                        renderValue={(e) => `${e.totalStars}★`}
                        renderSubline={(e) => `${e.reviewCount} review${e.reviewCount !== 1 ? "s" : ""} · avg ${e.avgStars.toFixed(1)}★`}
                        onEntryClick={(entry) => {
                          const records = reviewStarsMetrics.byTech[entry.tech]?.records || [];
                          if (records.length === 0) return;
                          setDrawerData({
                            type: "review_stars",
                            techName: entry.tech,
                            title: "Review Stars — " + entry.tech,
                            records,
                            subMetrics: [
                              { label: "Total Stars", value: `${entry.totalStars}★`, color: C.yellow },
                              { label: "Reviews", value: String(entry.reviewCount), color: C.muted },
                              { label: "Avg", value: `${entry.avgStars.toFixed(1)}★`, color: entry.avgStars >= 4 ? C.green : C.yellow },
                            ],
                          });
                        }}
                      />,
                      <TechLeaderboard
                        key="mentions"
                        title="Name Mentions"
                        entries={nameMentionLeaderboard}
                        countLabel="mentions"
                        emptyMessage={lastReviewDate ? `No name mentions in this period — last review ${lastReviewDate.toLocaleDateString()}` : "No reviews found"}
                        rateColorFn={() => C.cyan}
                        renderValue={(e) => String(e.mentions)}
                        renderSubline={(e) => `mentioned in ${e.reviewCount} of ${nameMentionMetrics.totalReviews} reviews`}
                        onEntryClick={(entry) => {
                          const records = nameMentionMetrics.byTech[entry.tech]?.records || [];
                          if (records.length === 0) return;
                          setDrawerData({
                            type: "name_mentions",
                            techName: entry.tech,
                            title: "Name Mentions — " + entry.tech,
                            records,
                            subMetrics: [
                              { label: "Mentions", value: String(entry.mentions), color: C.cyan },
                              { label: "Reviews", value: `${entry.reviewCount} of ${nameMentionMetrics.totalReviews}`, color: C.muted },
                            ],
                          });
                        }}
                      />,
                      <TechLeaderboard
                        key="review_bonus"
                        title={`Review Bonus — $${reviewBonusSettings.base}/5★ review worked`}
                        entries={reviewBonusBoard.map(e => ({ ...e, _hideValue: !canSeeBonusFor(e.tech) }))}
                        countLabel="bonus"
                        emptyMessage="No 5★ reviews credited in this period"
                        rateColorFn={() => C.accent}
                        renderValue={(e) => e._hideValue ? "—" : dollar(e.totalBonus)}
                        renderSubline={(e) => {
                          const parts = [`${e.reviewCount} 5★ review${e.reviewCount !== 1 ? "s" : ""} worked`];
                          if (e.cappedMonths > 0) parts.push(`capped ${e.cappedMonths}mo @ $${reviewBonusSettings.monthly_cap}`);
                          return parts.join(" · ");
                        }}
                        onEntryClick={(entry) => {
                          // Base bonus = 5★ reviews this tech WORKED ∪ was NAMED in.
                          // Show worked reviews (review_stars) plus any mention-only
                          // reviews (tagged so they render via the name_mentions card).
                          const ridOf = (r) => r.id || `${r.date?.toISOString?.() || ""}|${r.reviewerName || ""}`;
                          const worked = (reviewStarsMetrics.byTech[entry.tech]?.records || [])
                            .filter(r => r.rating === 5);
                          const workedIds = new Set(worked.map(ridOf));
                          const mentionOnly = (nameMentionMetrics.byTech[entry.tech]?.records || [])
                            .filter(r => r.rating === 5 && !workedIds.has(ridOf(r)))
                            .map(r => ({ ...r, _rType: "name_mentions" }));
                          const records = [...worked, ...mentionOnly]
                            .sort((a, b) => (b.date?.getTime?.() || 0) - (a.date?.getTime?.() || 0));
                          if (records.length === 0) return;
                          const showDollars = canSeeBonusFor(entry.tech);
                          setDrawerData({
                            type: "review_stars",
                            techName: entry.tech,
                            title: "Review Bonus — " + entry.tech,
                            records,
                            subMetrics: [
                              ...(showDollars ? [{ label: "Review Bonus", value: dollar(entry.totalBonus), color: C.accent }] : []),
                              { label: "5★ Reviews", value: String(entry.reviewCount), color: C.muted },
                              ...(entry.cappedMonths > 0
                                ? [{ label: "Months Capped", value: `${entry.cappedMonths} @ $${reviewBonusSettings.monthly_cap}`, color: C.yellow }]
                                : []),
                            ],
                          });
                        }}
                      />,
                      <TechLeaderboard
                        key="mention_bonus"
                        title={`Mention Bonus — $${reviewBonusSettings.mention}/5★ name mention`}
                        entries={mentionBonusBoard.map(e => ({ ...e, _hideValue: !canSeeBonusFor(e.tech) }))}
                        countLabel="bonus"
                        emptyMessage="No 5★ name-mention bonuses in this period"
                        rateColorFn={() => C.cyan}
                        renderValue={(e) => e._hideValue ? "—" : dollar(e.totalBonus)}
                        renderSubline={(e) => {
                          const parts = [`${e.mentionCount} 5★ mention${e.mentionCount !== 1 ? "s" : ""}`];
                          if (e.cappedMonths > 0) parts.push(`capped ${e.cappedMonths}mo @ $${reviewBonusSettings.monthly_cap}`);
                          return parts.join(" · ");
                        }}
                        onEntryClick={(entry) => {
                          // Mention bonus = 5★ reviews naming this tech. Reuse the working
                          // "name_mentions" drawer so records render correctly.
                          const records = (nameMentionMetrics.byTech[entry.tech]?.records || [])
                            .filter(r => r.rating === 5)
                            .sort((a, b) => (b.date?.getTime?.() || 0) - (a.date?.getTime?.() || 0));
                          if (records.length === 0) return;
                          const showDollars = canSeeBonusFor(entry.tech);
                          setDrawerData({
                            type: "name_mentions",
                            techName: entry.tech,
                            title: "Mention Bonus — " + entry.tech,
                            records,
                            subMetrics: [
                              ...(showDollars ? [{ label: "Mention Bonus", value: dollar(entry.totalBonus), color: C.cyan }] : []),
                              { label: "5★ Mentions", value: String(entry.mentionCount), color: C.muted },
                              ...(entry.cappedMonths > 0
                                ? [{ label: "Months Capped", value: `${entry.cappedMonths} @ $${reviewBonusSettings.monthly_cap}`, color: C.yellow }]
                                : []),
                            ],
                          });
                        }}
                      />,
                    ],
                  },
                  ...(hasPermission("view_profitability") ? [{
                    label: "Profit Bonuses",
                    boards: [
                      // ONE combined board (Dylan 2026-07-23): every job a tech
                      // touched — led or helped on — weighs into their average,
                      // so a lead's one-off crew job stops showing as its own
                      // weird 1-job entry. Bonus $ allocation is unchanged.
                      <TechLeaderboard
                        key="profit_all"
                        title="Avg Gross Profit % — All Jobs Worked"
                        entries={profitLeaderboard}
                        countLabel="jobs"
                        rateColorFn={(e) => e.rate >= 45 ? C.green : e.rate >= 35 ? C.yellow : C.red}
                        renderValue={(e) => pct(e.rate)}
                        renderSubline={(e) => {
                          const showDollars = canSeeBonusFor(e.tech);
                          const bonusBit = e.qualCount > 0
                            ? (showDollars ? ` · ${e.qualCount} bonus (${dollar(e.totalBonus)})` : ` · ${e.qualCount} qualifying`)
                            : "";
                          const ledBit = e.leadCount > 0 ? ` · ${e.leadCount} led` : "";
                          return `${e.count} job${e.count !== 1 ? "s" : ""}${ledBit}${bonusBit}`;
                        }}
                        emptyMessage={lastJobClosedDate ? `No jobs since ${PROFIT_BONUS_START.toLocaleDateString()}` : "No job data found"}
                        onEntryClick={(entry) => {
                          if (!entry.records?.length) return;
                          const showDollars = canSeeBonusFor(entry.tech);
                          // Strip $ from records the viewer isn't allowed to see.
                          const records = showDollars ? entry.records : entry.records.map(r => ({ ...r, _hideBonus: true }));
                          setDrawerData({
                            type: "bonus_rows",
                            techName: entry.tech,
                            title: `Jobs — ${entry.tech}`,
                            records,
                            subMetrics: [
                              { label: "Jobs", value: String(entry.count), color: C.muted },
                              ...(entry.leadCount > 0
                                ? [{ label: "As Lead", value: String(entry.leadCount), color: C.muted }]
                                : []),
                              { label: "Avg Margin", value: pct(entry.rate), color: entry.rate >= 45 ? C.green : entry.rate >= 35 ? C.yellow : C.red },
                              ...(entry.qualCount > 0 && showDollars
                                ? [{ label: `Bonus (${entry.qualCount})`, value: dollar(entry.totalBonus), color: C.accent }]
                                : entry.qualCount > 0
                                  ? [{ label: "Qualifying", value: String(entry.qualCount), color: C.accent }]
                                  : []),
                            ],
                          });
                        }}
                      />,
                    ],
                  }] : []),
                ].map(section => (
                  <div key={section.label} style={{ marginBottom: 32 }}>
                    <div style={{
                      fontSize: 11, fontWeight: 700, color: C.dim,
                      textTransform: "uppercase", letterSpacing: "0.1em",
                      marginBottom: 16, paddingBottom: 8,
                      borderBottom: `1px solid ${C.border}`,
                    }}>
                      {section.label}
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 20 }}>
                      {section.boards}
                    </div>
                  </div>
                ))}

              </>
            )}

            {/* SCORECARDS TAB — hidden (browser performance); computation code preserved above */}
            {false && (
              <Section title="Tech Scorecards">
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(340px, 1fr))", gap: 16 }}>
                  {techScores.map(t => (
                    <div key={t.tech} style={{
                      background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14, padding: 20
                    }}>
                      {/* Header */}
                      <div style={{ marginBottom: 14 }}>
                        <div style={{ fontSize: 18, fontWeight: 700, color: C.text }}>{t.tech}</div>
                        <div style={{ fontSize: 11, color: C.muted }}>
                          {t.jobCount > 0 ? `${t.jobCount} jobs · ` : ""}
                          {hrs(t.totalHours)} logged · {t.totalVisits} visits
                        </div>
                      </div>

                      {/* Metric 1: Same-Day Timesheet Rate */}
                      <div style={{ fontSize: 11, color: C.green, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
                        Timesheet Compliance
                      </div>
                      {t.tsTotal > 0 ? (
                        <div style={{ marginBottom: 14 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                            <span style={{ fontSize: 12, color: C.muted }}>Same-Day Rate</span>
                            <span style={{ fontSize: 16, fontWeight: 700, color: rateColor(t.sameDayRate) }}>
                              {pct(t.sameDayRate)}
                            </span>
                          </div>
                          <ScoreBar value={t.sameDayRate || 0} color={rateColor(t.sameDayRate)} />
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 8 }}>
                            <MetricMini label="On Time" value={t.tsOnTime} color={C.green}
                              onClick={t.tsOnTime > 0 ? () => {
                                const recs = (timesheetMetrics.byTech[t.tech]?.records || []).filter(r => r.onTime);
                                setDrawerData({ type: "timesheet", techName: t.tech, title: `On-Time Timesheets — ${t.tech}`, records: recs });
                              } : undefined}
                            />
                            <MetricMini label="Late" value={t.tsLate} color={t.tsLate > 0 ? C.red : C.green}
                              onClick={t.tsLate > 0 ? () => {
                                const recs = (timesheetMetrics.byTech[t.tech]?.records || []).filter(r => !r.onTime);
                                setDrawerData({ type: "timesheet", techName: t.tech, title: `Late Timesheets — ${t.tech}`, records: recs });
                              } : undefined}
                            />
                            <MetricMini label="Total" value={t.tsTotal}
                              onClick={t.tsTotal > 0 ? () => {
                                const recs = timesheetMetrics.byTech[t.tech]?.records || [];
                                setDrawerData({ type: "timesheet", techName: t.tech, title: `All Timesheets — ${t.tech}`, records: recs });
                              } : undefined}
                            />
                          </div>
                        </div>
                      ) : (
                        <div style={{ padding: "8px 0 14px", fontSize: 12, color: C.muted }}>No timesheet data</div>
                      )}

                      {/* Metric 2: Same-Day Notes Rate */}
                      <div style={{ fontSize: 11, color: C.blue, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
                        Notes Compliance
                      </div>
                      {t.totalVisits > 0 ? (
                        <div style={{ marginBottom: 14 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                            <span style={{ fontSize: 12, color: C.muted }}>Same-Day Notes</span>
                            <span style={{ fontSize: 16, fontWeight: 700, color: rateColor(t.noteRate) }}>
                              {pct(t.noteRate)}
                            </span>
                          </div>
                          <ScoreBar value={t.noteRate || 0} color={rateColor(t.noteRate)} />
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 8 }}>
                            <MetricMini label="With Note" value={t.visitsWithNote} color={C.green}
                              onClick={t.visitsWithNote > 0 ? () => {
                                const recs = (visitNoteMetrics.byTech[t.tech]?.records || []).filter(r => r.hasNote);
                                setDrawerData({ type: "notes", techName: t.tech, title: `Notes Written — ${t.tech}`, records: recs });
                              } : undefined}
                            />
                            <MetricMini label="Missing" value={t.visitsWithoutNote} color={t.visitsWithoutNote > 0 ? C.red : C.green}
                              onClick={t.visitsWithoutNote > 0 ? () => {
                                const recs = (visitNoteMetrics.byTech[t.tech]?.records || []).filter(r => !r.hasNote);
                                setDrawerData({ type: "notes", techName: t.tech, title: `Missing Notes — ${t.tech}`, records: recs });
                              } : undefined}
                            />
                            <MetricMini label="Total Visits" value={t.totalVisits}
                              onClick={t.totalVisits > 0 ? () => {
                                const recs = visitNoteMetrics.byTech[t.tech]?.records || [];
                                setDrawerData({ type: "notes", techName: t.tech, title: `All Visit Notes — ${t.tech}`, records: recs });
                              } : undefined}
                            />
                          </div>
                        </div>
                      ) : (
                        <div style={{ padding: "8px 0 14px", fontSize: 12, color: C.muted }}>No completed visits</div>
                      )}

                      {/* Metric 3: Same-Day Visit Completion Rate */}
                      <div style={{ fontSize: 11, color: C.orange, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
                        Visit Completion
                      </div>
                      {t.vcTotal > 0 ? (
                        <div style={{ marginBottom: 14 }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                            <span style={{ fontSize: 12, color: C.muted }}>Same-Day Rate</span>
                            <span style={{ fontSize: 16, fontWeight: 700, color: t.visitCompletionRate != null ? (t.visitCompletionRate >= 85 ? C.green : t.visitCompletionRate >= 70 ? C.yellow : C.red) : C.muted }}>
                              {pct(t.visitCompletionRate)}
                            </span>
                          </div>
                          <ScoreBar value={t.visitCompletionRate || 0} color={t.visitCompletionRate != null ? (t.visitCompletionRate >= 85 ? C.green : t.visitCompletionRate >= 70 ? C.yellow : C.red) : C.muted} />
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginTop: 8 }}>
                            <MetricMini label="On Time" value={t.vcOnTime} color={C.green}
                              onClick={t.vcOnTime > 0 ? () => {
                                const recs = (visitCompletionMetrics.byTech[t.tech]?.records || []).filter(r => r.onTime);
                                setDrawerData({ type: "visits", techName: t.tech, title: `Same-Day Completions — ${t.tech}`, records: recs });
                              } : undefined}
                            />
                            <MetricMini label="Late" value={t.vcLate} color={t.vcLate > 0 ? C.red : C.green}
                              onClick={t.vcLate > 0 ? () => {
                                const recs = (visitCompletionMetrics.byTech[t.tech]?.records || []).filter(r => !r.onTime);
                                setDrawerData({ type: "visits", techName: t.tech, title: `Late Completions — ${t.tech}`, records: recs });
                              } : undefined}
                            />
                            <MetricMini label="Total" value={t.vcTotal}
                              onClick={t.vcTotal > 0 ? () => {
                                const recs = visitCompletionMetrics.byTech[t.tech]?.records || [];
                                setDrawerData({ type: "visits", techName: t.tech, title: `All Visit Completions — ${t.tech}`, records: recs });
                              } : undefined}
                            />
                          </div>
                        </div>
                      ) : (
                        <div style={{ padding: "8px 0 14px", fontSize: 12, color: C.muted }}>No completion data</div>
                      )}

                      {/* Job Performance (from jb_jobs) */}
                      {t.jobCount > 0 && (hasPermission("view_revenue_per_tech") || hasPermission("view_profit_per_tech")) && (
                        <>
                          <div style={{ fontSize: 11, color: C.accent, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>
                            Job Performance
                          </div>
                          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
                            {hasPermission("view_revenue_per_tech") && (
                              <MetricMini label="Revenue" value={dollar(t.totalRevenue)} color={C.green}
                                onClick={t.jobCount > 0 ? () => {
                                  const tf = firstName(t.tech);
                                  const recs = filteredJobs.filter(j => j._leadTech && firstName(j._leadTech) === tf)
                                    .sort((a, b) => (b._closedDate?.getTime() || 0) - (a._closedDate?.getTime() || 0));
                                  setDrawerData({ type: "job_financial", techName: t.tech, title: `Job Revenue — ${t.tech}`, records: recs });
                                } : undefined}
                              />
                            )}
                            {hasPermission("view_profit_per_tech") && (
                              <MetricMini label="Profit" value={dollar(t.totalProfit)} color={C.blue}
                                onClick={t.jobCount > 0 ? () => {
                                  const tf = firstName(t.tech);
                                  const recs = filteredJobs.filter(j => j._leadTech && firstName(j._leadTech) === tf)
                                    .sort((a, b) => (b.profit || 0) - (a.profit || 0));
                                  setDrawerData({ type: "job_financial", techName: t.tech, title: `Job Profit — ${t.tech}`, records: recs });
                                } : undefined}
                              />
                            )}
                            {hasPermission("view_profit_per_tech") && (
                              <MetricMini label="Avg Margin" value={t.avgMargin != null ? pct(t.avgMargin) : "--"}
                                color={t.avgMargin >= 35 ? C.green : t.avgMargin >= 25 ? C.yellow : C.red}
                                onClick={t.jobCount > 0 ? () => {
                                  const tf = firstName(t.tech);
                                  const recs = filteredJobs.filter(j => j._leadTech && firstName(j._leadTech) === tf && j.profit_pct != null)
                                    .sort((a, b) => (b.profit_pct || 0) - (a.profit_pct || 0));
                                  setDrawerData({ type: "job_financial", techName: t.tech, title: `Job Margins — ${t.tech}`, records: recs });
                                } : undefined}
                              />
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
                {techScores.length === 0 && (
                  <div style={{ textAlign: "center", padding: 60, color: C.muted }}>No tech data for selected filters</div>
                )}
              </Section>
            )}

            {/* ═══════════════════════════════════════════════════════════════ */}
            {/* DETAILS TAB */}
            {/* ═══════════════════════════════════════════════════════════════ */}
            {tab === "details" && hasPermission("view_job_table") && (
              <Section title={`Job Details (${sortedJobs.length})`}>
                <div style={{
                  overflowX: "auto", border: `1px solid ${C.border}`, borderRadius: 12,
                  maxHeight: "70vh", overflowY: "auto"
                }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 800 }}>
                    <thead>
                      <tr>
                        <th style={thStyle("job_num")} onClick={() => toggleSort("job_num")}>Job #</th>
                        <th style={thStyle("client_name")} onClick={() => toggleSort("client_name")}>Client</th>
                        <th style={thStyle("_leadTech")} onClick={() => toggleSort("_leadTech")}>Tech</th>
                        <th style={thStyle("service_type")} onClick={() => toggleSort("service_type")}>Service</th>
                        <th style={thStyle("closedDate")} onClick={() => toggleSort("closedDate")}>Closed</th>
                        <th style={thStyle("total_revenue")} onClick={() => toggleSort("total_revenue")}>Revenue</th>
                        <th style={thStyle("profit")} onClick={() => toggleSort("profit")}>Profit</th>
                        <th style={thStyle("profit_pct")} onClick={() => toggleSort("profit_pct")}>Margin</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedJobs.slice(0, 200).map((j, i) => (
                        <tr key={j.id} style={{ background: i % 2 === 0 ? "transparent" : `${C.border}08` }}>
                          <td style={tdStyle}>{j.job_num}</td>
                          <td style={{ ...tdStyle, maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis" }}>{j.client_name}</td>
                          <td style={tdStyle}>{j._leadTech || "--"}</td>
                          <td style={{ ...tdStyle, maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", fontSize: 12 }}>{j.service_type || "--"}</td>
                          <td style={tdStyle}>{j._closedDate ? j._closedDate.toLocaleDateString("en-CA") : "--"}</td>
                          <td style={{ ...tdStyle, color: C.green }}>{dollar(j.total_revenue)}</td>
                          <td style={{ ...tdStyle, color: (j.profit || 0) >= 0 ? C.blue : C.red }}>{dollar(j.profit)}</td>
                          <td style={{ ...tdStyle, color: (j.profit_pct || 0) >= 35 ? C.green : (j.profit_pct || 0) >= 25 ? C.yellow : C.red }}>
                            {j.profit_pct != null ? pct(j.profit_pct) : "--"}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {sortedJobs.length > 200 && (
                    <div style={{ padding: 12, textAlign: "center", color: C.muted, fontSize: 12 }}>
                      Showing 200 of {sortedJobs.length} jobs
                    </div>
                  )}
                </div>
              </Section>
            )}

            {/* ═══════════════════════════════════════════════════════════════ */}
            {/* REFERRAL LEADERBOARD TAB — who referred the client, and what   */}
            {/* that client's work paid. jobs/invoices are handed down rather  */}
            {/* than re-fetched: this page already holds both.                 */}
            {/* ═══════════════════════════════════════════════════════════════ */}
            {tab === "referrals" && (
              <ReferralLeaderboard
                jobs={jobs}
                invoices={referralInvoices}
                techNames={allTechNames}
                canSeeBonusFor={canSeeBonusFor}
                isAdmin={isAdmin}
              />
            )}

            {/* ═══════════════════════════════════════════════════════════════ */}
            {/* BONUS TRACKER TAB */}
            {/* ═══════════════════════════════════════════════════════════════ */}
            {tab === "bonuses" && hasPermission("view_bonus") && (
              <BonusTracker />
            )}

            {/* ═══════════════════════════════════════════════════════════════ */}
            {/* MASTER REPORT TAB — admin-only cross-tech filterable view.    */}
            {/* Sub-tabs (Timesheets / Notes / Profitability / Reviews) live  */}
            {/* inside MasterCrewReport.jsx so this file stays manageable.    */}
            {/* ═══════════════════════════════════════════════════════════════ */}
            {tab === "report" && canSeeMasterReport && (
              <MasterCrewReport
                enrichedTimesheets={enrichedTimesheets}
                enrichedNotes={enrichedNotes}
                enrichedReviews={enrichedReviews}
                enrichedJobs={enrichedJobs}
                allTechNames={allTechNames}
              />
            )}
          </>
        )}
        </div>
        {/* end Main column */}
      </div>
      </div>
      {/* end Body row (left rail + main column) */}

      {/* ── Drill-down Drawer ── */}
      {drawerData && (
        <DetailDrawer
          data={drawerData}
          onClose={() => setDrawerData(null)}
          allActiveTechs={allTechNames}
          canEditAssignments={hasPermission("edit_review_assignments")}
          onUpdateReview={handleUpdateReview}
          onIgnoreReview={handleIgnoreReview}
          onUpdateMentions={handleUpdateMentions}
          onUpdateVisitCloser={handleUpdateVisitCloser}
          canEditVisitCloser={isAdmin}
          canEditMetricOverrides={isAdmin}
          onMetricOverride={handleMetricOverride}
          onAddMiss={handleAddMiss}
          jobs={jobs}
          timesheets={timesheets}
        />
      )}
    </div>
  );
}
